import React, { useState, useEffect, useRef } from "react";
import { Tag } from "lucide-react";
import { supabase } from "./supabase";
import { dispararActualizacionEnSegundoPlano } from "./servicios/actualizarPresupuestosTotales";
import { filtrarSalidasParaCliente } from "./permisos";
import { usePreventScrollShift } from "./hooks/usePreventScrollShift";
import {
  BarChart3,
  TrendingDown,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Search,
  Filter,
  Download,
  Upload,
  Calendar,
  Clock,
  Settings,
  Eye,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Info,
  ChevronDown,
  DollarSign,
  FileText,
  File,
  User,
  Users,
  CreditCard,
  Banknote,
  Building2,
  MapPin,
  Loader2,
  Camera,
  Image,
  Wallet,
  Layers,
  Package,
  Target,
  PieChart,
  ClipboardList,
} from "lucide-react";

// --- Utilidad a nivel de módulo: actualización de balance para entradas de comisión ---
// Se define a nivel de módulo para asegurar disponibilidad en todo el archivo
export async function actualizarBalanceCajaComision(entradaOriginal = null, entradaNueva = null) {
  try {
    if (entradaOriginal) {
      const { data: cajaOriginal, error: errorCajaOriginal } = await supabase
        .from("cajas")
        .select("balance_actual, nombre")
        .eq("id", entradaOriginal.caja_destino_id)
        .single();

      if (!errorCajaOriginal && cajaOriginal) {
        const balanceActual = parseFloat(cajaOriginal.balance_actual) || 0;
        const nuevoBalance = balanceActual - parseFloat(entradaOriginal.monto);

        await supabase
          .from("cajas")
          .update({
            balance_actual: nuevoBalance,
            updated_at: new Date().toISOString(),
          })
          .eq("id", entradaOriginal.caja_destino_id);
      }
    }

    if (entradaNueva) {
      const { data: cajaNueva, error: errorCajaNueva } = await supabase
        .from("cajas")
        .select("balance_actual, nombre")
        .eq("id", entradaNueva.caja_destino_id)
        .single();

      if (!errorCajaNueva && cajaNueva) {
        const balanceActual = parseFloat(cajaNueva.balance_actual) || 0;
        const nuevoBalance = balanceActual + parseFloat(entradaNueva.monto);

        await supabase
          .from("cajas")
          .update({
            balance_actual: nuevoBalance,
            updated_at: new Date().toISOString(),
          })
          .eq("id", entradaNueva.caja_destino_id);
      }
    }
  } catch (err) {
    console.error("Error en actualización de balance para comisión:", err);
  }
}

// Configuración anti-zoom móvil
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// Función para obtener colores de vista
const getVistaColor = (vistaId) => {
  const VISTA_COLORS = {
    salidas: {
      gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
      solid: "#2563eb",
      light: "#2563eb10",
      description: "Gastos y pagos",
    },
  };
  return VISTA_COLORS[vistaId] || VISTA_COLORS.salidas;
};

const formatDateForInput = (dateString) => {
  if (!dateString) return new Date().toISOString().split("T")[0];
  if (dateString.includes("T")) {
    return dateString.split("T")[0];
  }
  return dateString;
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

const formatMonthForSelect = (monthString) => {
  const [year, month] = monthString.split("-");
  const date = new Date(year, month - 1, 15);
  return new Intl.DateTimeFormat("es-ES", {
    year: "numeric",
    month: "long",
  }).format(date);
};

// Función para formatear monto con puntos cada 3 números
const formatearMonto = (valor) => {
  if (!valor) return "";
  // Remover todos los caracteres no numéricos excepto el punto decimal
  const numerosSolo = valor.toString().replace(/[^\d.]/g, "");

  // Separar parte entera y decimal
  const partes = numerosSolo.split(".");
  let parteEntera = partes[0];
  const parteDecimal = partes[1] || "";

  // Agregar puntos cada 3 dígitos en la parte entera
  parteEntera = parteEntera.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  // Reconstruir el número
  if (parteDecimal) {
    return `${parteEntera},${parteDecimal}`;
  }
  return parteEntera;
};

// Función para limpiar formato y obtener solo números
const limpiarFormatoMonto = (valor) => {
  if (!valor) return "";
  return valor
    .toString()
    .replace(/[^\d,]/g, "")
    .replace(",", ".");
};

// ✅ FUNCIÓN PARA FORMATEAR CUIT AUTOMÁTICAMENTE
const formatearCUIT = (cuit) => {
  if (!cuit) return "";
  const numerosSolo = cuit.replace(/\D/g, "");

  // Formateo progresivo en tiempo real
  if (numerosSolo.length <= 2) {
    return numerosSolo;
  } else if (numerosSolo.length <= 10) {
    return `${numerosSolo.slice(0, 2)}-${numerosSolo.slice(2)}`;
  } else {
    return `${numerosSolo.slice(0, 2)}-${numerosSolo.slice(
      2,
      10
    )}-${numerosSolo.slice(10)}`;
  }
};

// Dropdown personalizado unificado con búsqueda
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
  onSearch,
  canCreate = false,
  onCreateNew,
  createText = "Crear nuevo",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "12px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "280px",
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
          onWheel={(e) => {
            // Evita que el scroll burbujee al body cuando se llega al límite
            e.stopPropagation();
          }}
          onTouchMove={(e) => {
            // En mobile, evita scroll-bounce hacia el body
            e.stopPropagation();
          }}
        >
          {/* Campo de búsqueda */}
          <div style={{ padding: "8px" }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  canCreate &&
                  filteredOptions.length === 0 &&
                  searchTerm.trim()
                ) {
                  onCreateNew?.(searchTerm.trim());
                  setSearchTerm("");
                  setIsOpen(false);
                }
              }}
            />
          </div>

          {/* Opciones filtradas */}
          {filteredOptions.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
                setSearchTerm("");
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : (option.isFrecuente ? "#f59e0b" : "#374151"),
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : (option.isFrecuente ? "500" : "400"),
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{option.label}</span>
              <div
                style={{ display: "flex", gap: "4px", alignItems: "center" }}
              >
                {option.tieneInicial && (
                  <Target size={14} color="#8b5cf6" style={{ flexShrink: 0 }} />
                )}
                {option.tienePresupuesto && (
                  <ClipboardList
                    size={14}
                    color="#ec4899"
                    style={{ flexShrink: 0 }}
                  />
                )}
              </div>
            </button>
          ))}

          {/* Opción de crear nuevo */}
          {canCreate && searchTerm.trim() && filteredOptions.length === 0 && (
            <button
              onClick={() => {
                onCreateNew?.(searchTerm.trim());
                setSearchTerm("");
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: "#f0fdf4",
                color: "#166534",
                fontSize: "14px",
                fontWeight: "600",
                textAlign: "left",
                cursor: "pointer",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                touchAction: "manipulation",
              }}
            >
              <Plus size={14} />
              {createText}: "{searchTerm}"
            </button>
          )}

          {/* Mensaje cuando no hay resultados */}
          {!canCreate && filteredOptions.length === 0 && searchTerm && (
            <div
              style={{
                padding: "16px",
                textAlign: "center",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              No se encontraron resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Modal personalizado unificado
function CustomModal({
  isOpen,
  onClose,
  type = "info",
  title,
  message,
  onConfirm,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  isMobile,
}) {
  // Bloquear scroll del body y cerrar con Escape
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getConfig = () => {
    switch (type) {
      case "confirm":
        return {
          icon: AlertCircle,
          color: "#f59e0b",
          bgColor: "#fefbf3",
          borderColor: "#f59e0b",
        };
      case "error":
        return {
          icon: XCircle,
          color: "#ef4444",
          bgColor: "#fef2f2",
          borderColor: "#ef4444",
        };
      case "success":
        return {
          icon: CheckCircle2,
          color: "#059669",
          bgColor: "#f0fdf4",
          borderColor: "#059669",
        };
      default:
        return {
          icon: Info,
          color: "#3b82f6",
          bgColor: "#f0f9ff",
          borderColor: "#3b82f6",
        };
    }
  };

  const config = getConfig();
  const IconComponent = config.icon;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 15000,
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: isMobile ? "90%" : "400px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: `2px solid ${config.borderColor}`,
          textAlign: "center",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <IconComponent
            size={48}
            color={config.color}
            style={{ marginBottom: "12px" }}
          />
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: config.color,
            }}
          >
            {title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#64748b",
              lineHeight: "1.4",
            }}
          >
            {message}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: type === "confirm" ? "space-between" : "center",
          }}
        >
          {type === "confirm" && (
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: "12px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                background: "white",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                touchAction: "manipulation",
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={() => {
              if (type === "confirm" && onConfirm) {
                onConfirm();
              }
              onClose();
            }}
            style={{
              flex: type === "confirm" ? 1 : "auto",
              padding: "12px 20px",
              border: "none",
              borderRadius: "8px",
              background: config.color,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              minWidth: type !== "confirm" ? "120px" : "auto",
              touchAction: "manipulation",
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
function Salidas({ constructora, usuario, permisosCliente }) {
  const [salidas, setSalidas] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [todasLosConceptos, setTodasLosConceptos] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [todasLasEtapas, setTodasLasEtapas] = useState([]);
  const [etapasActivas, setEtapasActivas] = useState([]);
  const [subetapas, setSubetapas] = useState([]);
  const [todasLasSubetapas, setTodasLasSubetapas] = useState([]);
  const [subetapasActivas, setSubetapasActivas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [todosLosProveedores, setTodosLosProveedores] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isClient, setIsClient] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProject, setFilterProject] = useState(() => {
    // Intentar restaurar el último filtro de proyecto desde localStorage
    try {
      const saved = localStorage.getItem('salidas_filterProject');
      return saved || "todos";
    } catch (error) {
      console.error('Error restaurando filtro de proyecto:', error);
      return "todos";
    }
  });
  const [filterMonth, setFilterMonth] = useState(() => {
    try {
      const saved = localStorage.getItem('salidas_filterMonth');
      return saved || "todos";
    } catch (error) {
      console.error('Error restaurando filtro de mes:', error);
      return "todos";
    }
  });
  const [filterEstado, setFilterEstado] = useState(() => {
    try {
      const saved = localStorage.getItem('salidas_filterEstado');
      return saved || "activos";
    } catch (error) {
      console.error('Error restaurando filtro de estado:', error);
      return "activos";
    }
  });
  const [filterConcepto, setFilterConcepto] = useState(() => {
    try {
      const saved = localStorage.getItem('salidas_filterConcepto');
      return saved || "todos";
    } catch (error) {
      console.error('Error restaurando filtro de concepto:', error);
      return "todos";
    }
  });

  // Estado para panel de filtros
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  // Función para detectar si hay filtros activos
  const hayFiltrosActivos = () => {
    return filterMonth !== "todos" ||
           filterEstado !== "activos" ||
           filterConcepto !== "todos" ||
           filterEtapa !== "todos" ||
           filterProveedor !== null ||
           filterComision !== null;
  };

  // Función para actualizar balance de caja para entrada de comisión
  async function actualizarBalanceCajaComision(entradaOriginal = null, entradaNueva = null) {
    try {
      // Si hay entrada original, revertir su impacto (restar del balance)
      if (entradaOriginal) {
        const { data: cajaOriginal, error: errorCajaOriginal } = await supabase
          .from("cajas")
          .select("balance_actual, nombre")
          .eq("id", entradaOriginal.caja_destino_id)
          .single();

        if (!errorCajaOriginal && cajaOriginal) {
          const balanceActual = parseFloat(cajaOriginal.balance_actual) || 0;
          const nuevoBalance = balanceActual - parseFloat(entradaOriginal.monto);

          await supabase
            .from("cajas")
            .update({
              balance_actual: nuevoBalance,
              updated_at: new Date().toISOString(),
            })
            .eq("id", entradaOriginal.caja_destino_id);

          console.log(`🔄 Balance revertido para ${cajaOriginal.nombre}: ${balanceActual} - ${entradaOriginal.monto} = ${nuevoBalance}`);
        }
      }

      // Si hay entrada nueva, aplicar su impacto (sumar al balance)
      if (entradaNueva) {
        const { data: cajaNueva, error: errorCajaNueva } = await supabase
          .from("cajas")
          .select("balance_actual, nombre")
          .eq("id", entradaNueva.caja_destino_id)
          .single();

        if (!errorCajaNueva && cajaNueva) {
          const balanceActual = parseFloat(cajaNueva.balance_actual) || 0;
          const nuevoBalance = balanceActual + parseFloat(entradaNueva.monto);

          await supabase
            .from("cajas")
            .update({
              balance_actual: nuevoBalance,
              updated_at: new Date().toISOString(),
            })
            .eq("id", entradaNueva.caja_destino_id);

          console.log(`✅ Balance actualizado para ${cajaNueva.nombre}: ${balanceActual} + ${entradaNueva.monto} = ${nuevoBalance}`);
        }
      }
    } catch (err) {
      console.error("Error en actualización de balance para comisión:", err);
    }
  }

  // Reemplaza mano de obra por etapa
  const [filterEtapa, setFilterEtapa] = useState(() => {
    try {
      const saved = localStorage.getItem('salidas_filterEtapa');
      return saved || "todos";
    } catch (error) {
      console.error('Error restaurando filtro de etapa:', error);
      return "todos";
    }
  });
  const [filterProveedor, setFilterProveedor] = useState(() => {
    try {
      const saved = localStorage.getItem('salidas_filterProveedor');
      return saved || null;
    } catch (error) {
      console.error('Error restaurando filtro de proveedor:', error);
      return null;
    }
  });
  const [filterComision, setFilterComision] = useState(() => {
    try {
      const saved = localStorage.getItem('salidas_filterComision');
      return saved || null;
    } catch (error) {
      console.error('Error restaurando filtro de comisión:', error);
      return null;
    }
  });

  // Estados para datos filtrados
  const [salidasFiltradas, setSalidasFiltradas] = useState([]);
  
  // Estado para proveedores frecuentes
  const [proveedoresFrecuentes, setProveedoresFrecuentes] = useState([]);

  // Estados para modales
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSalidaDetail, setSelectedSalidaDetail] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);

  // Estados para alertas personalizadas
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });
  // Estados para presupuestos
  const [presupuestosIniciales, setPresupuestosIniciales] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]); // Determinar si es cliente y sus permisos
  const isCliente = usuario?.tipo_usuario === "cliente";
  const isConstructoraOrEmpleado =
    usuario?.tipo_usuario === "constructora" ||
    usuario?.tipo_usuario === "empleado";
  const puedeEditar = isConstructoraOrEmpleado;
  const puedeCrear = isConstructoraOrEmpleado;
  const puedeEliminar = isConstructoraOrEmpleado;

  // Obtener configuración de vista
  const vistaActual = "salidas";
  const config = getVistaColor(vistaActual);

  // ESC handler y scroll reset obligatorios
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowModal(false);
        setShowDetailModal(false);
        setShowDocumentModal(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (constructora?.id) {
      cargarDatos();
    }
  }, [constructora, filterEstado]);

  // Aplicar filtros cuando cambian los datos o permisos
  useEffect(() => {
    aplicarFiltros();
  }, [salidas, searchTerm, filterProject, filterMonth, filterConcepto, filterEtapa, filterProveedor, filterComision, permisosCliente]);


  // Cargar proveedores frecuentes cuando cambia el proyecto
  useEffect(() => {
    if (constructora?.id && filterProject !== "todos") {
      cargarProveedoresFrecuentes(filterProject);
    } else {
      setProveedoresFrecuentes([]);
    }
  }, [filterProject, constructora?.id]);

  // Función para guardar el filtro de proyecto en localStorage
  const guardarFiltroProyecto = (filtro) => {
    try {
      localStorage.setItem('salidas_filterProject', filtro);
    } catch (error) {
      console.error('Error guardando filtro de proyecto:', error);
    }
  };

  const showCustomAlert = (type, title, message, onConfirm = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
    });
  };

  const cargarDatos = async () => {
    try {
      // Cargar salidas con datos relacionados (SIN CAJA ORIGEN - la cargaremos por separado)
      let query = supabase
        .from("salidas")
        .select(
          `
          *,
          conceptos_salida (nombre),
          proyectos (codigo, apellido_cliente),
          proveedores_servicios!left (nombre),
          etapas_obra (nombre),
          subetapas_obra!left (nombre),
          usuarios_created:usuarios!salidas_created_by_fkey (email),
          usuarios_updated:usuarios!salidas_updated_by_fkey (email),
          usuarios_deleted:usuarios!salidas_deleted_by_fkey (email)
        `
        )
        .eq("constructora_id", constructora.id);

      // Filtro por proyecto para clientes
      if (isCliente && permisosCliente?.proyectoId) {
        query = query.eq("proyecto_id", permisosCliente.proyectoId);
      }

      // Filtro por estado (activos / eliminados)
      // Los clientes solo ven elementos activos, no pueden usar filtros de estado
      if (isCliente) {
        query = query.eq("activo", true).is("deleted_at", null);
      } else {
        // Constructoras pueden usar filtros de estado
        if (filterEstado === "activos") {
          query = query.eq("activo", true).is("deleted_at", null);
        } else if (filterEstado === "eliminados") {
          query = query.or("activo.eq.false,deleted_at.not.is.null");
        }
      }

      const { data: salidasData, error: salidasError } = await query.order(
        "fecha",
        { ascending: false }
      );

      if (salidasError) {
        console.error("Error cargando salidas:", salidasError);
      } else {
        console.log("🔍 Debug - Salidas cargadas:", salidasData?.length || 0);
        console.log("🔍 Debug - Primera salida:", salidasData?.[0]);
        console.log("🔍 Debug - Proyectos en salidas:", salidasData?.map(s => ({ 
          id: s.id, 
          proyecto_id: s.proyecto_id, 
          proyecto: s.proyectos,
          tiene_proyecto: !!s.proyecto_id,
          tiene_relacion: !!s.proyectos
        })));
        setSalidas(salidasData || []);
      }

      // Cargar cajas por separado para mapear nombres
      const { data: cajasParaMapeo, error: cajasMapeoError } = await supabase
        .from("cajas")
        .select("id, nombre, tipo_caja, moneda")
        .eq("constructora_id", constructora.id)
        .eq("activo", true);

      if (cajasMapeoError) {
        console.error("Error cargando cajas para mapeo:", cajasMapeoError);
      } else {
        // Crear un mapeo de ID a nombre de caja
        const cajasMap = {};
        (cajasParaMapeo || []).forEach(caja => {
          cajasMap[caja.id] = caja;
        });
        // Guardar todas las cajas en estado para futuros cruces
        setCajas(cajasParaMapeo || []);
        
        // Agregar los datos de caja a cada salida
        const salidasConCajas = (salidasData || []).map(salida => ({
          ...salida,
          cajas: salida.caja_origen_id ? cajasMap[salida.caja_origen_id] : null
        }));
        
        console.log("🔍 Debug - Mapeo de cajas:", cajasMap);
        console.log("🔍 Debug - Primera salida con caja:", salidasConCajas[0]);
        
        setSalidas(salidasConCajas);
      }

      // Cargar proyectos según permisos
      let proyectosData = [];
      let proyectosError = null;

      if (filterEstado === "eliminados") {
        const { data: idsData, error: idsError } = await supabase
          .from("salidas")
          .select("proyecto_id")
          .eq("constructora_id", constructora.id)
          .or("activo.eq.false,deleted_at.not.is.null");

        if (idsError) {
          proyectosError = idsError;
        } else {
          const ids = Array.from(
            new Set((idsData || []).map((r) => r.proyecto_id).filter(Boolean))
          );
          let query = supabase
            .from("proyectos")
            .select("*")
            .eq("constructora_id", constructora.id);

          if (isCliente && permisosCliente?.proyectoId) {
            if (ids.includes(permisosCliente.proyectoId)) {
              query = query.eq("id", permisosCliente.proyectoId);
            } else {
              proyectosData = [];
              query = null;
            }
          } else if (ids.length > 0) {
            query = query.in("id", ids).order("codigo");
          } else {
            proyectosData = [];
            query = null;
          }

          if (query) {
            const { data, error } = await query;
            proyectosData = data || [];
            proyectosError = error || null;
          }
        }
      } else {
        let query = supabase
          .from("proyectos")
          .select("*")
          .eq("constructora_id", constructora.id)
          .eq("activo", true)
          .is("deleted_at", null);

        if (isCliente && permisosCliente?.proyectoId) {
          query = query.eq("id", permisosCliente.proyectoId);
        } else {
          query = query.order("codigo");
        }

        const { data, error } = await query;
        proyectosData = data || [];
        proyectosError = error || null;
      }

      if (proyectosError) {
        console.error("Error cargando proyectos:", proyectosError);
      } else {
        setProyectos(proyectosData || []);

        // Si es cliente, preseleccionar su proyecto
        if (isCliente && permisosCliente?.proyectoId) {
          setFilterProject(permisosCliente.proyectoId);
        }
      }

      // Cargar conceptos de salida (solo activos para dropdowns)
      const { data: conceptosData, error: conceptosError } = await supabase
        .from("conceptos_salida")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("nombre");

      if (conceptosError) {
        console.error("Error cargando conceptos:", conceptosError);
      } else {
        setConceptos(conceptosData || []);
      }

      // Cargar TODOS los conceptos (sin filtros) para salidas existentes
      const { data: todasLosConceptosData, error: todasLosConceptosError } =
        await supabase
          .from("conceptos_salida")
          .select("*")
          .eq("constructora_id", constructora.id)
          .order("nombre");

      if (todasLosConceptosError) {
        console.error(
          "Error cargando todos los conceptos:",
          todasLosConceptosError
        );
      } else {
        setTodasLosConceptos(todasLosConceptosData || []);
      }

      // Cargar etapas de obra (solo activas para dropdowns)
      const { data: etapasData, error: etapasError } = await supabase
        .from("etapas_obra")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("orden", { ascending: true });

      if (etapasError) {
        console.error("Error cargando etapas:", etapasError);
      } else {
        setEtapas(etapasData || []);
      }

      // Cargar TODAS las etapas (sin filtros) para salidas existentes
      const { data: todasLasEtapasData, error: todasLasEtapasError } =
        await supabase
          .from("etapas_obra")
          .select("*")
          .eq("constructora_id", constructora.id)
          .order("orden", { ascending: true });

      if (todasLasEtapasError) {
        console.error("Error cargando todas las etapas:", todasLasEtapasError);
      } else {
        setTodasLasEtapas(todasLasEtapasData || []);
      }

      // Cargar subetapas de obra (solo activas para dropdowns)
      const { data: subetapasData, error: subetapasError } = await supabase
        .from("subetapas_obra")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("orden", { ascending: true });

      if (subetapasError) {
        console.error("Error cargando subetapas:", subetapasError);
      } else {
        setSubetapas(subetapasData || []);
      }

      // Cargar TODAS las subetapas (sin filtros) para salidas existentes
      const { data: todasLasSubetapasData, error: todasLasSubetapasError } =
        await supabase
          .from("subetapas_obra")
          .select("*")
          .eq("constructora_id", constructora.id)
          .order("orden", { ascending: true });

      if (todasLasSubetapasError) {
        console.error(
          "Error cargando todas las subetapas:",
          todasLasSubetapasError
        );
      } else {
        setTodasLasSubetapas(todasLasSubetapasData || []);
      }

      // Cargar proveedores de servicios (solo activos para dropdowns)
      const { data: proveedoresData, error: proveedoresError } = await supabase
        .from("proveedores_servicios")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("nombre");

      if (proveedoresError) {
        console.error("Error cargando proveedores:", proveedoresError);
      } else {
        setProveedores(proveedoresData || []);
      }

      // Cargar TODOS los proveedores (sin filtros) para salidas existentes
      const { data: todosLosProveedoresData, error: todosLosProveedoresError } =
        await supabase
          .from("proveedores_servicios")
          .select("*")
          .eq("constructora_id", constructora.id)
          .order("nombre");

      if (todosLosProveedoresError) {
        console.error(
          "Error cargando todos los proveedores:",
          todosLosProveedoresError
        );
      } else {
        setTodosLosProveedores(todosLosProveedoresData || []);
      }

      // Cargar proveedores frecuentes
      await cargarProveedoresFrecuentes();

      // Cargar todas las cajas
      const { data: cajasData, error: cajasError } = await supabase
        .from("cajas")
        .select(
          `
          *,
          proyectos (codigo)
        `
        )
        .eq("constructora_id", constructora.id)
        .order("tipo_caja");

      if (cajasError) {
        console.error("Error cargando cajas:", cajasError);
      } else {
        setCajas(cajasData || []);
      }
      // Cargar presupuestos iniciales
      const {
        data: presupuestosInicialesData,
        error: presupuestosInicialesError,
      } = await supabase
        .from("presupuestos_iniciales")
        .select("*")
        .eq("constructora_id", constructora.id);

      if (presupuestosInicialesError) {
        console.error(
          "Error cargando presupuestos iniciales:",
          presupuestosInicialesError
        );
      } else {
        setPresupuestosIniciales(presupuestosInicialesData || []);
      }
      // Cargar presupuestos
      const { data: presupuestosData, error: presupuestosError } =
        await supabase
          .from("presupuestos")
          .select("*")
          .eq("constructora_id", constructora.id);

      if (presupuestosError) {
        console.error("Error cargando presupuestos:", presupuestosError);
      } else {
        setPresupuestos(presupuestosData || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoading(false);
  };

  // Función para cargar proveedores frecuentes
  const cargarProveedoresFrecuentes = async (proyectoId = null) => {
    try {
      if (!proyectoId || proyectoId === "todos") {
        setProveedoresFrecuentes([]);
        return;
      }

      const { data, error } = await supabase
        .from('vista_proveedores_frecuentes')
        .select('proveedor_id')
        .eq('constructora_id', constructora.id)
        .eq('proyecto_id', proyectoId)
        .order('frecuencia', { ascending: false })
        .limit(5);
        
      if (!error) {
        setProveedoresFrecuentes(data.map(p => p.proveedor_id));
      }
    } catch (error) {
      console.error('Error cargando proveedores frecuentes:', error);
    }
  };

  // Función para aplicar todos los filtros
  const aplicarFiltros = () => {
    console.log("🔄 APLICANDO FILTROS EN SALIDAS");
    console.log("📊 Salidas originales:", salidas.length);
    console.log("👤 Es cliente:", isCliente);
    console.log("🔑 Permisos cliente:", permisosCliente);
    console.log("🏦 Cajas disponibles:", cajas.length);

    let salidasParaProcesar = [...salidas];
    console.log(
      "📝 Salidas para procesar (inicial):",
      salidasParaProcesar.length
    );

    // 1. FILTROS DE PERMISOS: Aplicar filtros de cliente primero
    if (isCliente && permisosCliente) {
      console.log("🔒 APLICANDO FILTROS DE CLIENTE");
      console.log("📋 Datos cliente antes del filtro:", {
        esCliente: permisosCliente.esCliente,
        proyectoId: permisosCliente.proyectoId,
        permisos: permisosCliente.permisos,
      });

      const salidasAntesFiltro = salidasParaProcesar.length;
      salidasParaProcesar = filtrarSalidasParaCliente(
        salidasParaProcesar,
        permisosCliente,
        cajas
      );

      console.log(
        `📊 Filtro aplicado: ${salidasAntesFiltro} → ${salidasParaProcesar.length} salidas`
      );
      console.log(
        "📋 Salidas después del filtro cliente:",
        salidasParaProcesar
      );
    } else {
      console.log("⚠️ NO se aplicaron filtros de cliente");
      console.log("Razones:", {
        isCliente,
        tienePermisosCliente: !!permisosCliente,
        cantidadCajas: cajas.length,
      });
    }

    // 2. FILTROS DE BÚSQUEDA: Aplicar filtros de interfaz
    const salidasFiltradas = salidasParaProcesar.filter((salida) => {
      // Filtro de búsqueda por texto
      const matchesSearch =
        salida.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        salida.proyectos?.codigo
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        salida.proyectos?.apellido_cliente
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        salida.proveedores_servicios?.nombre
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        salida.etapas_obra?.nombre
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        salida.subetapas_obra?.nombre
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      // Filtro por proyecto (solo para constructoras, clientes ya están filtrados)
      const matchesProject =
        isCliente ||
        filterProject === "todos" ||
        salida.proyecto_id === filterProject;

      // Filtro por mes
      const matchesMonth =
        filterMonth === "todos" || salida.fecha.substring(0, 7) === filterMonth;

      // Filtro por concepto
      const matchesConcepto =
        filterConcepto === "todos" || salida.concepto_id === filterConcepto;

      // Filtro por etapa
      const matchesEtapa =
        filterEtapa === "todos" || salida.etapa_id === filterEtapa;

      // Filtro de proveedor
      const matchesProveedor = !filterProveedor || salida.proveedor_id === filterProveedor;

      // Filtro de comisión (solo para constructoras)
      const matchesComision = isCliente || !filterComision ||
        (filterComision === "con_comision" && salida.tiene_comision) ||
        (filterComision === "sin_comision" && !salida.tiene_comision);

      return matchesSearch && matchesProject && matchesMonth && matchesConcepto && 
             matchesEtapa && matchesProveedor && matchesComision;
    });

    console.log("📊 Resultado final:", salidasFiltradas.length, "salidas");
    setSalidasFiltradas(salidasFiltradas);
  };

  // Función para abrir vista detallada
  const openDetailModal = (salida) => {
    setSelectedSalidaDetail(salida);
    setShowDetailModal(true);
  };

  const handleRestaurar = async (id) => {
    showCustomAlert(
      "confirm",
      "¿Restaurar salida?",
      "Se volverá a aplicar el impacto en la caja de origen.",
      async () => {
        const { error } = await supabase.rpc("rpc_restore_salida", {
          p_salida_id: id,
          p_usuario_id: usuario.id,
        });
        if (error) {
          showCustomAlert(
            "error",
            "Error",
            "No se pudo restaurar: " + error.message
          );
        } else {
          await cargarDatos();
          showCustomAlert("success", "Éxito", "Salida restaurada");
        }
      }
    );
  };

  // ✅ FUNCIÓN PARA EXPORTAR A CSV
  const exportarACSV = () => {
    if (salidasFiltradas.length === 0) {
      showCustomAlert("error", "Error", "No hay datos para exportar");
      return;
    }

    // Debug: verificar qué datos tenemos
    console.log("🔍 Debug - Datos para exportar:", salidasFiltradas.slice(0, 2));
    console.log("🔍 Debug - Primera salida completa:", salidasFiltradas[0]);
    console.log("🔍 Debug - Caja de la primera salida:", salidasFiltradas[0]?.cajas);
    console.log("🔍 Debug - Tipo de usuario:", usuario?.tipo_usuario);

    // Determinar si es cliente para mostrar datos limitados
    const esCliente = usuario?.tipo_usuario === 'cliente';

    // Preparar datos para CSV - DIFERENTES COLUMNAS SEGÚN TIPO DE USUARIO
    const datosCSV = salidasFiltradas.map((salida) => {
      // Determinar el origen: caja o cliente directo
      let origen = '';
      if (salida.caja_origen_id) {
        // Si tiene caja_origen_id, mostrar nombre de la caja
        origen = salida?.cajas?.nombre || (cajas?.find((c) => c.id === salida.caja_origen_id)?.nombre) || '';
      } else {
        // Si no tiene caja_origen_id, fue pago directo del cliente
        origen = salida.proyectos?.apellido_cliente || 'Cliente Directo';
      }
      
      if (esCliente) {
        // CSV LIMITADO PARA CLIENTES
        return {
          'Fecha': formatDateForDisplay(salida.fecha),
          'Descripción': salida.descripcion || '',
          'Monto': parseFloat(salida.monto).toLocaleString('es-AR', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          }),
          'Moneda': salida.moneda,
          'Origen': origen,
          'Concepto Salida': salida.conceptos_salida?.nombre || '',
          'Etapa': salida.etapas_obra?.nombre || '',
          'Subetapa': salida.subetapas_obra?.nombre || '',
          'Proveedor': salida.proveedores_servicios?.nombre || '',
          'Tiene Factura': salida.tiene_factura ? 'Sí' : 'No',
          'Facturador': salida.facturador || '',
          'Factura a Nombre de': salida.factura_a_nombre_de || '',
          'Tipo de Factura': salida.tipo_factura || '',
          'Método de Pago': salida.metodo_pago || '',
          'Tipo de Cambio': salida.tipo_cambio || ''
        };
            } else {
        // Cálculos de comisión y neto proveedor (solo constructoras)
        const tieneComision = !!salida.tiene_comision;
        const montoComision = Number(salida.monto_comision || 0);
        const retencionEnOrigen = !!salida.comision_retencion_en_origen;
        const montoSalida = Number(salida.monto || 0);
        const netoProveedor = retencionEnOrigen && tieneComision
          ? Math.max(montoSalida - montoComision, 0)
          : montoSalida;
        // CSV COMPLETO PARA CONSTRUCTORAS
        return {
          // Identificación básica
          'ID': salida.id,
      'Constructora': constructora.nombre,
      'Proyecto Código': salida.proyectos?.codigo || '',
      'Origen': origen,
      'Concepto Salida': salida.conceptos_salida?.nombre || '',
      'Proveedor': salida.proveedores_servicios?.nombre || '',
      'Etapa': salida.etapas_obra?.nombre || '',
      'Subetapa': salida.subetapas_obra?.nombre || '',
      
      // Datos principales
      'Fecha': formatDateForDisplay(salida.fecha),
      'Descripción': salida.descripcion || '',
      'Monto': parseFloat(salida.monto).toLocaleString('es-AR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }),
      'Moneda': salida.moneda,
      'Tipo de Cambio': salida.tipo_cambio || '',
      'Método de Pago': salida.metodo_pago || '',
      
      // Relaciones adicionales
      'Cliente': salida.proyectos?.apellido_cliente || '',
      
      // Documentos
      'Tiene Comprobante': salida.tiene_comprobante ? 'Sí' : 'No',
      'Comprobante URL': salida.comprobante_url || '',
      'Tiene Factura': salida.tiene_factura ? 'Sí' : 'No',
      'Factura URL': salida.factura_url || '',
      'Facturador': salida.facturador || '',
      'Factura a Nombre de': salida.factura_a_nombre_de || '',
      
      // Comisiones
      'Tiene Comisión': salida.tiene_comision ? 'Sí' : 'No',
      'Comisión Cobrada': salida.comision_cobrada ? 'Sí' : 'No',
      'Monto Comisión': salida.monto_comision ? parseFloat(salida.monto_comision).toLocaleString('es-AR', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }) : '',
      'Retención en Origen': retencionEnOrigen ? 'Sí' : 'No',
      'Neto Proveedor': netoProveedor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      'Facturador Comisión': salida.facturador_comision || '',
      'Factura a Nombre de Comisión': salida.factura_a_nombre_de_comision || '',
      'Comisión Relacionada ID': salida.comision_relacionada_id || '',
      
      // Auditoría
      'Creado por': salida.usuarios_created?.email || salida.created_by || '',
      'Fecha de Creación': salida.created_at ? new Date(salida.created_at).toLocaleString('es-AR') : '',
      'Actualizado por': salida.usuarios_updated?.email || salida.updated_by || '',
      'Fecha de Actualización': salida.updated_at ? new Date(salida.updated_at).toLocaleString('es-AR') : '',
      'Eliminado por': salida.usuarios_deleted?.email || salida.deleted_by || '',
      'Fecha de Eliminación': salida.deleted_at ? new Date(salida.deleted_at).toLocaleString('es-AR') : '',
      'Estado': salida.activo ? 'Activo' : 'Eliminado',
      'Razón de Eliminación': salida.deleted_reason || '',
      'Vía de Eliminación': salida.deleted_via || '',
      'Batch ID de Eliminación': salida.deleted_batch_id || ''
        };
      }
    });

    // Crear CSV
    const headers = Object.keys(datosCSV[0]);
    const csvContent = [
      headers.join(','),
      ...datosCSV.map(row => 
        headers.map(header => {
          const value = row[header];
          // Manejar valores null/undefined
          const stringValue = value !== null && value !== undefined ? value.toString() : '';
          // Escapar comillas y envolver en comillas si contiene coma o salto de línea
          const escapedValue = stringValue.replace(/"/g, '""');
          return escapedValue.includes(',') || escapedValue.includes('\n') ? `"${escapedValue}"` : escapedValue;
        }).join(',')
      )
    ].join('\n');

    // Crear y descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `salidas_${constructora.nombre}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    const tipoUsuario = esCliente ? "cliente" : "constructora";
    showCustomAlert("success", "Éxito", `Se exportaron ${salidasFiltradas.length} salidas a CSV (vista de ${tipoUsuario})`);
  };

  // Eliminar entrada de comisión (scope de Salidas)
  const eliminarEntradaComisionById = async (entradaId) => {
    try {
      // Obtener datos de la entrada antes de eliminar
      const { data: entradaEliminar } = await supabase
        .from("entradas")
        .select("id, monto, moneda, caja_destino_id")
        .eq("id", entradaId)
        .single();

      // Soft delete de la entrada
      await supabase
        .from("entradas")
        .update({
          activo: false,
          deleted_at: new Date().toISOString(),
          deleted_by: constructora?.usuario_id,
        })
        .eq("id", entradaId);

      // Poner comisión vinculada en pendiente y limpiar enlace
      await supabase
        .from("comisiones")
        .update({
          estado: 'pendiente',
          fecha_cobro: null,
          entrada_relacionada_id: null,
          updated_at: new Date().toISOString(),
        })
        .eq("entrada_relacionada_id", entradaId);

      // 🔧 ACTUALIZAR BALANCE: Revertir el monto en la caja
      if (entradaEliminar) {
        await actualizarBalanceCajaComision(entradaEliminar, null);
      }

      return true;
    } catch (e) {
      console.error('Error en eliminarEntradaComisionById:', e);
      return false;
    }
  };
  const handleEliminar = async (id) => {
    if (!puedeEliminar) {
      showCustomAlert(
        "error",
        "Sin permisos",
        "No tienes permisos para eliminar salidas"
      );
      return;
    }

    // Verificar comisión asociada (cobrada o pendiente) y, si existe, su entrada relacionada
    let comisionAsociada = null;
    let entradaComision = null;
    let proveedorNombre = "";
    try {
      const { data: comisiones } = await supabase
        .from("comisiones")
        .select("id, estado, entrada_relacionada_id, moneda, monto_comision")
        .eq("salida_id", id)
        .eq("activo", true)
        .is("deleted_at", null);
      comisionAsociada = Array.isArray(comisiones) && comisiones.length > 0 ? comisiones[0] : null;

      if (comisionAsociada?.entrada_relacionada_id) {
        const { data: entrada } = await supabase
          .from("entradas")
          .select("id, monto, moneda, fecha, descripcion")
          .eq("id", comisionAsociada.entrada_relacionada_id)
          .single();
        entradaComision = entrada || null;
      }

      // Obtener nombre del proveedor de la salida
      const { data: salidaInfo } = await supabase
        .from("salidas")
        .select("proveedor_id, proveedores_servicios (nombre)")
        .eq("id", id)
        .single();
      proveedorNombre = salidaInfo?.proveedores_servicios?.nombre || "";
    } catch (_) {}

    const descripcionEntrada = entradaComision
      ? `\n\nSe eliminará la entrada de comisión asociada:\n- Fecha: ${entradaComision.fecha}\n- Monto: ${entradaComision.moneda} $${parseFloat(entradaComision.monto || 0).toLocaleString()}\n- Proveedor: ${proveedorNombre || '—'}\n- Descripción: ${entradaComision.descripcion || '—'}`
      : (comisionAsociada ? "\n\nLa salida tiene comisión asociada, pero no hay entrada de comisión para eliminar." : "");

    const mensaje = `Esta acción revertirá los cambios en las cajas y no se puede deshacer.${descripcionEntrada}`;

    showCustomAlert(
      "confirm",
      "¿Eliminar salida?",
      mensaje,
      async () => {
        try {
          // Si existe una entrada asociada a la comisión, eliminarla primero
          if (entradaComision?.id) {
            try {
              await eliminarEntradaComisionById(entradaComision.id);
            } catch (e) {
              console.error("Error eliminando entrada de comisión asociada:", e);
            }
          }

          // SOFT DELETE vía RPC (ajusta cajas en servidor y marca eliminado)
          const { error } = await supabase.rpc("rpc_soft_delete_salida", {
            p_salida_id: id,
            p_usuario_id: usuario.id,
            p_reason: null,
          });

          if (error) {
            showCustomAlert(
              "error",
              "Error",
              "Error al eliminar: " + error.message
            );
          } else {
            // ✅ Disparar actualización silenciosa PIA → totales tras eliminar
            try {
              // Obtener proyecto_id de la salida eliminada para disparar actualización
              const { data: salidaEliminada } = await supabase
                .from("salidas")
                .select("proyecto_id")
                .eq("id", id)
                .single();
              
              if (salidaEliminada?.proyecto_id) {
                dispararActualizacionEnSegundoPlano(salidaEliminada.proyecto_id);
              }
            } catch (_) {}
            
            cargarDatos();
            
            // Mensaje de éxito personalizado
            const mensajeExito = entradaComision?.id
              ? `Salida eliminada correctamente. También se eliminó la entrada de comisión asociada (${entradaComision.moneda} $${parseFloat(entradaComision.monto || 0).toLocaleString()} - ${entradaComision.fecha}).`
              : "Salida eliminada correctamente";
            
            showCustomAlert(
              "success",
              "Éxito",
              mensajeExito
            );
          }
        } catch (err) {
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      }
    );
  };

  // Obtener meses únicos de salidas filtradas
  const mesesUnicos = [
    ...new Set(salidasFiltradas.map((e) => e.fecha.substring(0, 7))),
  ]
    .sort()
    .reverse();

  // Prevenir scroll shift cuando el modal está abierto
  usePreventScrollShift(showModal);

  return (
    <div
      className="main-container"
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* CSS anti-zoom obligatorio */}
      <style jsx>{`
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Hover effects */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>

      {/* Header unificado compacto */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: config.gradient,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 16px ${config.light}`,
                flexShrink: 0,
              }}
            >
              <TrendingDown size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isCliente ? "Tus gastos" : "Salidas"}
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isCliente
                  ? `Salidas de tu proyecto ${
                      permisosCliente?.proyectoCodigo || ""
                    }`
                  : `Gestioná los gastos de ${constructora?.nombre}`}
              </p>
            </div>
          </div>

          {/* Botones desktop */}
          {!isMobile && (
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {/* Botón Nueva Salida - solo si puede crear */}
              {puedeCrear && (
                <button
                  onClick={() => {
                    setEditingEntry(null);
                    setShowModal(true);
                  }}
                  className="button-hover"
                  style={{
                    background: config.gradient,
                    color: "white",
                    border: "none",
                    padding: "12px 20px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: `0 4px 16px ${config.light}`,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                    flexShrink: 0,
                    minWidth: "max-content",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Plus size={16} />
                  Nueva salida
                </button>
              )}
              
              

              {/* Botón Exportar CSV - siempre visible */}
              <button
                onClick={exportarACSV}
                className="button-hover"
                style={{
                  padding: isMobile ? "10px 16px" : "12px 20px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "12px",
                  background: "white",
                  color: "#475569",
                  fontSize: isMobile ? "14px" : "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                  flexShrink: 0,
                  minWidth: "max-content",
                  whiteSpace: "nowrap",
                }}
              >
                <Download size={16} />
                Exportar CSV
              </button>
            </div>
          )}
        </div>

        {/* Búsqueda, Proyecto y Botón Filtros (en la misma línea) */}
        <div style={{ 
          marginBottom: "20px",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr auto" : (isCliente ? "2.6fr auto" : "2fr 1fr auto"),
          gap: "12px",
          alignItems: "center"
        }}>
          {/* Campo de búsqueda */}
          <div style={{ position: "relative" }}>
            <Search
              size={16}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#64748b",
                zIndex: 1,
              }}
            />
            <input
              type="text"
              placeholder="Buscar por proyecto, cliente, proveedor, etapa o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus={false}
              autoComplete="off"
              style={{
                width: "100%",
                padding: isMobile
                  ? "12px 16px 12px 44px"
                  : "10px 10px 10px 36px",
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                fontSize: isMobile ? "16px" : "14px",
                WebkitAppearance: "none",
                appearance: "none",
                background: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                outline: "none",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#f1f5f9";
                e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
              }}
            />
          </div>

          {/* Botón Filtros para Mobile - solo visible en mobile */}
          {isMobile && (
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              style={{
                padding: "12px",
                border: "2px solid #e2e8f0",
                borderRadius: "12px",
                background: showFilterPanel ? "#f1f5f9" : "white",
                color: showFilterPanel ? "#1e293b" : "#475569",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                minWidth: "44px",
                minHeight: "44px",
              }}
            >
              <Settings size={18} />
            </button>
          )}

          {/* Filtro de proyecto - Solo para constructoras en desktop */}
          {!isCliente && !isMobile && (
            <CustomDropdown
              value={filterProject}
              onChange={(value) => {
                setFilterProject(value);
                guardarFiltroProyecto(value);
              }}
              options={[
                { value: "todos", label: "Todos los proyectos" },
                ...proyectos.map((proyecto) => ({
                  value: proyecto.id,
                  label: `${proyecto.codigo} - ${proyecto.apellido_cliente}`,
                })),
              ]}
              placeholder="Proyecto"
              icon={Building2}
            />
          )}

          {/* Botón Filtros - mover aquí para compartir línea con búsqueda y proyecto */}
          {!isMobile && (
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className="button-hover"
              style={{
                // Compacto SOLO para clientes: mismo alto, menos ancho
                padding: isCliente ? "10px 14px" : "12px 20px",
                border: isCliente ? "2px solid #f1f5f9" : "2px solid #e2e8f0",
                borderRadius: "12px",
                background: showFilterPanel ? "#f1f5f9" : (hayFiltrosActivos() ? "#fef3c7" : "white"),
                color: showFilterPanel ? "#1e293b" : (hayFiltrosActivos() ? "#92400e" : "#475569"),
                fontSize: isCliente ? "14px" : "13px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: isCliente ? "8px" : "6px",
                transition: isCliente ? "all 0.2s ease" : "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                minHeight: "44px",
                position: "relative",
                width: isCliente ? "fit-content" : undefined,
              }}
            >
              <Settings size={16} />
              Filtros
              {hayFiltrosActivos() && (
                <div
                  style={{
                    position: "absolute",
                    top: "-4px",
                    right: "-4px",
                    width: "8px",
                    height: "8px",
                    backgroundColor: "#f59e0b",
                    borderRadius: "50%",
                    border: "2px solid white",
                  }}
                />
              )}
            </button>
          )}
        </div>

        {/* Filtros Adicionales con animación de apertura/cierre */}
        <div
          style={{
            maxHeight: showFilterPanel ? (isMobile ? "500px" : "120px") : "0px",
            overflow: showFilterPanel ? "visible" : "hidden",
            transition: "all 0.25s ease-out",
            opacity: showFilterPanel ? 1 : 0,
            marginBottom: showFilterPanel ? "20px" : "0px",
            position: "relative",
            zIndex: 20,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isCliente
                ? "repeat(auto-fit, minmax(200px, 1fr))"
                : "1fr 0.7fr 1.2fr 1fr 1fr 1fr",
              gap: isMobile ? "8px" : "12px",
              alignItems: "center",
              padding: isMobile && showFilterPanel ? "12px 0" : "0",
            }}
          >
            {/* Filtro de proyecto - Solo para constructoras en mobile */}
            {!isCliente && isMobile && (
              <CustomDropdown
                value={filterProject}
                onChange={(value) => {
                  setFilterProject(value);
                  guardarFiltroProyecto(value);
                }}
                options={[
                  { value: "todos", label: "Todos los proyectos" },
                  ...proyectos.map((proyecto) => ({
                    value: proyecto.id,
                    label: `${proyecto.codigo} - ${proyecto.apellido_cliente}`,
                  })),
                ]}
                placeholder="Proyecto"
                icon={Building2}
              />
            )}

            {/* Filtro de mes */}
            <CustomDropdown
              value={filterMonth}
              onChange={(value) => {
                setFilterMonth(value);
                try { localStorage.setItem('salidas_filterMonth', value); } catch {}
              }}
              options={[
                { value: "todos", label: "Todos los meses" },
                ...mesesUnicos.map((mes) => ({
                  value: mes,
                  label: formatMonthForSelect(mes),
                })),
              ]}
              placeholder="Filtrar por mes"
              icon={Calendar}
            />

            {/* Filtro de estado - Solo para constructoras */}
            {!isCliente && (
              <CustomDropdown
                value={filterEstado}
                onChange={(value) => {
                  setFilterEstado(value);
                  try { localStorage.setItem('salidas_filterEstado', value); } catch {}
                }}
                options={[
                  { value: "activos", label: "Activos" },
                  { value: "eliminados", label: "Eliminados" },
                ]}
                placeholder="Estado"
                icon={Filter}
              />
            )}

            {/* Filtro por concepto */}
            <CustomDropdown
              value={filterConcepto}
              onChange={(value) => {
                setFilterConcepto(value);
                try { localStorage.setItem('salidas_filterConcepto', value); } catch {}
              }}
              options={[
                { value: "todos", label: "Todos los conceptos" },
                ...(
                  (isCliente || filterProject !== "todos")
                    ? conceptos
                        .filter((c) => salidas.some(s => (isCliente ? (permisosCliente?.proyectoId ? s.proyecto_id === permisosCliente.proyectoId : true) : (filterProject === "todos" || s.proyecto_id === filterProject)) && s.concepto_id === c.id))
                        .map((c) => ({ value: c.id, label: c.nombre }))
                    : conceptos.map((c) => ({ value: c.id, label: c.nombre }))
                ),
              ]}
              placeholder="Filtrar por concepto"
              icon={Tag}
            />

            {/* Filtro de etapa de obra */}
            <CustomDropdown
              value={filterEtapa}
              onChange={(value) => {
                setFilterEtapa(value);
                try { localStorage.setItem('salidas_filterEtapa', value); } catch {}
              }}
              options={[
                { value: "todos", label: "Todas las etapas" },
                ...(
                  (isCliente || filterProject !== "todos")
                    ? etapas
                        .filter((e) => salidas.some(s => (isCliente ? (permisosCliente?.proyectoId ? s.proyecto_id === permisosCliente.proyectoId : true) : (filterProject === "todos" || s.proyecto_id === filterProject)) && s.etapa_id === e.id))
                        .map((e) => ({ value: e.id, label: e.nombre }))
                    : etapas.map((e) => ({ value: e.id, label: e.nombre }))
                )
              ]}
              placeholder="Etapa"
              icon={Layers}
            />

            {/* Filtro de proveedores */}
            <CustomDropdown
              value={filterProveedor || "todos"}
              onChange={(value) => {
                setFilterProveedor(value === "todos" ? null : value);
                try { localStorage.setItem('salidas_filterProveedor', value === "todos" ? "" : value); } catch {}
              }}
              options={[
                { value: "todos", label: "Proveedores" },
                ...(
                  (isCliente || filterProject !== "todos")
                    ? proveedores
                        .filter((p) => salidas.some(s => (isCliente ? (permisosCliente?.proyectoId ? s.proyecto_id === permisosCliente.proyectoId : true) : (filterProject === "todos" || s.proyecto_id === filterProject)) && s.proveedor_id === p.id))
                        .map((p) => ({ value: p.id, label: p.nombre }))
                    : proveedores.map((p) => ({ value: p.id, label: p.nombre }))
                ),
              ]}
              placeholder="Proveedores"
              icon={User}
            />

            {/* Filtro de comisión - Solo para constructoras */}
            {!isCliente && (
              <CustomDropdown
                value={filterComision || "todos"}
                onChange={(value) => {
                  setFilterComision(value === "todos" ? null : value);
                  try { localStorage.setItem('salidas_filterComision', value === "todos" ? "" : value); } catch {}
                }}
                options={[
                  { value: "todos", label: "Todas las salidas" },
                  { value: "con_comision", label: "Con comisión" },
                  { value: "sin_comision", label: "Sin comisión" },
                ]}
                placeholder="Filtrar por comisión"
                icon={Tag}
              />
            )}
          </div>
        </div>
      </div>

      {/* Stats compactas - SOLO DESKTOP */}
      {!isMobile && (
        <div
          className="stats-section"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "12px",
            marginBottom: "20px",
            animation: "fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {(() => {
            // Detectar si hay filtro por proveedor activo
            const hayFiltroProveedor = filterProveedor !== null;
            
            // Detectar si alguna salida tiene comisión con retención en origen
            const haySalidasConComision = salidasFiltradas.some(s => 
              s.tiene_comision && s.comision_retencion_en_origen
            );
            
            // Solo mostrar vista de proveedor si hay filtro por proveedor Y hay comisiones
            const mostrarVistaProveedor = !isCliente && hayFiltroProveedor && haySalidasConComision;
            
            if (mostrarVistaProveedor) {
              // Calcular montos reales del proveedor (total - comisión)
              const salidasARS = salidasFiltradas.filter(s => s.moneda === "ARS");
              const salidasUSD = salidasFiltradas.filter(s => s.moneda === "USD");
              
              const totalARSProveedor = salidasARS.reduce((sum, s) => {
                const monto = parseFloat(s.monto || 0);
                const comision = s.tiene_comision && s.comision_retencion_en_origen 
                  ? parseFloat(s.monto_comision || 0) 
                  : 0;
                return sum + Math.max(monto - comision, 0);
              }, 0);
              
              const totalUSDProveedor = salidasUSD.reduce((sum, s) => {
                const monto = parseFloat(s.monto || 0);
                const comision = s.tiene_comision && s.comision_retencion_en_origen 
                  ? parseFloat(s.monto_comision || 0) 
                  : 0;
                return sum + Math.max(monto - comision, 0);
              }, 0);
              
              // Calcular equivalentes del proveedor
              const equivalenteARSProveedor = salidasFiltradas.reduce((sum, s) => {
                const monto = parseFloat(s.monto || 0);
                const comision = s.tiene_comision && s.comision_retencion_en_origen 
                  ? parseFloat(s.monto_comision || 0) 
                  : 0;
                const montoNeto = Math.max(monto - comision, 0);
                
                if (s.moneda === "ARS") {
                  return sum + montoNeto;
                } else if (s.moneda === "USD") {
                  return sum + (montoNeto * parseFloat(s.tipo_cambio || 1));
                }
                return sum;
              }, 0);
              
              const equivalenteUSDProveedor = salidasFiltradas.reduce((sum, s) => {
                const monto = parseFloat(s.monto || 0);
                const comision = s.tiene_comision && s.comision_retencion_en_origen 
                  ? parseFloat(s.monto_comision || 0) 
                  : 0;
                const montoNeto = Math.max(monto - comision, 0);
                
                if (s.moneda === "USD") {
                  return sum + montoNeto;
                } else if (s.moneda === "ARS") {
                  return sum + (montoNeto / parseFloat(s.tipo_cambio || 1));
                }
                return sum;
              }, 0);
              
              // Vista con montos de proveedor
              return [
                {
                  label: "Total ARS Proveedor",
                  value: "$" + totalARSProveedor.toLocaleString(),
                  icon: DollarSign,
                  color: "#0ea5e9",
                  subtitle: "Total equivalente: $" +
                    equivalenteARSProveedor.toLocaleString("es-AR", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }),
                },
                {
                  label: "Total USD Proveedor",
                  value: "US$" + totalUSDProveedor.toLocaleString(),
                  icon: DollarSign,
                  color: "#8b5cf6",
                  subtitle: "Total equivalente: US$" +
                    equivalenteUSDProveedor.toLocaleString("es-AR", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }),
                },
                {
                  label: "Total ARS Cliente",
                  value:
                    "$" +
                    salidasFiltradas
                      .filter((e) => e.moneda === "ARS")
                      .reduce((sum, e) => sum + parseFloat(e.monto), 0)
                      .toLocaleString(),
                  icon: DollarSign,
                  color: "#0ea5e9",
                  subtitle: "Total equivalente: $" +
                    salidasFiltradas
                      .reduce((sum, s) => {
                        if (s.moneda === "ARS") {
                          return sum + parseFloat(s.monto || 0);
                        } else if (s.moneda === "USD") {
                          return sum + (parseFloat(s.monto || 0) * parseFloat(s.tipo_cambio || 1));
                        }
                        return sum;
                      }, 0)
                      .toLocaleString("es-AR", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }),
                },
                {
                  label: "Total USD Cliente",
                  value:
                    "US$" +
                    salidasFiltradas
                      .filter((e) => e.moneda === "USD")
                      .reduce((sum, e) => sum + parseFloat(e.monto), 0)
                      .toLocaleString(),
                  icon: DollarSign,
                  color: "#8b5cf6",
                  subtitle: "Total equivalente: US$" +
                    salidasFiltradas
                      .reduce((sum, s) => {
                        if (s.moneda === "USD") {
                          return sum + parseFloat(s.monto || 0);
                        } else if (s.moneda === "ARS") {
                          return sum + (parseFloat(s.monto || 0) / parseFloat(s.tipo_cambio || 1));
                        }
                        return sum;
                      }, 0)
                      .toLocaleString("es-AR", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }),
                },
              ];
            } else {
              // Vista normal (sin filtro de proveedor o sin comisiones)
              return [
                {
                  label: "Total registros",
                  value: salidasFiltradas.length.toLocaleString(),
                  icon: BarChart3,
                  color: "#3b82f6",
                },
                {
                  label: "Este mes",
                  value: salidasFiltradas
                    .filter(
                      (e) =>
                        e.fecha.substring(0, 7) ===
                        new Date().toISOString().substring(0, 7)
                    )
                    .length.toLocaleString(),
                  icon: Calendar,
                  color: "#10b981",
                },
                {
                  label: "Total ARS",
                  value:
                    "$" +
                    salidasFiltradas
                      .filter((e) => e.moneda === "ARS")
                      .reduce((sum, e) => sum + parseFloat(e.monto), 0)
                      .toLocaleString(),
                  icon: DollarSign,
                  color: "#0ea5e9",
                  subtitle: "Total equivalente: $" +
                    salidasFiltradas
                      .reduce((sum, s) => {
                        if (s.moneda === "ARS") {
                          return sum + parseFloat(s.monto || 0);
                        } else if (s.moneda === "USD") {
                          return sum + (parseFloat(s.monto || 0) * parseFloat(s.tipo_cambio || 1));
                        }
                        return sum;
                      }, 0)
                      .toLocaleString("es-AR", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }),
                },
                {
                  label: "Total USD",
                  value:
                    "US$" +
                    salidasFiltradas
                      .filter((e) => e.moneda === "USD")
                      .reduce((sum, e) => sum + parseFloat(e.monto), 0)
                      .toLocaleString(),
                  icon: DollarSign,
                  color: "#8b5cf6",
                  subtitle: "Total equivalente: US$" +
                    salidasFiltradas
                      .reduce((sum, s) => {
                        if (s.moneda === "USD") {
                          return sum + parseFloat(s.monto || 0);
                        } else if (s.moneda === "ARS") {
                          return sum + (parseFloat(s.monto || 0) / parseFloat(s.tipo_cambio || 1));
                        }
                        return sum;
                      }, 0)
                      .toLocaleString("es-AR", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }),
                },
              ];
            }
          })().map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="stat-card"
                style={{
                  background: "white",
                  padding: "12px",
                  borderRadius: "12px",
                  textAlign: "center",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  minHeight: "80px",
                  animation: `slideInRight 0.${
                    8 + index
                  }s cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    background: `${stat.color}10`,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                  }}
                >
                  <IconComponent size={16} color={stat.color} />
                </div>
                <p
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    margin: "0 0 4px 0",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {stat.label}
                </p>
                <div>
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                      margin: 0,
                    }}
                  >
                    {stat.value}
                  </p>
                  {stat.subtitle && (
                    <p
                      style={{
                        fontSize: "9px",
                        fontWeight: "400",
                        color: "#9ca3af",
                        margin: "1px 0 0 0",
                        lineHeight: "1",
                      }}
                    >
                      {stat.subtitle}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lista de Salidas */}
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 20px",
            background: "white",
            borderRadius: "16px",
            border: "1px solid #f1f5f9",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "2px solid #e2e8f0",
                borderTop: "2px solid #3b82f6",
                borderRadius: "50%",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "500",
              }}
            >
              Cargando...
            </p>
          </div>
        </div>
      ) : salidasFiltradas.length === 0 ? (
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: isMobile ? "40px 24px" : "80px 40px",
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <div
            style={{
              background: config.light,
              borderRadius: "20px",
              padding: "20px",
              display: "inline-flex",
              marginBottom: "24px",
            }}
          >
            <TrendingDown size={48} color={config.solid} />
          </div>
          <h3
            style={{
              fontSize: isMobile ? "20px" : "24px",
              color: "#1e293b",
              marginBottom: "12px",
              fontWeight: "700",
            }}
          >
            {searchTerm || filterProject !== "todos" || filterMonth !== "todos"
              ? "No se encontraron salidas"
              : isCliente
              ? "No tienes salidas registradas"
              : "No hay salidas aún"}
          </h3>
          <p
            style={{
              color: "#64748b",
              fontSize: "16px",
              marginBottom:
                searchTerm ||
                filterProject !== "todos" ||
                filterMonth !== "todos"
                  ? "0"
                  : "32px",
              lineHeight: "1.5",
            }}
          >
            {searchTerm || filterProject !== "todos" || filterMonth !== "todos"
              ? "Intenta cambiar los filtros de búsqueda"
              : isCliente
              ? "Las salidas aparecerán cuando se registren pagos relacionados con tu proyecto"
              : "Comienza registrando tu primera salida"}
          </p>
          {!searchTerm &&
            filterProject === "todos" &&
            filterMonth === "todos" && (
              <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                {puedeCrear && (
                  <button
                    onClick={() => {
                      setEditingEntry(null);
                      setShowModal(true);
                    }}
                    className="button-hover"
                    style={{
                      background: config.gradient,
                      color: "white",
                      border: "none",
                      padding: "14px 28px",
                      borderRadius: "12px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      boxShadow: `0 8px 32px ${config.light}`,
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      touchAction: "manipulation",
                    }}
                  >
                    <Plus size={16} />
                    Crear primera salida
                  </button>
                )}
                
                {/* Botón Exportar CSV - siempre visible */}
                <button
                  onClick={exportarACSV}
                  className="button-hover"
                  style={{
                    padding: "14px 28px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    background: "white",
                    color: "#475569",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                >
                  <Download size={16} />
                  Exportar CSV
                </button>
              </div>
            )}
        </div>
      ) : (
        <>
          {/* MÓVIL: Cards unificadas profesionales */}
          {isMobile && isClient ? (
            <div style={{ padding: "8px" }}>
              {salidasFiltradas.map((salida, index) => {
                const proyecto = proyectos.find(
                  (p) => p.id === salida.proyecto_id
                );
                const proveedor = salida.proveedores_servicios;
                const concepto = conceptos.find(
                  (c) => c.id === salida.concepto_id
                );
                const etapa = etapas.find((e) => e.id === salida.etapa_id);
                const subetapa = subetapas.find(
                  (s) => s.id === salida.subetapa_id
                );

                return (
                  <div
                    key={salida.id}
                    onClick={() => openDetailModal(salida)}
                    style={{
                      background: "white",
                      border: "1px solid #f1f5f9",
                      borderRadius: "12px",
                      padding: "16px",
                      marginBottom: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      animation: `slideInRight 0.${
                        5 + (index % 10)
                      }s cubic-bezier(0.4, 0, 0.2, 1)`,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      touchAction: "manipulation",
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.transform = "scale(0.98)";
                      e.currentTarget.style.backgroundColor = "#f8fafc";
                    }}
                    onTouchEnd={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.backgroundColor = "white";
                    }}
                  >
                    {/* HEADER UNIFICADO */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#1e293b",
                            marginBottom: "4px",
                          }}
                        >
                          {proyecto?.codigo}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            fontWeight: "500",
                          }}
                        >
                          {proyecto?.apellido_cliente}
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: "right",
                          marginLeft: "12px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "18px",
                            fontWeight: "800",
                            color: config.solid,
                            marginBottom: "2px",
                          }}
                        >
                          -{salida.moneda} $
                          {Number(salida.monto).toLocaleString()}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#64748b",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: "4px",
                          }}
                        >
                          <Calendar size={10} />
                          {formatDateForDisplay(salida.fecha)}
                        </div>
                      </div>
                    </div>

                    {/* BADGES UNIFICADOS */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginBottom: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          background: "#eff6ff",
                          color: "#3b82f6",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "600",
                        }}
                      >
                        {concepto?.nombre}
                      </span>
                      {etapa && (
                        <span
                          style={{
                            background: "#f3e8ff",
                            color: "#7c3aed",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "9px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                          }}
                        >
                          <Layers size={8} />
                          {etapa.nombre}
                        </span>
                      )}
                      {subetapa && (
                        <span
                          style={{
                            background: "#fef3cd",
                            color: "#92400e",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "9px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                          }}
                        >
                          <Package size={8} />
                          {subetapa.nombre}
                        </span>
                      )}
                      <span
                        style={{
                          background: "#f1f5f9",
                          color: "#475569",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "9px",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "2px",
                        }}
                      >
                        <Users size={8} />
                        {proveedor?.nombre}
                      </span>
                    </div>

                    {/* DESCRIPCIÓN (si existe) */}
                    {salida.descripcion && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          marginBottom: "12px",
                          lineHeight: "1.4",
                        }}
                      >
                        {salida.descripcion}
                      </div>
                    )}

                    {/* FOOTER UNIFICADO */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingTop: "12px",
                        borderTop: "1px solid #f1f5f9",
                      }}
                    >
                      <div style={{ display: "flex", gap: "4px" }}>
                        {/* Origen del pago */}
                        {salida.cajas ? (
                          <span
                            style={{
                              fontSize: "9px",
                              background: "#eff6ff",
                              color: "#1d4ed8",
                              padding: "3px 6px",
                              borderRadius: "4px",
                              fontWeight: "500",
                              display: "flex",
                              alignItems: "center",
                              gap: "2px",
                            }}
                          >
                            <Banknote size={8} />
                            {salida.cajas.nombre}
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: "9px",
                              background: "#f0fdf4",
                              color: "#166534",
                              padding: "3px 6px",
                              borderRadius: "4px",
                              fontWeight: "500",
                              display: "flex",
                              alignItems: "center",
                              gap: "2px",
                            }}
                          >
                            <User size={8} />
                            Cliente
                          </span>
                        )}

                        {/* Documentos */}
                        {salida.tiene_comprobante && (
                          <span
                            style={{
                              fontSize: "9px",
                              background: "#dcfce7",
                              color: "#166534",
                              padding: "3px 6px",
                              borderRadius: "4px",
                              fontWeight: "500",
                              display: "flex",
                              alignItems: "center",
                              gap: "2px",
                            }}
                          >
                            <FileText size={8} />
                            Comprobante
                          </span>
                        )}
                        {salida.tiene_factura && (
                          <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                            <span
                              style={{
                                fontSize: "9px",
                                background: "#fef3cd",
                                color: "#92400e",
                                padding: "3px 6px",
                                borderRadius: "4px",
                                fontWeight: "500",
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                              }}
                            >
                              <FileText size={8} />
                              Factura
                            </span>
                            {salida.tipo_factura && (
                              <span style={{
                                background: salida.tipo_factura === 'A' ? '#dcfce7' : '#fef3c7',
                                color: salida.tipo_factura === 'A' ? '#166534' : '#92400e',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontSize: '8px',
                                fontWeight: '600',
                                flexShrink: 0
                              }}>
                                {salida.tipo_factura}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div style={{ display: "flex", gap: "4px" }}>
                        {puedeEditar && filterEstado === "activos" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingEntry(salida);
                              setShowModal(true);
                            }}
                            style={{
                              background: "#eff6ff",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 8px",
                              cursor: "pointer",
                              color: "#3b82f6",
                              touchAction: "manipulation",
                            }}
                          >
                            <Edit size={12} />
                          </button>
                        )}
                        {puedeEliminar && filterEstado === "activos" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEliminar(salida.id);
                            }}
                            style={{
                              background: "#fef2f2",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 8px",
                              cursor: "pointer",
                              color: "#ef4444",
                              touchAction: "manipulation",
                            }}
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                        {puedeEliminar && filterEstado === "eliminados" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestaurar(salida.id);
                            }}
                            style={{
                              background: "#f0fdf4",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 8px",
                              cursor: "pointer",
                              color: "#22c55e",
                              touchAction: "manipulation",
                            }}
                          >
                            <Package size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* DESKTOP: Tabla profesional */
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                border: "1px solid #f1f5f9",
              }}
            >
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      <th
                        style={{
                          padding: "16px 20px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.025em",
                          width: "8%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Calendar size={14} />
                          Fecha
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "16px 20px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.025em",
                          width: "15%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Building2 size={14} />
                          Proyecto
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "16px 20px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.025em",
                          width: "12%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Users size={14} />
                          Proveedor
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "16px 20px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.025em",
                          width: "10%",
                        }}
                      >
                        Concepto
                      </th>
                      <th
                        style={{
                          padding: "16px 20px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.025em",
                          width: "15%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Layers size={14} />
                          Etapa/Subetapa
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "16px 20px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.025em",
                          width: "12%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Wallet size={14} />
                          Origen
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "16px 20px",
                          textAlign: "right",
                          fontWeight: "600",
                          color: "#374151",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.025em",
                          width: "10%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: "6px",
                          }}
                        >
                          <DollarSign size={14} />
                          Monto
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "16px 20px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.025em",
                          width: "10%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <File size={14} />
                          Documentos
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                          fontWeight: "600",
                          color: "#374151",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.025em",
                          width: "8%",
                        }}
                      >
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {salidasFiltradas.map((salida, index) => {
                      const proyecto = proyectos.find(
                        (p) => p.id === salida.proyecto_id
                      );
                      const proveedor = salida.proveedores_servicios;
                      const concepto = todasLosConceptos.find(
                        (c) => c.id === salida.concepto_id
                      );
                      const etapa = todasLasEtapas.find(
                        (e) => e.id === salida.etapa_id
                      );
                      const subetapa = todasLasSubetapas.find(
                        (s) => s.id === salida.subetapa_id
                      );

                      return (
                        <tr
                          key={salida.id}
                          style={{
                            borderBottom:
                              index < salidasFiltradas.length - 1
                                ? "1px solid #f1f5f9"
                                : "none",
                            transition: "background-color 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.target.parentElement.style.backgroundColor =
                              "#f8fafc";
                          }}
                          onMouseLeave={(e) => {
                            e.target.parentElement.style.backgroundColor =
                              "transparent";
                          }}
                        >
                          <td
                            style={{
                              padding: "16px 20px",
                              color: "#374151",
                              fontSize: "14px",
                              fontWeight: "500",
                            }}
                          >
                            {formatDateForDisplay(salida.fecha)}
                          </td>
                          <td
                            style={{
                              padding: "16px 20px",
                              color: "#1e293b",
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  marginBottom: "2px",
                                }}
                              >
                                <Building2 size={12} color="#3b82f6" />
                                {proyecto?.codigo}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#64748b",
                                  fontWeight: "400",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <User size={10} />
                                {proyecto?.apellido_cliente}
                              </div>
                            </div>
                          </td>
                          <td
                            style={{
                              padding: "16px 20px",
                              color: "#64748b",
                              fontSize: "14px",
                            }}
                          >
                            <span
                              style={{
                                background: "#f1f5f9",
                                color: "#475569",
                                padding: "6px 10px",
                                borderRadius: "8px",
                                fontSize: "12px",
                                fontWeight: "500",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                width: "fit-content",
                              }}
                            >
                              <Users size={12} />
                              {proveedor?.nombre}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: "16px 20px",
                              color: "#64748b",
                              fontSize: "14px",
                            }}
                          >
                            <span
                              style={{
                                background: config.light,
                                color: config.solid,
                                padding: "6px 10px",
                                borderRadius: "8px",
                                fontSize: "12px",
                                fontWeight: "500",
                                width: "fit-content",
                                display: "inline-block",
                              }}
                            >
                              {concepto?.nombre}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: "16px 20px",
                              color: "#64748b",
                              fontSize: "12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              {etapa && (
                                <span
                                  style={{
                                    background: "#f3e8ff",
                                    color: "#7c3aed",
                                    padding: "4px 8px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "500",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    width: "fit-content",
                                  }}
                                >
                                  <Layers size={10} />
                                  {etapa.nombre}
                                </span>
                              )}
                              {subetapa && (
                                <span
                                  style={{
                                    background: "#fef3cd",
                                    color: "#92400e",
                                    padding: "4px 8px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "500",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    width: "fit-content",
                                  }}
                                >
                                  <Package size={10} />
                                  {subetapa.nombre}
                                </span>
                              )}
                              {!etapa && !subetapa && (
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontSize: "11px",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Sin asignar
                                </span>
                              )}
                            </div>
                          </td>
                          <td
                            style={{
                              padding: "16px 20px",
                              color: "#64748b",
                              fontSize: "14px",
                            }}
                          >
                            {salida.cajas ? (
                              <span
                                style={{
                                  background: "#eff6ff",
                                  color: "#1d4ed8",
                                  padding: "6px 10px",
                                  borderRadius: "8px",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  width: "fit-content",
                                }}
                              >
                                <Banknote size={12} />
                                {salida.cajas.nombre} {salida.cajas.moneda}
                              </span>
                            ) : (
                              <span
                                style={{
                                  background: "#f0fdf4",
                                  color: "#166534",
                                  padding: "6px 10px",
                                  borderRadius: "8px",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  width: "fit-content",
                                }}
                              >
                                <User size={12} />
                                Cliente: {proyecto?.apellido_cliente}
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              padding: "16px 20px",
                              textAlign: "right",
                              fontSize: "14px",
                              fontWeight: "700",
                              color: config.solid,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: "4px",
                              }}
                            >
                              <TrendingDown size={14} />-{salida.moneda}{" "}
                              {Number(salida.monto).toLocaleString()}
                            </div>
                            {salida.tipo_cambio && salida.moneda === "USD" && (
                              <div
                                style={{
                                  fontSize: "10px",
                                  color: "#64748b",
                                  fontWeight: "400",
                                  marginTop: "2px",
                                }}
                              >
                                TC: ${salida.tipo_cambio}
                              </div>
                            )}
                          </td>
                          <td
                            style={{
                              padding: "16px 20px",
                              color: "#64748b",
                              fontSize: "13px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "6px",
                                flexWrap: "wrap",
                              }}
                            >
                              {salida.tiene_comprobante && (
                                <button
                                  onClick={() =>
                                    window.open(
                                      salida.comprobante_url,
                                      "_blank"
                                    )
                                  }
                                  style={{
                                    fontSize: "10px",
                                    background: "#dcfce7",
                                    color: "#166534",
                                    padding: "4px 8px",
                                    borderRadius: "6px",
                                    border: "none",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    fontWeight: "500",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    touchAction: "manipulation",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.background = "#bbf7d0";
                                    e.target.style.transform =
                                      "translateY(-1px)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.background = "#dcfce7";
                                    e.target.style.transform = "translateY(0)";
                                  }}
                                >
                                  <FileText size={10} />
                                  Comprobante
                                </button>
                              )}
                              {salida.tiene_factura && (
                                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  <button
                                    onClick={() =>
                                      window.open(salida.factura_url, "_blank")
                                    }
                                    style={{
                                      fontSize: "10px",
                                      background: "#fef3cd",
                                      color: "#92400e",
                                      padding: "4px 8px",
                                      borderRadius: "6px",
                                      border: "none",
                                      cursor: "pointer",
                                      transition: "all 0.2s ease",
                                      fontWeight: "500",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      touchAction: "manipulation",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.background = "#fed7aa";
                                      e.target.style.transform =
                                        "translateY(-1px)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.background = "#fef3cd";
                                      e.target.style.transform = "translateY(0)";
                                    }}
                                  >
                                    <FileText size={10} />
                                    Factura
                                  </button>
                                  {salida.tipo_factura && (
                                    <span style={{
                                      background: salida.tipo_factura === 'A' ? '#dcfce7' : '#fef3c7',
                                      color: salida.tipo_factura === 'A' ? '#166534' : '#92400e',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      fontSize: '10px',
                                      fontWeight: '600',
                                      flexShrink: 0
                                    }}>
                                      {salida.tipo_factura}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td
                            style={{
                              padding: "16px 20px",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "6px",
                                justifyContent: "center",
                              }}
                            >
                              {/* Botón para ver detalles - SIEMPRE VISIBLE */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDetailModal(salida);
                                }}
                                className="action-button"
                                style={{
                                  background: "#eff6ff",
                                  border: "none",
                                  borderRadius: "6px",
                                  padding: "8px 10px",
                                  cursor: "pointer",
                                  color: "#1d4ed8",
                                  fontSize: "12px",
                                  transition: "all 0.2s ease",
                                  touchAction: "manipulation",
                                }}
                              >
                                <Eye size={14} />
                              </button>

                              {/* Solo mostrar editar/eliminar si tiene permisos */}
                              {puedeEditar && filterEstado === "activos" && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingEntry(salida);
                                    setShowModal(true);
                                  }}
                                  className="action-button"
                                  style={{
                                    background: "#f1f5f9",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "8px 10px",
                                    cursor: "pointer",
                                    color: "#475569",
                                    fontSize: "12px",
                                    transition: "all 0.2s ease",
                                    touchAction: "manipulation",
                                  }}
                                >
                                  <Edit size={14} />
                                </button>
                              )}
                              {puedeEliminar && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (filterEstado === "activos")
                                      handleEliminar(salida.id);
                                    else handleRestaurar(salida.id);
                                  }}
                                  className="action-button"
                                  style={{
                                    background:
                                      filterEstado === "activos"
                                        ? "#fef2f2"
                                        : "#f0fdf4",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "8px 10px",
                                    cursor: "pointer",
                                    color:
                                      filterEstado === "activos"
                                        ? "#dc2626"
                                        : "#22c55e",
                                    fontSize: "12px",
                                    transition: "all 0.2s ease",
                                    touchAction: "manipulation",
                                  }}
                                >
                                  {filterEstado === "activos" ? (
                                    <Trash2 size={14} />
                                  ) : (
                                    <Package size={14} />
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Botón flotante móvil unificado */}
      {isMobile && puedeCrear && (
        <div
          style={{
            position: "fixed",
            bottom: "100px",
            right: "20px",
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => {
              setEditingEntry(null);
              setShowModal(true);
            }}
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "28px",
              background: config.gradient,
              color: "white",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              boxShadow: `0 8px 32px ${config.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
            onTouchStart={(e) => {
              e.currentTarget.style.transform = "scale(0.95)";
            }}
            onTouchEnd={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <Plus size={24} />
          </button>
        </div>
      )}
      {/* Modal para crear/editar - Solo si tiene permisos */}
      {showModal && puedeCrear && (
        <SalidaModal
          constructora={constructora}
          usuario={usuario}
          salida={editingEntry}
          proyectos={proyectos}
          conceptos={conceptos}
          todasLosConceptos={todasLosConceptos}
          etapas={todasLasEtapas}
          subetapas={todasLasSubetapas}
          proveedores={proveedores}
          todosLosProveedores={todosLosProveedores}
          cajas={cajas}
          presupuestosIniciales={presupuestosIniciales}
          presupuestos={presupuestos}
          onClose={() => {
            setShowModal(false);
            setEditingEntry(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingEntry(null);
            cargarDatos();
            showCustomAlert(
              "success",
              "Éxito",
              editingEntry
                ? "Salida actualizada correctamente"
                : "Salida creada correctamente"
            );
          }}
          onConceptoCreado={(nuevoConcepto) => {
            setConceptos((prev) =>
              [...prev, nuevoConcepto].sort((a, b) =>
                a.nombre.localeCompare(b.nombre)
              )
            );
          }}
          onProveedorCreado={(nuevoProveedor) => {
            setProveedores((prev) => {
              const existe = prev.some((p) => p.id === nuevoProveedor.id);
              const lista = existe
                ? prev.map((p) => (p.id === nuevoProveedor.id ? { ...p, ...nuevoProveedor } : p))
                : [...prev, nuevoProveedor];
              return lista.sort((a, b) => a.nombre.localeCompare(b.nombre));
            });
            setTodosLosProveedores((prev) => {
              const existe = prev.some((p) => p.id === nuevoProveedor.id);
              const lista = existe
                ? prev.map((p) => (p.id === nuevoProveedor.id ? { ...p, ...nuevoProveedor } : p))
                : [...prev, nuevoProveedor];
              return lista.sort((a, b) => a.nombre.localeCompare(b.nombre));
            });
          }}
          onEtapaCreada={(nuevaEtapa) => {
            setEtapas((prev) =>
              [...prev, nuevaEtapa].sort(
                (a, b) => (a.orden || 999) - (b.orden || 999)
              )
            );
            setTodasLasEtapas((prev) =>
              [...prev, nuevaEtapa].sort(
                (a, b) => (a.orden || 999) - (b.orden || 999)
              )
            );
          }}
          onSubetapaCreada={(nuevaSubetapa) => {
            setSubetapas((prev) =>
              [...prev, nuevaSubetapa].sort(
                (a, b) => (a.orden || 999) - (b.orden || 999)
              )
            );
            setTodasLasSubetapas((prev) =>
              [...prev, nuevaSubetapa].sort(
                (a, b) => (a.orden || 999) - (b.orden || 999)
              )
            );
          }}
          isMobile={isMobile}
          showCustomAlert={showCustomAlert}
          permisosCliente={permisosCliente}
        />
      )}

      {/* Modal de Vista Detallada */}
      {showDetailModal && selectedSalidaDetail && (
        <SalidaDetailModal
          salida={selectedSalidaDetail}
          proyectos={proyectos}
          proveedores={todosLosProveedores}
          cajas={cajas}
          conceptos={todasLosConceptos}
          etapas={todasLasEtapas}
          subetapas={todasLasSubetapas}
          constructora={constructora}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedSalidaDetail(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* Modal personalizado para alertas */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        isMobile={isMobile}
      />

    </div>
  );
}

// Modal para vista detallada de la salida
function SalidaDetailModal({
  salida,
  proyectos,
  proveedores,
  cajas,
  conceptos,
  etapas,
  subetapas,
  constructora,
  onClose,
  isMobile,
}) {
  const proyecto = proyectos.find((p) => p.id === salida.proyecto_id);
  const proveedor = salida.proveedores_servicios;
  const concepto = conceptos.find((c) => c.id === salida.concepto_id);
  const etapa = etapas.find((e) => e.id === salida.etapa_id);
  const subetapa = subetapas.find((s) => s.id === salida.subetapa_id);
  const cajaOrigen = cajas.find((c) => c.id === salida.caja_origen_id);

  const obtenerNombrePorCuit = (cuit) => {
    if (!cuit) return "";

    if (constructora?.cuit === cuit) {
      return constructora.nombre;
    }

    const proyectoEncontrado = proyectos.find((p) => p.cuit_cliente === cuit);
    if (proyectoEncontrado) {
      return proyectoEncontrado.apellido_cliente;
    }

    const proveedorEncontrado = proveedores.find((p) => p.cuit === cuit);
    if (proveedorEncontrado) {
      return proveedorEncontrado.nombre;
    }

    return "";
  };

  // Bloquear el scroll del fondo mientras el modal esté abierto
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 12000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
        overscrollBehavior: "contain",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header unificado */}
        <div
          style={{
            background: getVistaColor("salidas").gradient,
            color: "white",
            padding: isMobile ? "20px" : "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TrendingDown size={20} color="white" />
            </div>
            <div>
              <h2
                style={{
                  margin: "0 0 4px 0",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                }}
              >
                Vista detallada - Salida
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                }}
              >
                {proyecto?.codigo} - {formatDateForDisplay(salida.fecha)}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "10px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              touchAction: "manipulation",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido con scroll */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "30px",
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "24px",
            }}
          >
            {/* Información Principal */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={18} />
                Información principal
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Proyecto */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Building2 size={12} />
                    Proyecto
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {proyecto?.codigo}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#64748b",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <User size={12} />
                    Cliente: {proyecto?.apellido_cliente}
                  </p>
                </div>

                {/* Proveedor */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Users size={12} />
                    Proveedor
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {proveedor?.nombre}
                  </p>
                  {proveedor?.cuit && (
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "13px",
                        color: "#64748b",
                        fontFamily: "monospace",
                      }}
                    >
                      CUIT: {proveedor.cuit}
                    </p>
                  )}
                </div>

                {/* Concepto */}
                <div
                  style={{
                    background: "#fef2f2",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #fecaca",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#991b1b",
                      textTransform: "uppercase",
                    }}
                  >
                    Concepto
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#dc2626",
                    }}
                  >
                    {concepto?.nombre}
                  </p>
                </div>

                {/* Etapa y Subetapa */}
                {(etapa || subetapa) && (
                  <div
                    style={{
                      background: "#f3e8ff",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "1px solid #e9d5ff",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 12px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#7c3aed",
                        textTransform: "uppercase",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Layers size={12} />
                      Etapa de Obra
                    </p>

                    {etapa && (
                      <div style={{ marginBottom: subetapa ? "12px" : "0" }}>
                        <p
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "11px",
                            fontWeight: "600",
                            color: "#6b21a8",
                            textTransform: "uppercase",
                          }}
                        >
                          Etapa
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#7c3aed",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Layers size={14} />
                          {etapa.nombre}
                        </p>
                      </div>
                    )}

                    {subetapa && (
                      <div>
                        <p
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "11px",
                            fontWeight: "600",
                            color: "#6b21a8",
                            textTransform: "uppercase",
                          }}
                        >
                          Subetapa
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#7c3aed",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Package size={14} />
                          {subetapa.nombre}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Monto */}
                <div
                  style={{
                    background: getVistaColor("salidas").light,
                    padding: "20px",
                    borderRadius: "12px",
                    border: `2px solid ${getVistaColor("salidas").solid}`,
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#991b1b",
                      textTransform: "uppercase",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <DollarSign size={12} />
                    Monto
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: isMobile ? "24px" : "28px",
                      fontWeight: "800",
                      color: getVistaColor("salidas").solid,
                    }}
                  >
                    -{salida.moneda} ${Number(salida.monto).toLocaleString()}
                  </p>
                  {salida.tipo_cambio && salida.moneda === "USD" && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      Tipo de cambio: ${salida.tipo_cambio}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/* Información Adicional */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Info size={18} />
                Detalles adicionales
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Fecha */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Calendar size={12} />
                    Fecha
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {formatDateForDisplay(salida.fecha)}
                  </p>
                </div>

                {/* Origen del pago */}
                <div
                  style={{
                    background: cajaOrigen ? "#eff6ff" : "#f0fdf4",
                    padding: "16px",
                    borderRadius: "12px",
                    border: `1px solid ${cajaOrigen ? "#dbeafe" : "#dcfce7"}`,
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: cajaOrigen ? "#1e40af" : "#166534",
                      textTransform: "uppercase",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Wallet size={12} />
                    Origen del pago
                  </p>
                  {cajaOrigen ? (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#1d4ed8",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Banknote size={16} />
                      {cajaOrigen.nombre} {cajaOrigen.moneda}
                    </p>
                  ) : (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#166534",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <User size={16} />
                      Cliente: {proyecto?.apellido_cliente}
                    </p>
                  )}
                </div>

                {/* Método de pago */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <CreditCard size={12} />
                    Método de pago
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                      textTransform: "capitalize",
                    }}
                  >
                    {salida.metodo_pago}
                  </p>
                </div>

                {/* Descripción */}
                {salida.descripcion && (
                  <div
                    style={{
                      background: "#f8fafc",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                      }}
                    >
                      Descripción
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#1e293b",
                        lineHeight: "1.5",
                      }}
                    >
                      {salida.descripcion}
                    </p>
                  </div>
                )}

                {/* Datos de facturación */}
                {salida.tiene_factura &&
                  (salida.facturador || salida.factura_a_nombre_de) && (
                    <div
                      style={{
                        background: "#fefbf3",
                        padding: "16px",
                        borderRadius: "12px",
                        border: "1px solid #fbbf24",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#f59e0b",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <FileText size={14} />
                        Datos de facturación
                      </p>

                      {salida.facturador && (
                        <div style={{ marginBottom: "12px" }}>
                          <p
                            style={{
                              margin: "0 0 4px 0",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#92400e",
                              textTransform: "uppercase",
                            }}
                          >
                            Facturador
                          </p>
                          <p
                            style={{
                              margin: "0 0 2px 0",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#1e293b",
                              fontFamily: "monospace",
                            }}
                          >
                            {salida.facturador}
                          </p>
                          {obtenerNombrePorCuit(salida.facturador) && (
                            <p
                              style={{
                                margin: 0,
                                fontSize: "12px",
                                color: "#059669",
                                fontWeight: "500",
                              }}
                            >
                              {obtenerNombrePorCuit(salida.facturador)}
                            </p>
                          )}
                        </div>
                      )}

                      {salida.factura_a_nombre_de && (
                        <div>
                          <p
                            style={{
                              margin: "0 0 4px 0",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#92400e",
                              textTransform: "uppercase",
                            }}
                          >
                            Factura a nombre de
                          </p>
                          <p
                            style={{
                              margin: "0 0 2px 0",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#1e293b",
                              fontFamily: "monospace",
                            }}
                          >
                            {salida.factura_a_nombre_de}
                          </p>
                          {obtenerNombrePorCuit(salida.factura_a_nombre_de) && (
                            <p
                              style={{
                                margin: 0,
                                fontSize: "12px",
                                color: "#059669",
                                fontWeight: "500",
                              }}
                            >
                              {obtenerNombrePorCuit(salida.factura_a_nombre_de)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Documentos adjuntos */}
          {(salida.tiene_comprobante || salida.tiene_factura) && (
            <div
              style={{
                marginTop: "32px",
              }}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <File size={18} />
                Documentos adjuntos
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "20px",
                }}
              >
                {salida.tiene_comprobante && salida.comprobante_url && (
                  <div
                    style={{
                      background: "white",
                      border: "2px solid #22c55e",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                        color: "white",
                        padding: "12px 16px",
                        fontSize: "14px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <FileText size={14} />
                        Comprobante
                      </span>
                      <div style={{ display: "flex", gap: "4px" }}>
                        <button
                          onClick={() => {
                            const proyecto = proyectos.find(
                              (p) => p.id === salida.proyecto_id
                            );
                            const fileName = `comprobante_${
                              proyecto?.codigo || "salida"
                            }_${formatDateForDisplay(salida.fecha).replace(
                              /\//g,
                              "-"
                            )}.${salida.comprobante_url.split(".").pop()}`;

                            const a = document.createElement("a");
                            a.href = salida.comprobante_url;
                            a.download = fileName;
                            a.target = "_blank";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            touchAction: "manipulation",
                          }}
                        >
                          <Download size={10} />
                        </button>
                        <button
                          onClick={() =>
                            window.open(salida.comprobante_url, "_blank")
                          }
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            touchAction: "manipulation",
                          }}
                        >
                          <Eye size={10} />
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        height: "200px",
                        background: "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        window.open(salida.comprobante_url, "_blank")
                      }
                    >
                      {salida.comprobante_url.match(
                        /\.(jpg|jpeg|png|gif|webp)$/i
                      ) ? (
                        <img
                          src={salida.comprobante_url}
                          alt="Comprobante"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            color: "white",
                            textAlign: "center",
                          }}
                        >
                          <FileText size={32} style={{ marginBottom: "8px" }} />
                          <p style={{ fontSize: "12px", margin: 0 }}>
                            Vista previa no disponible
                          </p>
                          <p
                            style={{
                              fontSize: "10px",
                              margin: "4px 0 0 0",
                              opacity: 0.8,
                            }}
                          >
                            Click para abrir
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {salida.tiene_factura && salida.factura_url && (
                  <div
                    style={{
                      background: "white",
                      border: "2px solid #f59e0b",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        color: "white",
                        padding: "12px 16px",
                        fontSize: "14px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <FileText size={14} />
                        Factura
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {salida.tipo_factura && (
                          <span style={{
                            background: salida.tipo_factura === 'A' ? '#dcfce7' : '#fef3c7',
                            color: salida.tipo_factura === 'A' ? '#166534' : '#92400e',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {salida.tipo_factura}
                          </span>
                        )}
                        <button
                          onClick={() => {
                            const proyecto = proyectos.find(
                              (p) => p.id === salida.proyecto_id
                            );
                            const fileName = `factura_${
                              proyecto?.codigo || "salida"
                            }_${formatDateForDisplay(salida.fecha).replace(
                              /\//g,
                              "-"
                            )}.${salida.factura_url.split(".").pop()}`;

                            const a = document.createElement("a");
                            a.href = salida.factura_url;
                            a.download = fileName;
                            a.target = "_blank";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            touchAction: "manipulation",
                          }}
                        >
                          <Download size={10} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            // Crear un enlace temporal para abrir en nueva pestaña sin recargar
                            const link = document.createElement('a');
                            link.href = salida.factura_url;
                            link.target = '_blank';
                            link.rel = 'noopener noreferrer';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            touchAction: "manipulation",
                          }}
                        >
                          <Eye size={10} />
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        height: "200px",
                        background: "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Crear un enlace temporal para abrir en nueva pestaña sin recargar
                        const link = document.createElement('a');
                        link.href = salida.factura_url;
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      {salida.factura_url.match(
                        /\.(jpg|jpeg|png|gif|webp)$/i
                      ) ? (
                        <img
                          src={salida.factura_url}
                          alt="Factura"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            color: "white",
                            textAlign: "center",
                          }}
                        >
                          <FileText size={32} style={{ marginBottom: "8px" }} />
                          <p style={{ fontSize: "12px", margin: 0 }}>
                            Vista previa no disponible
                          </p>
                          <p
                            style={{
                              fontSize: "10px",
                              margin: "4px 0 0 0",
                              opacity: 0.8,
                            }}
                          >
                            Click para abrir
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// Modal profesional para crear/editar salidas con etapas y subetapas
function SalidaModal({
  constructora,
  usuario,
  salida,
  proyectos,
  conceptos,
  todasLosConceptos,
  etapas,
  subetapas,
  proveedores,
  todosLosProveedores,
  cajas,
  presupuestosIniciales,
  presupuestos,
  onClose,
  onSave,
  onConceptoCreado,
  onProveedorCreado,
  onEtapaCreada,
  onSubetapaCreada,
  isMobile,
  showCustomAlert,
  permisosCliente,
}) {



  
  const [formData, setFormData] = useState({
    proyecto_id: "",
    concepto_id: "",
    proveedor_id: "",
    etapa_id: "",
    subetapa_id: "",
    monto: "",
    moneda: "ARS",
    fecha: new Date().toISOString().split("T")[0],
    descripcion: "",
    metodo_pago: "transferencia",
    tipo_cambio: "1",
    caja_origen_id: "",
    comprobante_url: "",
    factura_url: "",
    tiene_comprobante: false,
    tiene_factura: false,
    tipo_factura: "A",
    facturador: "",
    factura_a_nombre_de: "",
    tiene_comision: false,
    monto_comision: "",
    comision_cobrada: false,
    tiene_comprobante_comision: false,
    tiene_factura_comision: false,
    comprobante_comision_url: "",
    factura_comision_url: "",
    facturador_comision: "",
    factura_a_nombre_de_comision: "",
    comision_retencion_en_origen: false,
    moneda_comision: "",
    tipo_cambio_comision: "",
    es_adicional: false,

  });

  const [guardando, setGuardando] = useState(false);
  const [subiendoComprobante, setSubiendoComprobante] = useState(false);
  const [subiendoFactura, setSubiendoFactura] = useState(false);
  const [subiendoComprobanteComision, setSubiendoComprobanteComision] = useState(false);
  const [subiendoFacturaComision, setSubiendoFacturaComision] = useState(false);
  const [dolarOficial, setDolarOficial] = useState(null); // venta para salida
  const [dolarOficialCompra, setDolarOficialCompra] = useState(null); // compra para comisión
  const [loadingDolar, setLoadingDolar] = useState(false);
  const [saldoInsuficiente, setSaldoInsuficiente] = useState(false);

  // Estados para modales de proveedor y subetapa
  const [showNewProveedorModal, setShowNewProveedorModal] = useState(false);
  const [showNewSubetapaModal, setShowNewSubetapaModal] = useState(false);
  const [showCuitModal, setShowCuitModal] = useState(false);
  const [proveedorParaCuit, setProveedorParaCuit] = useState(null);
  const [guardandoProveedor, setGuardandoProveedor] = useState(false);
  const [guardandoSubetapa, setGuardandoSubetapa] = useState(false);
  const [guardandoCuit, setGuardandoCuit] = useState(false);
  const [nuevoProveedor, setNuevoProveedor] = useState({
    nombre: "",
    cuit: "",
  });
  const [nuevaSubetapa, setNuevaSubetapa] = useState({
    nombre: "",
    etapa_id: "",
  });
  const [cuitProveedor, setCuitProveedor] = useState("");
  const [showCuitDiferenteModal, setShowCuitDiferenteModal] = useState(false);
  const [cuitDiferenteData, setCuitDiferenteData] = useState(null);

  // Cajas disponibles según el proyecto seleccionado
  const [cajasDisponibles, setCajasDisponibles] = useState([]);

  // Subetapas filtradas por etapa seleccionada
  const [subetapasFiltradas, setSubetapasFiltradas] = useState([]);
  
  // Estado para proveedores frecuentes en el modal
  const [proveedoresFrecuentes, setProveedoresFrecuentes] = useState([]);

  // Helper: parsea string de monto con miles en puntos y decimales con coma o punto
  const parseMontoNumber = (valor) => {
    if (valor === null || valor === undefined) return 0;
    try {
      const original = String(valor);
      const limpio = original.replace(/\./g, "").replace(",", ".");
      const num = parseFloat(limpio);
      const resultado = isNaN(num) ? 0 : num;
      
      console.log("🔍 DEBUG parseMontoNumber:", {
        valorOriginal: valor,
        valorString: original,
        valorLimpio: limpio,
        numeroFinal: resultado
      });
      
      return resultado;
    } catch (error) {
      console.log("🔍 DEBUG parseMontoNumber ERROR:", error, valor);
      return 0;
    }
  };

  // Obtener configuración de vista
  const config = getVistaColor("salidas");

  // Función para verificar estado real de comisión
  const verificarEstadoComision = async (salidaId) => {
    try {
      // Buscar comisión relacionada
      const { data: comision } = await supabase
        .from("comisiones")
        .select("id, estado, entrada_relacionada_id, moneda, tipo_cambio")
        .eq("salida_id", salidaId)
        .eq("activo", true)
        .is("deleted_at", null)
        .single();

      if (comision) {
        // Verificar si la entrada relacionada existe y está activa
        if (comision.entrada_relacionada_id) {
          const { data: entrada } = await supabase
            .from("entradas")
            .select("id, activo")
            .eq("id", comision.entrada_relacionada_id)
            .single();

          // Si la entrada no existe o no está activa, actualizar comisión a pendiente
          if (!entrada || !entrada.activo) {
            await supabase
              .from("comisiones")
              .update({
                estado: 'pendiente',
                fecha_cobro: null,
                entrada_relacionada_id: null,
                updated_at: new Date().toISOString(),
              })
              .eq("id", comision.id);

            // Mostrar notificación de cambio automático
            showCustomAlert("info", "Estado actualizado", "La comisión ha cambiado automáticamente a 'pendiente' porque la entrada relacionada fue eliminada.");

            return { estado: 'pendiente', moneda: comision.moneda, tipo_cambio: comision.tipo_cambio };
          }
        }
        return { estado: comision.estado, moneda: comision.moneda, tipo_cambio: comision.tipo_cambio };
      }
      return null;
    } catch (error) {
      console.error("Error verificando estado de comisión:", error);
      return null;
    }
  };

  // Función para cargar proveedores frecuentes en el modal
  const cargarProveedoresFrecuentes = async (proyectoId) => {
    try {
      if (!proyectoId || proyectoId === "todos") {
        setProveedoresFrecuentes([]);
        return;
      }

      const { data, error } = await supabase
        .from('vista_proveedores_frecuentes')
        .select('proveedor_id')
        .eq('constructora_id', constructora.id)
        .eq('proyecto_id', proyectoId)
        .order('frecuencia', { ascending: false })
        .limit(5);
        
      if (!error) {
        setProveedoresFrecuentes(data.map(p => p.proveedor_id));
      }
    } catch (error) {
      console.error('Error cargando proveedores frecuentes:', error);
    }
  };

  useEffect(() => {
    const inicializarFormData = async () => {
      if (salida) {

        console.log("🔍 Inicializando formData con salida:", {
          etapa_id: salida.etapa_id,
          subetapa_id: salida.subetapa_id,
        });

        // Verificar estado real de comisión si tiene comisión
        let estadoComisionReal = null;
        let comisionInfo = null;
        if (salida.tiene_comision) {
          comisionInfo = await verificarEstadoComision(salida.id);
          estadoComisionReal = comisionInfo?.estado;
          console.log("🔍 Estado real de comisión:", estadoComisionReal);
          console.log("🔍 Información completa de comisión:", comisionInfo);
        }

        console.log("🔍 EDITANDO SALIDA - Tipo de cambio original:", salida.tipo_cambio);
        setFormData({
          proyecto_id: salida.proyecto_id || "",
          concepto_id: salida.concepto_id || "",
          proveedor_id: salida.proveedor_id || "",
          etapa_id: salida.etapa_id || "",
          subetapa_id: salida.subetapa_id || "",
          monto: salida.monto || "",
          moneda: salida.moneda || "ARS",
          fecha: salida.fecha || new Date().toISOString().split("T")[0],
          descripcion: salida.descripcion || "",
          metodo_pago: salida.metodo_pago || "transferencia",
          tipo_cambio: salida.tipo_cambio ? salida.tipo_cambio.toString() : "1",
          caja_origen_id: salida.caja_origen_id || "",
          comprobante_url: salida.comprobante_url || "",
          factura_url: salida.factura_url || "",
          tiene_comprobante: salida.tiene_comprobante || false,
          tiene_factura: salida.tiene_factura || false,
          tipo_factura: salida.tipo_factura || "A",
          facturador: salida.facturador || "",
          factura_a_nombre_de: salida.factura_a_nombre_de || "",
          tiene_comision: salida.tiene_comision || false,
          monto_comision: (salida.monto_comision?.toString() || ""),
          comision_cobrada: estadoComisionReal === 'cobrada' ? true : false,
          moneda_comision: comisionInfo?.moneda || salida.moneda || "ARS",
          es_adicional: salida.es_adicional || false,
          tipo_cambio_comision: comisionInfo?.tipo_cambio ? comisionInfo.tipo_cambio.toString() : "",
          tiene_comprobante_comision: salida.tiene_comprobante_comision || false,
          tiene_factura_comision: salida.tiene_factura_comision || false,
          comprobante_comision_url: salida.comprobante_comision_url || "",
          factura_comision_url: salida.factura_comision_url || "",
          facturador_comision: salida.facturador_comision || "",
          factura_a_nombre_de_comision: salida.factura_a_nombre_de_comision || "",
          comision_retencion_en_origen: salida.comision_retencion_en_origen || false,

        });
      } else {
        // Si es cliente, preseleccionar su proyecto
        if (permisosCliente?.proyectoId) {
          setFormData((prev) => ({
            ...prev,
            proyecto_id: permisosCliente.proyectoId,
          }));
        }
      }
    };

    inicializarFormData();
  }, [salida, permisosCliente]);



  // Cargar cotización del dólar al abrir el modal (solo nuevas salidas)
  useEffect(() => {
    if (!salida) {
      cargarDolarOficial();
    }
    // Si se está editando, NO hacer nada con el dólar oficial
  }, [salida]);

  // Precargar dólar oficial COMPRA para comisión al montar
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("https://dolarapi.com/v1/dolares/oficial");
        const data = await res.json();
        const compra = data?.compra;
        if (compra) setDolarOficialCompra(compra);
      } catch (e) {
        console.error('Error precargando dólar oficial compra', e);
      }
    })();
  }, []);

  // Cargar cotización VENTA para comisión cuando se habilita comisión
  useEffect(() => {
    (async () => {
      try {
        if (formData.tiene_comision) {
          // Obtener siempre el valor actual de venta para comisiones
          const res = await fetch("https://dolarapi.com/v1/dolares/oficial");
          const data = await res.json();
          const venta = data?.venta;
          
          if (venta) {
            setDolarOficialCompra(venta); // Reutilizamos la variable existente pero con valor de venta
          }
          
          if ((formData.moneda_comision || formData.moneda) === 'USD' && !formData.tipo_cambio_comision) {
            const tc = venta || null;
            if (tc) {
              setFormData((prev) => ({ ...prev, tipo_cambio_comision: tc.toString() }));
            }
          }
          if ((formData.moneda_comision || formData.moneda) === 'ARS' && !formData.tipo_cambio_comision) {
            setFormData((prev) => ({ ...prev, tipo_cambio_comision: '1' }));
          }
        }
      } catch (e) {
        console.error('Error inicializando TC comisión', e);
      }
    })();
  }, [formData.tiene_comision, formData.moneda_comision]);

  // Función para calcular equivalente en la otra moneda
  const calcularEquivalente = () => {
    if (!formData.monto || !formData.tipo_cambio) return null;

    const monto = parseFloat(formData.monto);
    const tipoCambio = parseFloat(formData.tipo_cambio);

    if (formData.moneda === "USD") {
      const equivalenteARS = monto * tipoCambio;
      return `≈ ARS $${equivalenteARS.toLocaleString("es-AR", {
        maximumFractionDigits: 2,
      })}`;
    } else {
      const equivalenteUSD = monto / tipoCambio;
      return `≈ USD $${equivalenteUSD.toLocaleString("en-US", {
        maximumFractionDigits: 2,
      })}`;
    }
  };

  // Actualizar cajas disponibles cuando cambia el proyecto O la moneda
  useEffect(() => {
    if (formData.proyecto_id) {
      const proyectoSeleccionado = proyectos.find(
        (p) => p.id === formData.proyecto_id
      );
      actualizarCajasDisponibles(proyectoSeleccionado);

      // Auto-completar factura a nombre del cliente
      if (proyectoSeleccionado && !formData.factura_a_nombre_de) {
        setFormData((prev) => ({
          ...prev,
          factura_a_nombre_de: proyectoSeleccionado.cuit_cliente || "",
        }));
      }
    } else {
      setCajasDisponibles([]);
    }
  }, [formData.proyecto_id, formData.moneda, cajas, proyectos]);

  // Cargar proveedores frecuentes cuando cambia el proyecto
  useEffect(() => {
    if (formData.proyecto_id && !salida) {
      cargarProveedoresFrecuentes(formData.proyecto_id);
    } else {
      setProveedoresFrecuentes([]);
    }
  }, [formData.proyecto_id, salida]);

  // Filtrar subetapas cuando cambia la etapa seleccionada
  useEffect(() => {
    console.log("🔍 Filtrando subetapas:", {
      etapa_id: formData.etapa_id,
      subetapa_id: formData.subetapa_id,
      total_subetapas: subetapas.length,
      editando: !!salida,
    });

    if (formData.etapa_id) {
      // Para dropdowns, usar solo subetapas activas + la actual si se está editando
      const subetapasActivas = subetapas.filter(
        (s) => s.activo === true || s.id === formData.subetapa_id
      );
      const subetapasParaEtapa = subetapasActivas.filter(
        (s) => s.etapa_id === formData.etapa_id
      );
      setSubetapasFiltradas(subetapasParaEtapa);

      console.log("🔍 Subetapas para etapa:", {
        etapa_id: formData.etapa_id,
        subetapas_encontradas: subetapasParaEtapa.length,
        subetapa_actual: formData.subetapa_id,
        subetapa_encontrada: subetapasParaEtapa.find(
          (s) => s.id === formData.subetapa_id
        ),
      });

      // Solo limpiar subetapa si estamos creando una nueva salida (no editando)
      // y la subetapa seleccionada no pertenece a la nueva etapa
      if (
        !salida && // Solo para nuevas salidas
        formData.subetapa_id &&
        !subetapasParaEtapa.find((s) => s.id === formData.subetapa_id)
      ) {
        console.log(
          "⚠️ Limpiando subetapa porque no pertenece a la etapa (nueva salida)"
        );
        setFormData((prev) => ({
          ...prev,
          subetapa_id: "",
        }));
      }
    } else {
      setSubetapasFiltradas([]);
      // Solo limpiar subetapa si estamos creando una nueva salida
      if (!salida) {
        console.log("⚠️ Limpiando subetapa porque no hay etapa (nueva salida)");
        setFormData((prev) => ({
          ...prev,
          subetapa_id: "",
        }));
      }
    }
  }, [formData.etapa_id, subetapas, salida]);

  // Auto-completar usando vista de cajas y conceptos frecuentes + buscar defaults inteligentes de etapas
  useEffect(() => {
    if (formData.proveedor_id && formData.proyecto_id && formData.moneda) {
      const proveedorSeleccionado = proveedores.find(
        (p) => p.id === formData.proveedor_id
      );
      if (
        proveedorSeleccionado &&
        proveedorSeleccionado.cuit &&
        !formData.facturador
      ) {
        setFormData((prev) => ({
          ...prev,
          facturador: proveedorSeleccionado.cuit,
        }));
      }

      // SOLO buscar defaults inteligentes si NO estamos editando (nueva salida)
      if (!salida) {
        buscarDefaultsInteligentes();
      }
    }
  }, [formData.proveedor_id, formData.proyecto_id, formData.moneda]);

  // Limpiar campos relacionados cuando cambia el proveedor (solo para nuevas salidas)
  const proveedorAnteriorRef = useRef(null);
  const aplicarDefaultsForzadosRef = useRef(false);
  
  useEffect(() => {
    if (!salida && formData.proveedor_id && proveedorAnteriorRef.current && proveedorAnteriorRef.current !== formData.proveedor_id) {
      // Buscar el nuevo proveedor primero
      const nuevoProveedor = todosLosProveedores.find(p => p.id === formData.proveedor_id);
      
      console.log("🔄 Proveedor cambiado de", proveedorAnteriorRef.current, "a", formData.proveedor_id);
      console.log("🔄 Nuevo proveedor:", nuevoProveedor?.nombre, "CUIT:", nuevoProveedor?.cuit);
      
      // Limpiar campos que dependen del proveedor específico
      
      setFormData((prev) => ({
        ...prev,
        concepto_id: "",
        etapa_id: "",
        subetapa_id: "",
        caja_origen_id: "",
        metodo_pago: "transferencia",
        // Actualizar CUIT del facturador con el nuevo proveedor
        facturador: nuevoProveedor?.cuit ? formatearCUIT(nuevoProveedor.cuit) : "",
      }));
      
      // Buscar defaults del nuevo proveedor en el próximo tick para que los clears se apliquen primero
      aplicarDefaultsForzadosRef.current = true;
      setTimeout(() => {
        buscarDefaultsInteligentes({
          proveedor_id: formData.proveedor_id,
          proyecto_id: formData.proyecto_id,
          moneda: formData.moneda,
        });
      }, 50);
    }
    
    // Actualizar el ref con el proveedor actual
    proveedorAnteriorRef.current = formData.proveedor_id;
  }, [formData.proveedor_id]);



  // Verificar saldo cuando cambia caja origen o monto
  useEffect(() => {
    verificarSaldo();
  }, [formData.caja_origen_id, formData.monto, formData.moneda]);

  // Inicializar etapa en modal de subetapa cuando se abre
  useEffect(() => {
    if (showNewSubetapaModal && formData.etapa_id) {
      setNuevaSubetapa((prev) => ({
        ...prev,
        etapa_id: formData.etapa_id,
      }));
    }
  }, [showNewSubetapaModal, formData.etapa_id]);

  // Auto-completar CUIT del facturador cuando se marca "tiene factura" o cambia el proveedor
  useEffect(() => {
    if (
      formData.tiene_factura &&
      formData.proveedor_id &&
      !formData.facturador
    ) {
      // Buscar en la lista de proveedores
      let proveedorSeleccionado = proveedores.find(
        (p) => p.id === formData.proveedor_id
      );

      // Si no se encuentra en la lista, puede ser un proveedor recién creado
      // Buscar en el proveedor que se acaba de crear/actualizar
      if (
        !proveedorSeleccionado &&
        proveedorParaCuit &&
        proveedorParaCuit.id === formData.proveedor_id
      ) {
        proveedorSeleccionado = proveedorParaCuit;
      }

      if (proveedorSeleccionado && proveedorSeleccionado.cuit) {
        setFormData((prev) => ({
          ...prev,
          facturador: formatearCUIT(proveedorSeleccionado.cuit),
        }));
      }
    }
  }, [
    formData.tiene_factura,
    formData.proveedor_id,
    formData.facturador,
    proveedores,
    proveedorParaCuit,
  ]);

  // Auto-completar CUIT del facturador cuando se selecciona un proveedor con CUIT
  useEffect(() => {
    if (formData.tiene_factura && formData.proveedor_id) {
      // Buscar en la lista de proveedores
      let proveedorSeleccionado = proveedores.find(
        (p) => p.id === formData.proveedor_id
      );

      // Si no se encuentra en la lista, puede ser un proveedor recién creado
      if (
        !proveedorSeleccionado &&
        proveedorParaCuit &&
        proveedorParaCuit.id === formData.proveedor_id
      ) {
        proveedorSeleccionado = proveedorParaCuit;
      }

      if (
        proveedorSeleccionado &&
        proveedorSeleccionado.cuit &&
        !formData.facturador
      ) {
        setFormData((prev) => ({
          ...prev,
          facturador: formatearCUIT(proveedorSeleccionado.cuit),
        }));
      }
    }
  }, [formData.proveedor_id, proveedores, proveedorParaCuit]);

  // Auto-completar CUITs de comisión cuando cambia proveedor o proyecto
  useEffect(() => {
    if (formData.tiene_factura_comision) {
      const proveedorSeleccionado = todosLosProveedores.find(
        (p) => p.id === formData.proveedor_id
      );
      const proyectoSeleccionado = proyectos.find(
        (p) => p.id === formData.proyecto_id
      );

      // Auto-completar CUIT facturador si no está lleno
      if (proveedorSeleccionado && proveedorSeleccionado.cuit && !formData.facturador_comision) {
        setFormData((prev) => ({
          ...prev,
          facturador_comision: formatearCUIT(proveedorSeleccionado.cuit),
        }));
      }

      // Auto-completar CUIT facturado si no está lleno
      // Auto-completar CUIT de la constructora para comisión
      if (!formData.factura_a_nombre_de_comision) {
        const proyectoSeleccionado = proyectos.find(p => p.id === formData.proyecto_id);
        if (proyectoSeleccionado && proyectoSeleccionado.cuit_cliente) {
          setFormData((prev) => ({
            ...prev,
            factura_a_nombre_de_comision: proyectoSeleccionado.cuit_cliente,
          }));
        }
      }
    }
  }, [formData.proveedor_id, formData.proyecto_id, formData.tiene_factura_comision, todosLosProveedores, proyectos]);

  const cargarDolarOficial = async () => {
    // SOLO cargar si NO se está editando
    if (salida) {
      console.log("🚫 NO cargando dólar oficial porque se está editando");
      return;
    }
    
    setLoadingDolar(true);
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      const dolarVenta = data.venta;
      setDolarOficial(dolarVenta);

      // Autocompletar tipo de cambio SOLO si está vacío
      setFormData((prev) => {
        const current = parseFloat(prev.tipo_cambio);
        const shouldAutocomplete = !prev.tipo_cambio || Number.isNaN(current) || current === 1;
        return shouldAutocomplete ? { ...prev, tipo_cambio: dolarVenta.toString() } : prev;
      });

      console.log("Dólar oficial (venta) cargado:", dolarVenta);
    } catch (error) {
      console.error("Error cargando dólar oficial:", error);
      setDolarOficial(1000);
      // Autocompletar tipo de cambio SOLO si está vacío
      setFormData((prev) => {
        const current = parseFloat(prev.tipo_cambio);
        const shouldAutocomplete = !prev.tipo_cambio || Number.isNaN(current) || current === 1;
        return shouldAutocomplete ? { ...prev, tipo_cambio: "1000" } : prev;
      });
    }
    setLoadingDolar(false);
  };
  // Función para buscar defaults usando la vista actualizada (acepta overrides para evitar carreras)
  const buscarDefaultsInteligentes = async (overrides = {}) => {
    const proveedorId = overrides.proveedor_id ?? formData.proveedor_id;
    const proyectoId = overrides.proyecto_id ?? formData.proyecto_id;
    const monedaSel = overrides.moneda ?? formData.moneda;

    if (!proveedorId || !proyectoId || !monedaSel) {
      console.log("❌ FALTAN DATOS:", {
        proveedor_id: proveedorId,
        proyecto_id: proyectoId,
        moneda: monedaSel,
      });
      return;
    }

    try {
      console.log("🔍 BUSCANDO DEFAULTS INTELIGENTES...");
      console.log("📋 Parámetros de búsqueda:", { constructora_id: constructora.id, proyecto_id: proyectoId, proveedor_id: proveedorId, moneda: monedaSel });

      // 1. BUSCAR EL DEFAULT ESPECÍFICO DE CAJAS
      const { data: defaultData, error } = await supabase
        .from("vista_cajas_frecuentes")
        .select(
          "caja_tipo_frecuente, concepto_id_frecuente, metodo_pago_frecuente, frecuencia_caja, frecuencia_concepto, frecuencia_metodo_pago"
        )
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoId)
        .eq("proveedor_id", proveedorId)
        .eq("moneda", monedaSel)
        .single();

      console.log("🎯 DEFAULT ESPECÍFICO ENCONTRADO:", defaultData);
      console.log("❓ ERROR EN BÚSQUEDA ESPECÍFICA:", error);

      if (!error && defaultData) {
        console.log("✅ APLICANDO DEFAULTS DE CAJAS...");

        // 1. CONCEPTO más frecuente
        if (defaultData.concepto_id_frecuente && (aplicarDefaultsForzadosRef.current || !formData.concepto_id)) {
          console.log(
            "📝 APLICANDO CONCEPTO:",
            defaultData.concepto_id_frecuente
          );
          setFormData((prev) => ({ ...prev, concepto_id: defaultData.concepto_id_frecuente }));
        }

        // 2. CAJA más frecuente
        if (defaultData.caja_tipo_frecuente) {
          console.log(
            "🏦 BUSCANDO CAJA TIPO:",
            defaultData.caja_tipo_frecuente
          );
          console.log(
            "🏦 CAJAS DISPONIBLES:",
            cajas.map((c) => ({
              id: c.id,
              nombre: c.nombre,
              tipo: c.tipo_caja,
              moneda: c.moneda,
            }))
          );

          const cajaInteligente = cajas.find(
            (caja) =>
              caja.tipo_caja === defaultData.caja_tipo_frecuente &&
              caja.moneda === monedaSel &&
              (caja.tipo_caja === "empresa" || caja.proyecto_id === proyectoId)
          );

          console.log("🏦 CAJA ENCONTRADA:", cajaInteligente);

          if (cajaInteligente && (aplicarDefaultsForzadosRef.current || !formData.caja_origen_id)) {
            console.log("🏦 APLICANDO CAJA:", cajaInteligente.nombre);
            setFormData((prev) => ({ ...prev, caja_origen_id: cajaInteligente.id }));
          }
        }

        // 3. MÉTODO DE PAGO más frecuente
        if (defaultData.metodo_pago_frecuente && (aplicarDefaultsForzadosRef.current || formData.metodo_pago === "transferencia")) {
          console.log(
            "💳 APLICANDO MÉTODO DE PAGO:",
            defaultData.metodo_pago_frecuente
          );
          setFormData((prev) => ({ ...prev, metodo_pago: defaultData.metodo_pago_frecuente }));
        }
      }

      // 2. BUSCAR DEFAULTS DE ETAPAS Y SUBETAPAS MÁS FRECUENTES PARA ESTE PROVEEDOR
      const { data: etapasData, error: etapasError } = await supabase
        .from("salidas")
        .select(
          "etapa_id, subetapa_id, etapas_obra(nombre), subetapas_obra(nombre)"
        )
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", proyectoId)
        .eq("proveedor_id", proveedorId)
        .not("etapa_id", "is", null)
        .not("subetapa_id", "is", null)
        .order("created_at", { ascending: false })
        .limit(10);

      if (!etapasError && etapasData && etapasData.length > 0) {
        console.log("🔍 HISTORIAL DE ETAPAS ENCONTRADO:", etapasData);

        // Contar frecuencia de etapas
        const frecuenciaEtapas = {};
        const frecuenciaSubetapas = {};

        etapasData.forEach((item) => {
          if (item.etapa_id) {
            frecuenciaEtapas[item.etapa_id] =
              (frecuenciaEtapas[item.etapa_id] || 0) + 1;
          }
          if (item.subetapa_id) {
            frecuenciaSubetapas[item.subetapa_id] =
              (frecuenciaSubetapas[item.subetapa_id] || 0) + 1;
          }
        });

        // Obtener etapa más frecuente
        const etapaMasFrecuente = Object.keys(frecuenciaEtapas).reduce((a, b) =>
          frecuenciaEtapas[a] > frecuenciaEtapas[b] ? a : b
        );

        // Obtener subetapa más frecuente
        const subetapaMasFrecuente = Object.keys(frecuenciaSubetapas).reduce(
          (a, b) => (frecuenciaSubetapas[a] > frecuenciaSubetapas[b] ? a : b)
        );

        console.log(
          "📊 ETAPA MÁS FRECUENTE:",
          etapaMasFrecuente,
          "con",
          frecuenciaEtapas[etapaMasFrecuente],
          "usos"
        );
        console.log(
          "📊 SUBETAPA MÁS FRECUENTE:",
          subetapaMasFrecuente,
          "con",
          frecuenciaSubetapas[subetapaMasFrecuente],
          "usos"
        );

        // Aplicar solo si no hay etapa seleccionada
        if ((aplicarDefaultsForzadosRef.current || !formData.etapa_id) && etapaMasFrecuente) {
          setFormData((prev) => ({ ...prev, etapa_id: etapaMasFrecuente }));
        }

        // Aplicar solo si no hay subetapa seleccionada
        if ((aplicarDefaultsForzadosRef.current || !formData.subetapa_id) && subetapaMasFrecuente) {
          setFormData((prev) => ({ ...prev, subetapa_id: subetapaMasFrecuente }));
        }
      }

      console.log("⚠️ NO HAY HISTORIAL DE ETAPAS, USANDO DEFAULTS BÁSICOS...");

      // Fallback: defaults básicos si no hay historial
      if (!formData.caja_origen_id) {
        const cajaDefault = cajas.find(
          (caja) =>
            caja.tipo_caja === "proyecto" &&
            caja.moneda === formData.moneda &&
            caja.proyecto_id === formData.proyecto_id
        );

        if (cajaDefault) {
          console.log("🏦 APLICANDO CAJA DEFAULT:", cajaDefault.nombre);
          setFormData((prev) => ({
            ...prev,
            caja_origen_id: cajaDefault.id,
          }));
        }
      }
    } catch (error) {
      console.error("❌ ERROR EN DEFAULTS INTELIGENTES:", error);
    } finally {
      aplicarDefaultsForzadosRef.current = false;
    }
  };

  const verificarSaldo = () => {
    if (!formData.caja_origen_id || !formData.monto) {
      setSaldoInsuficiente(false);
      return;
    }

    const cajaOrigen = cajas.find((c) => c.id === formData.caja_origen_id);
    if (cajaOrigen) {
      const saldoDisponible = parseFloat(cajaOrigen.balance_actual) || 0;
      const montoSalida = parseFloat(formData.monto) || 0;
      setSaldoInsuficiente(montoSalida > saldoDisponible);
    }
  };

  // Función para buscar nombres por CUIT
  const obtenerNombrePorCuit = (cuit) => {
    if (!cuit) return "";

    if (constructora?.cuit === cuit) {
      return constructora.nombre;
    }

    const proyectoEncontrado = proyectos.find((p) => p.cuit_cliente === cuit);
    if (proyectoEncontrado) {
      return proyectoEncontrado.apellido_cliente;
    }

    const proveedorEncontrado = todosLosProveedores.find((p) => p.cuit === cuit);
    if (proveedorEncontrado) {
      return proveedorEncontrado.nombre;
    }

    return "";
  };

  const actualizarCajasDisponibles = (proyecto) => {
    if (!proyecto) return;

    const cajasProyecto = cajas.filter((caja) => {
      if (caja.moneda !== formData.moneda) return false;

      if (caja.tipo_caja === "empresa") return true;
      if (caja.proyecto_id === proyecto.id) return true;

      return false;
    });

    const cajasOrdenadas = cajasProyecto.sort((a, b) => {
      if (a.tipo_caja === "empresa" && b.tipo_caja !== "empresa") return -1;
      if (a.tipo_caja !== "empresa" && b.tipo_caja === "empresa") return 1;
      if (a.tipo_caja === "proyecto" && b.tipo_caja === "mano_obra") return -1;
      if (a.tipo_caja === "mano_obra" && b.tipo_caja === "proyecto") return 1;
      return a.nombre.localeCompare(b.nombre);
    });

    setCajasDisponibles(cajasOrdenadas);

    if (formData.caja_origen_id) {
      const cajaEstaDisponible = cajasOrdenadas.some(
        (caja) => caja.id === formData.caja_origen_id
      );
      if (!cajaEstaDisponible) {
        console.log(
          "🔄 Caja seleccionada no disponible para",
          formData.moneda,
          "- Limpiando selección"
        );
        setFormData((prev) => ({
          ...prev,
          caja_origen_id: "",
        }));
      }
    }
  };

  const generarNombreCaja = (caja) => {
    const nombreBase = caja.nombre || `Caja ${caja.tipo_caja}`;
    return `${nombreBase} ${caja.moneda}`;
  };

  // Función para verificar si existe presupuesto para la combinación
  const verificarPresupuestoExiste = (
    proyectoId,
    conceptoId,
    etapaId,
    subetapaId,
    proveedorId
  ) => {
    if (!proyectoId || !conceptoId || !proveedorId) {
      return { tieneInicial: false, tienePresupuesto: false };
    }

    // Verificar presupuesto inicial (requiere etapa y subetapa)
    const tieneInicial =
      etapaId &&
      subetapaId &&
      presupuestosIniciales.some(
        (p) =>
          p.proyecto_id === proyectoId &&
          p.concepto_salida_id === conceptoId &&
          p.etapa_id === etapaId &&
          p.subetapa_id === subetapaId
      );

    // Verifica: Proyecto + Concepto + Proveedor + Etapa + Subetapa
    const tienePresupuesto =
      etapaId &&
      subetapaId &&
      presupuestos.some(
        (p) =>
          p.proyecto_id === proyectoId &&
          p.concepto_salida_id === conceptoId &&
          p.proveedor_id === proveedorId &&
          p.etapa_id === etapaId && // ✅ AGREGADO
          p.subetapa_id === subetapaId // ✅ AGREGADO
      );

    return { tieneInicial, tienePresupuesto };
  };

  // Funciones para crear nuevos elementos
  const crearNuevoConcepto = async (nombre) => {
    try {
      const { data, error } = await supabase
        .from("conceptos_salida")
        .insert([
          {
            constructora_id: constructora.id,
            nombre: nombre.trim(),
            activo: true,
          },
        ])
        .select();

      if (error) {
        console.error("Error creando concepto:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al crear concepto: " + error.message
        );
      } else {
        const nuevoConcepto = data[0];
        onConceptoCreado(nuevoConcepto);
        setFormData((prev) => ({
          ...prev,
          concepto_id: nuevoConcepto.id,
        }));
        showCustomAlert("success", "Éxito", "Concepto creado correctamente");
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
  };

  const crearNuevoProveedor = async (nombre) => {
    try {
      // Verificar si ya existe un proveedor inactivo con el mismo nombre
      const { data: proveedorExistente } = await supabase
        .from("proveedores_servicios")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("nombre", nombre.trim())
        .eq("activo", false)
        .single();

      let result;
      if (proveedorExistente) {
        // Auto-reactivación silenciosa: actualizar el proveedor existente
        result = await supabase
          .from("proveedores_servicios")
          .update({
            activo: true,
          })
          .eq("id", proveedorExistente.id)
          .select();
      } else {
        // Crear nuevo proveedor
        result = await supabase
          .from("proveedores_servicios")
          .insert([
            {
              constructora_id: constructora.id,
              nombre: nombre.trim(),
              cuit: null,
              activo: true,
            },
          ])
          .select();
      }

      const { data, error } = result;

      if (error) {
        console.error("Error creando proveedor:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al crear proveedor: " + error.message
        );
      } else {
        const nuevoProveedor = data[0];
        onProveedorCreado(nuevoProveedor);
        setFormData((prev) => ({
          ...prev,
          proveedor_id: nuevoProveedor.id,
        }));

        // Mostrar modal para agregar CUIT solo si no tiene CUIT
        if (!nuevoProveedor.cuit) {
          setProveedorParaCuit(nuevoProveedor);
          setShowCuitModal(true);
        }
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
  };

  const agregarNuevoProveedor = async () => {
    if (!nuevoProveedor.nombre.trim()) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor ingresa un nombre para el proveedor"
      );
      return;
    }

    setGuardandoProveedor(true);
    try {
      // Verificar si ya existe un proveedor inactivo con el mismo nombre
      const { data: proveedorExistente } = await supabase
        .from("proveedores_servicios")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("nombre", nuevoProveedor.nombre.trim())
        .eq("activo", false)
        .single();

      let result;
      if (proveedorExistente) {
        // Auto-reactivación silenciosa: actualizar el proveedor existente
        result = await supabase
          .from("proveedores_servicios")
          .update({
            cuit: nuevoProveedor.cuit.trim() || null,
            activo: true,
          })
          .eq("id", proveedorExistente.id)
          .select();
      } else {
        // Crear nuevo proveedor
        result = await supabase
          .from("proveedores_servicios")
          .insert([
            {
              constructora_id: constructora.id,
              nombre: nuevoProveedor.nombre.trim(),
              cuit: nuevoProveedor.cuit.trim() || null,
              activo: true,
            },
          ])
          .select();
      }

      const { data, error } = result;

      if (error) {
        console.error("Error guardando proveedor:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al guardar proveedor: " + error.message
        );
      } else {
        showCustomAlert("success", "Éxito", "Proveedor agregado correctamente");

        const nuevoProveedorCreado = data[0];
        onProveedorCreado(nuevoProveedorCreado);

        setFormData((prev) => ({
          ...prev,
          proveedor_id: nuevoProveedorCreado.id,
        }));

        setNuevoProveedor({
          nombre: "",
          cuit: "",
        });
        setShowNewProveedorModal(false);
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
    setGuardandoProveedor(false);
  };

  const crearNuevaEtapa = async (nombre) => {
    try {
      const { data, error } = await supabase
        .from("etapas_obra")
        .insert([
          {
            constructora_id: constructora.id,
            nombre: nombre.trim(),
            orden: etapas.length + 1,
            activo: true,
          },
        ])
        .select();

      if (error) {
        console.error("Error creando etapa:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al crear etapa: " + error.message
        );
      } else {
        const nuevaEtapa = data[0];
        onEtapaCreada(nuevaEtapa);
        setFormData((prev) => ({
          ...prev,
          etapa_id: nuevaEtapa.id,
        }));
        showCustomAlert("success", "Éxito", "Etapa creada correctamente");
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
  };

  const crearNuevaSubetapa = async (nombre) => {
    try {
      if (!formData.etapa_id) {
        showCustomAlert(
          "error",
          "Error",
          "Primero debes seleccionar una etapa"
        );
        return;
      }

      const { data, error } = await supabase
        .from("subetapas_obra")
        .insert([
          {
            constructora_id: constructora.id,
            etapa_id: formData.etapa_id,
            nombre: nombre.trim(),
            orden: subetapasFiltradas.length + 1,
            activo: true,
          },
        ])
        .select();

      if (error) {
        console.error("Error creando subetapa:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al crear subetapa: " + error.message
        );
      } else {
        const nuevaSubetapa = data[0];
        onSubetapaCreada(nuevaSubetapa);
        setFormData((prev) => ({
          ...prev,
          subetapa_id: nuevaSubetapa.id,
        }));
        showCustomAlert("success", "Éxito", "Subetapa creada correctamente");
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
  };

  const agregarNuevaSubetapa = async () => {
    if (!nuevaSubetapa.nombre.trim()) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor ingresa un nombre para la subetapa"
      );
      return;
    }

    if (!nuevaSubetapa.etapa_id) {
      showCustomAlert("error", "Error", "Por favor selecciona una etapa");
      return;
    }

    setGuardandoSubetapa(true);
    try {
      const { data, error } = await supabase
        .from("subetapas_obra")
        .insert([
          {
            constructora_id: constructora.id,
            etapa_id: nuevaSubetapa.etapa_id,
            nombre: nuevaSubetapa.nombre.trim(),
            orden: subetapasFiltradas.length + 1,
            activo: true,
          },
        ])
        .select();

      if (error) {
        console.error("Error guardando subetapa:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al guardar subetapa: " + error.message
        );
      } else {
        showCustomAlert("success", "Éxito", "Subetapa agregada correctamente");

        const nuevaSubetapaCreada = data[0];
        onSubetapaCreada(nuevaSubetapaCreada);

        setFormData((prev) => ({
          ...prev,
          subetapa_id: nuevaSubetapaCreada.id,
        }));

        setNuevaSubetapa({
          nombre: "",
          etapa_id: "",
        });
        setShowNewSubetapaModal(false);
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
    setGuardandoSubetapa(false);
  };

  const handleFileUpload = async (file, tipo) => {
    if (!file) return;

    if (!file.type.startsWith("image/") && !file.type.includes("pdf")) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor selecciona un archivo de imagen o PDF"
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showCustomAlert(
        "error",
        "Error",
        "El archivo es muy grande. Máximo 10MB"
      );
      return;
    }

    if (tipo === "comprobante") {
      setSubiendoComprobante(true);
    } else if (tipo === "factura") {
      setSubiendoFactura(true);
    } else if (tipo === "comprobante_comision") {
      setSubiendoComprobanteComision(true);
    } else if (tipo === "factura_comision") {
      setSubiendoFacturaComision(true);
    }

    try {
      const fileExt = file.name.split(".").pop().toLowerCase();
      const timestamp = Date.now();
      const fileName = `${tipo}-salida-${constructora.id}-${timestamp}.${fileExt}`;
      const bucketName = tipo === "comprobante" ? "comprobantes" : "facturas";

      console.log(`Subiendo ${tipo}:`, fileName);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type,
          cacheControl: "3600",
        });

      if (uploadError) {
        console.error(`Error en upload de ${tipo}:`, uploadError);
        throw uploadError;
      }

      console.log(`Upload ${tipo} exitoso:`, uploadData);

      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(fileName);

      const finalUrl = urlData.publicUrl;
      console.log(`URL ${tipo} generada:`, finalUrl);

      if (tipo === "comprobante") {
        setFormData((prev) => ({
          ...prev,
          comprobante_url: finalUrl,
          tiene_comprobante: true,
        }));
      } else if (tipo === "factura") {
        setFormData((prev) => ({
          ...prev,
          factura_url: finalUrl,
          tiene_factura: true,
        }));
      } else if (tipo === "comprobante_comision") {
        setFormData((prev) => ({
          ...prev,
          comprobante_comision_url: finalUrl,
          tiene_comprobante_comision: true,
        }));
      } else if (tipo === "factura_comision") {
        setFormData((prev) => ({
          ...prev,
          factura_comision_url: finalUrl,
          tiene_factura_comision: true,
        }));
      }
    } catch (err) {
      console.error(`Error subiendo ${tipo}:`, err);
      showCustomAlert(
        "error",
        "Error",
        `Error al subir ${tipo}: ` + err.message
      );
    }

    if (tipo === "comprobante") {
      setSubiendoComprobante(false);
    } else if (tipo === "factura") {
      setSubiendoFactura(false);
    } else if (tipo === "comprobante_comision") {
      setSubiendoComprobanteComision(false);
    } else if (tipo === "factura_comision") {
      setSubiendoFacturaComision(false);
    }
  };

  const handleSave = async () => {
    if (
      !formData.proyecto_id ||
      !formData.proveedor_id ||
      !formData.concepto_id ||
      !formData.monto ||
      !formData.fecha
    ) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor completa todos los campos obligatorios"
      );
      return;
    }

    if (parseFloat(formData.monto) <= 0) {
      showCustomAlert("error", "Error", "El monto debe ser mayor a 0");
      return;
    }

    if (!formData.tipo_cambio || parseFloat(formData.tipo_cambio) <= 0) {
      showCustomAlert("error", "Error", "El tipo de cambio debe ser mayor a 0");
      return;
    }

    if (
      formData.tiene_factura &&
      (!formData.facturador || !formData.factura_a_nombre_de)
    ) {
      showCustomAlert(
        "error",
        "Error",
        "Si tiene factura, debe completar el facturador y a nombre de quien se factura"
      );
      return;
    }

    if (saldoInsuficiente) {
      showCustomAlert(
        "confirm",
        "Saldo insuficiente",
        "El saldo de la caja es insuficiente para esta operación. ¿Deseas continuar de todas formas?",
        () => proceedWithSave()
      );
      return;
    }

    await proceedWithSave();
  };
  const proceedWithSave = async () => {
    setGuardando(true);

    try {
      const dataToSave = {
        constructora_id: constructora.id,
        proyecto_id: formData.proyecto_id,
        concepto_id: formData.concepto_id,
        proveedor_id: formData.proveedor_id,
        etapa_id: formData.etapa_id || null,
        subetapa_id: formData.subetapa_id || null,
        monto: parseFloat(formData.monto),
        moneda: formData.moneda,
        fecha: formData.fecha,
        descripcion: formData.descripcion.trim() || null,
        metodo_pago: formData.metodo_pago,
        tipo_cambio: parseFloat(formData.tipo_cambio),
        caja_origen_id:
          formData.caja_origen_id === "" || !formData.caja_origen_id
            ? null
            : formData.caja_origen_id,
        comprobante_url: formData.comprobante_url || null,
        factura_url: formData.factura_url || null,
        tiene_comprobante: formData.tiene_comprobante,
        tiene_factura: formData.tiene_factura,
        tipo_factura: formData.tiene_factura ? (formData.tipo_factura || "A") : null,
        facturador: formData.facturador.trim() || null,
        factura_a_nombre_de: formData.factura_a_nombre_de.trim() || null,
        tiene_comision: formData.tiene_comision,
        es_adicional: formData.es_adicional,
        monto_comision: formData.tiene_comision ? (() => {
          try {
            const montoComision = typeof formData.monto_comision === 'string' 
              ? formData.monto_comision.replace(/\./g, "") 
              : formData.monto_comision?.toString()?.replace(/\./g, "") || "0";
            return parseFloat(montoComision) || 0;
          } catch (error) {
            return 0;
          }
        })() : null,
        comision_cobrada: formData.comision_cobrada,
        tiene_comprobante_comision: formData.tiene_comprobante_comision,
        tiene_factura_comision: formData.tiene_factura_comision,
        comprobante_comision_url: formData.comprobante_comision_url || null,
        factura_comision_url: formData.factura_comision_url || null,
        facturador_comision: formData.facturador_comision.trim() || null,
        factura_a_nombre_de_comision: formData.factura_a_nombre_de_comision.trim() || null,
        comision_retencion_en_origen: !!formData.comision_retencion_en_origen,
      };

      // Agregar campos de auditoría según si es creación o actualización
      if (salida) {
        // Actualización: solo updated_by
        dataToSave.updated_by = usuario.id;
        console.log("📝 Actualizando salida - updated_by:", usuario.id);
      } else {
        // Creación: created_by y updated_by
        dataToSave.created_by = usuario.id;
        dataToSave.updated_by = usuario.id;
        console.log("🆕 Creando salida - created_by:", usuario.id, "updated_by:", usuario.id);
      }

      console.log("🔍 Debug CUITs a guardar en salida:", {
        facturador_comision: formData.facturador_comision,
        factura_a_nombre_de_comision: formData.factura_a_nombre_de_comision,
        tiene_factura_comision: formData.tiene_factura_comision
      });



      let result;
      if (salida) {
        result = await supabase
          .from("salidas")
          .update(dataToSave)
          .eq("id", salida.id)
          .select();
      } else {
        result = await supabase.from("salidas").insert([dataToSave]).select();
      }

      // ✅ Disparar actualización silenciosa PIA → totales para el proyecto afectado (resolver proyecto_id de múltiples fuentes)
      try {
        const { data, error } = result || {};
        const proyectoIdBg = (data && data[0]?.proyecto_id) 
          || (salida && salida.proyecto_id)
          || dataToSave.proyecto_id 
          || formData.proyecto_id;
        if (proyectoIdBg) dispararActualizacionEnSegundoPlano(proyectoIdBg);
      } catch (_) {}

      const { data, error } = result;

      if (error) {
        console.error("Error guardando salida:", error);
        showCustomAlert("error", "Error", "Error al guardar: " + error.message);
      } else {
        await actualizarBalanceCajas();
        await actualizarProveedorCuit();

        // Manejar comisión si existe (solo para constructores)
        if (!permisosCliente) {
          // Importante: NO usar parseFloat porque "400.000" -> 400.
          // Usamos el parser que respeta formato local para obtener 400000.
          const montoComisionNumber = parseMontoNumber(formData.monto_comision);
          await manejarComision(
            data[0].id,
            formData.tiene_comision,
            montoComisionNumber,
            formData.comision_cobrada
          );
        }

        onSave();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }

    setGuardando(false);
  };

  const actualizarBalanceCajas = async () => {
    try {
      const montoOperacion = parseFloat(formData.monto);

      if (salida) {
        const montoOriginal = parseFloat(salida.monto);
        const cajaOriginalId = salida.caja_origen_id;
        const monedaOriginal = salida.moneda;

        console.log("🔄 EDITANDO SALIDA - Verificando cambios...");
        console.log("Monto:", montoOriginal, "→", montoOperacion);
        console.log("Caja:", cajaOriginalId, "→", formData.caja_origen_id);
        console.log("Moneda:", monedaOriginal, "→", formData.moneda);

        if (cajaOriginalId) {
          const { data: cajaOriginalData, error: errorOriginal } =
            await supabase
              .from("cajas")
              .select("balance_actual")
              .eq("id", cajaOriginalId)
              .single();

          if (!errorOriginal && cajaOriginalData) {
            const balanceOriginal =
              parseFloat(cajaOriginalData.balance_actual) || 0;
            const balanceRevertido = balanceOriginal + montoOriginal;

            await supabase
              .from("cajas")
              .update({
                balance_actual: balanceRevertido,
                updated_at: new Date().toISOString(),
              })
              .eq("id", cajaOriginalId);

            console.log(
              `🔄 REVERTIDO: Caja original ${balanceOriginal} + ${montoOriginal} = ${balanceRevertido} (${monedaOriginal})`
            );
          }
        } else {
          console.log("🔄 Valor original era cliente directo - No revertir");
        }

        if (formData.caja_origen_id) {
          const { data: cajaOrigen, error: errorOrigen } = await supabase
            .from("cajas")
            .select("balance_actual")
            .eq("id", formData.caja_origen_id)
            .single();

          if (!errorOrigen && cajaOrigen) {
            const balanceActualOrigen =
              parseFloat(cajaOrigen.balance_actual) || 0;
            const nuevoBalanceOrigen = balanceActualOrigen - montoOperacion;

            await supabase
              .from("cajas")
              .update({
                balance_actual: nuevoBalanceOrigen,
                updated_at: new Date().toISOString(),
              })
              .eq("id", formData.caja_origen_id);

            console.log(
              `✅ APLICADO: Caja nueva ${balanceActualOrigen} - ${montoOperacion} = ${nuevoBalanceOrigen} (${formData.moneda})`
            );
          }
        } else {
          console.log(
            "✅ NUEVO VALOR: Cliente pagó directo - No aplicar descuento"
          );
        }
      } else {
        console.log("🆕 NUEVA SALIDA...");
        if (formData.caja_origen_id) {
          const { data: cajaOrigen, error: errorOrigen } = await supabase
            .from("cajas")
            .select("balance_actual")
            .eq("id", formData.caja_origen_id)
            .single();

          if (!errorOrigen && cajaOrigen) {
            const balanceActualOrigen =
              parseFloat(cajaOrigen.balance_actual) || 0;
            const nuevoBalanceOrigen = balanceActualOrigen - montoOperacion;

            await supabase
              .from("cajas")
              .update({
                balance_actual: nuevoBalanceOrigen,
                updated_at: new Date().toISOString(),
              })
              .eq("id", formData.caja_origen_id);

            console.log(
              `✅ NUEVA SALIDA: Caja ${balanceActualOrigen} - ${montoOperacion} = ${nuevoBalanceOrigen} (${formData.moneda})`
            );
          }
        } else {
          console.log("✅ NUEVA SALIDA: Cliente pagó directo - No descontar");
        }
      }
    } catch (err) {
      console.error("❌ Error en actualización de balances:", err);
    }
  };

  const actualizarProveedorCuit = async () => {
    if (!formData.facturador || !formData.proveedor_id) return;

    try {
      const proveedor = proveedores.find((p) => p.id === formData.proveedor_id);
      if (proveedor && !proveedor.cuit && formData.facturador.trim()) {
        await supabase
          .from("proveedores_servicios")
          .update({
            cuit: formData.facturador.trim(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", formData.proveedor_id);

        console.log("CUIT actualizado en proveedor:", formData.facturador);
      }
    } catch (err) {
      console.error("Error actualizando CUIT proveedor:", err);
    }
  };

  // Función para manejar comisiones
  const manejarComision = async (salidaId, tieneComision, montoComision, comisionCobrada) => {
    try {
      if (tieneComision && montoComision > 0) {
        // Crear o actualizar comisión
        const { data: comisionExistente } = await supabase
          .from('comisiones')
          .select('*')
          .eq('salida_id', salidaId)
          .single();

        if (comisionExistente) {
          // Verificar cambios de estado
          const cambioACobrada = comisionExistente.estado === 'pendiente' && comisionCobrada;
          const cambioAPendiente = comisionExistente.estado === 'cobrada' && !comisionCobrada;
          
          // Actualizar comisión existente
          console.log("🔍 DEBUG actualizando comisión:", {
            montoComisionOriginal: montoComision,
            tipoMontoComision: typeof montoComision,
            formDataMontoComision: formData.monto_comision,
            tipoFormDataMontoComision: typeof formData.monto_comision
          });
          
          await supabase
            .from('comisiones')
            .update({
              monto_comision: montoComision,
              moneda: formData.moneda_comision || formData.moneda,
              tipo_cambio: formData.tipo_cambio_comision || (formData.moneda === 'USD' ? formData.tipo_cambio : 1),
              estado: comisionCobrada ? 'cobrada' : 'pendiente',
              fecha_cobro: comisionCobrada ? new Date().toISOString().split("T")[0] : null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', comisionExistente.id);

          // Si cambió a cobrada, intentar restaurar entrada o generar nueva
          if (cambioACobrada) {
            const entradaRestaurada = await restaurarEntradaComision(comisionExistente.id, montoComision);
            if (entradaRestaurada) {
              showCustomAlert("success", "Comisión cobrada", "Se ha restaurado la entrada anterior de la comisión.");
            } else {
              await generarEntradaComision(comisionExistente.id, montoComision);
              showCustomAlert("success", "Comisión cobrada", "Se ha generado automáticamente una entrada por la comisión cobrada.");
            }
          }
          
          // Si cambió a pendiente, eliminar entrada relacionada
          if (cambioAPendiente && comisionExistente.entrada_relacionada_id) {
            await eliminarEntradaComision(comisionExistente.entrada_relacionada_id);
            showCustomAlert("info", "Comisión pendiente", "Se ha eliminado la entrada relacionada a la comisión.");
          } else if (!cambioACobrada && !cambioAPendiente) {
            // Solo actualización de monto u otros campos - actualizar entrada relacionada si existe
            if (comisionExistente.entrada_relacionada_id) {
              const entradaActualizada = await actualizarEntradaComision(
                comisionExistente.id,
                montoComision,
                formData.moneda_comision || formData.moneda,
                formData.tipo_cambio_comision || (formData.moneda === 'USD' ? formData.tipo_cambio : 1)
              );
              
              if (entradaActualizada) {
                showCustomAlert("success", "Comisión actualizada", "Los datos de la comisión y la entrada relacionada han sido actualizados correctamente.");
              } else {
                showCustomAlert("success", "Comisión actualizada", "Los datos de la comisión han sido actualizados correctamente.");
              }
            } else {
              showCustomAlert("success", "Comisión actualizada", "Los datos de la comisión han sido actualizados correctamente.");
            }
          }
        } else {
          // Crear nueva comisión
          const { data: nuevaComision } = await supabase
            .from('comisiones')
            .insert({
              constructora_id: constructora.id,
              salida_id: salidaId,
              proveedor_id: formData.proveedor_id,
              monto_comision: montoComision,
              moneda: formData.moneda_comision || formData.moneda,
              tipo_cambio: formData.tipo_cambio_comision || (formData.moneda === 'USD' ? formData.tipo_cambio : 1),
              estado: comisionCobrada ? 'cobrada' : 'pendiente',
              fecha_comision: formData.fecha,
              fecha_cobro: comisionCobrada ? new Date().toISOString().split("T")[0] : null,
              created_by: constructora?.usuario_id,
            })
            .select()
            .single();

          // Si se crea como cobrada, generar entrada automática
          if (comisionCobrada && nuevaComision) {
            await generarEntradaComision(nuevaComision.id, montoComision);
            showCustomAlert("success", "Comisión cobrada", "Se ha generado automáticamente una entrada por la comisión cobrada.");
          } else if (!comisionCobrada) {
            showCustomAlert("info", "Comisión pendiente", "Comisión creada como pendiente. Cuando la marques como cobrada, se generará automáticamente una entrada.");
          }
        }
      } else {
        // Eliminar comisión si existe
        const { data: comisionExistente } = await supabase
          .from('comisiones')
          .select('*')
          .eq('salida_id', salidaId)
          .single();

        if (comisionExistente) {
          // Si hay entrada relacionada, eliminarla también
          if (comisionExistente.entrada_relacionada_id) {
            await supabase
              .from('entradas')
              .update({
                activo: false,
                deleted_at: new Date().toISOString(),
                deleted_by: constructora?.usuario_id,
              })
              .eq('id', comisionExistente.entrada_relacionada_id);
          }

          // Eliminar comisión
          await supabase
            .from('comisiones')
            .update({
              activo: false,
              deleted_at: new Date().toISOString(),
              deleted_by: constructora?.usuario_id,
            })
            .eq('id', comisionExistente.id);
            
          showCustomAlert("info", "Comisión eliminada", "La comisión y su entrada relacionada han sido eliminadas.");
        } else {
          showCustomAlert("info", "Comisión eliminada", "La comisión ha sido eliminada.");
        }
      }
    } catch (error) {
      console.error('Error manejando comisión:', error);
    }
  };

  // Función para generar entrada automática de comisión
  async function generarEntradaComision(comisionId, montoComision) {
    try {
      // Obtener datos de la comisión y la salida relacionada
      const { data: comisionData } = await supabase
        .from("comisiones")
        .select(`
          *,
          salidas (
            moneda,
            proyecto_id,
            proveedor_id,
            tiene_factura_comision,
            factura_comision_url,
            facturador_comision,
            factura_a_nombre_de_comision,
            tiene_comprobante_comision,
            comprobante_comision_url
          )
        `)
        .eq("id", comisionId)
        .single();

      if (!comisionData) {
        console.error("No se encontró la comisión:", comisionId);
        return;
      }

      const salidaData = comisionData.salidas;
      
      // Buscar caja de empresa de la moneda correspondiente
      const { data: cajaEmpresa } = await supabase
        .from("cajas")
        .select("id")
        .eq("constructora_id", constructora.id)
        .eq("tipo_caja", "empresa")
        .eq("moneda", comisionData.moneda || salidaData.moneda)
        .eq("activo", true)
        .is("deleted_at", null)
        .single();

      if (!cajaEmpresa) {
        console.error("No se encontró caja de empresa para la moneda", comisionData.moneda || salidaData.moneda);
        return;
      }

      // Buscar concepto "Comisiones"
      const { data: conceptoComisiones } = await supabase
        .from("conceptos_entrada")
        .select("id")
        .eq("constructora_id", constructora.id)
        .eq("nombre", "Comisiones")
        .eq("activo", true)
        .single();

      if (!conceptoComisiones) {
        console.error("No se encontró el concepto 'Comisiones'");
        return;
      }

      // Obtener datos del proveedor
      const proveedorSeleccionado = todosLosProveedores.find(
        (p) => p.id === salidaData.proveedor_id
      );

      // Crear entrada automática con información completa de comisión
      console.log("🔍 Generando entrada con datos:", {
        tiene_factura: salidaData.tiene_factura_comision,
        factura_url: salidaData.factura_comision_url,
        facturador: salidaData.facturador_comision,
        factura_a_nombre_de: salidaData.factura_a_nombre_de_comision,
        tiene_comprobante: salidaData.tiene_comprobante_comision,
        comprobante_url: salidaData.comprobante_comision_url,
      });
      
      console.log("🔍 Debug CUITs comisión:", {
        facturador_comision: salidaData.facturador_comision,
        factura_a_nombre_de_comision: salidaData.factura_a_nombre_de_comision,
        tiene_factura_comision: salidaData.tiene_factura_comision
      });
      
      console.log("🔍 Debug salidaData completo:", salidaData);
      
      const { data: entrada, error: errorEntrada } = await supabase
        .from("entradas")
        .insert({
          constructora_id: constructora.id,
          proyecto_id: salidaData.proyecto_id,
          caja_destino_id: cajaEmpresa.id,
          concepto_id: conceptoComisiones.id,
          monto: montoComision,
          moneda: comisionData.moneda || salidaData.moneda,
          tipo_cambio: comisionData.tipo_cambio || 1,
          descripcion: `Comisión de ${proveedorSeleccionado?.nombre || 'Proveedor'}`,
          fecha: new Date().toISOString().split("T")[0],
          comision_relacionada_id: comisionId,
          // Información de factura de comisión (desde la salida)
          tiene_factura: salidaData.tiene_factura_comision,
          factura_url: salidaData.factura_comision_url,
          facturador: salidaData.facturador_comision,
          factura_a_nombre_de: salidaData.factura_a_nombre_de_comision,
          // Información de comprobante de comisión (desde la salida)
          tiene_comprobante: salidaData.tiene_comprobante_comision,
          comprobante_url: salidaData.comprobante_comision_url,
          created_by: constructora?.usuario_id,
        })
        .select()
        .single();

      if (errorEntrada) {
        console.error("Error creando entrada de comisión:", errorEntrada);
        return;
      }

      console.log("✅ Entrada creada exitosamente:", entrada);
      
      // 🔧 ACTUALIZAR BALANCE DE LA CAJA DE EMPRESA
      await actualizarBalanceCajaComision(null, entrada);
      
      console.log("🔍 Verificando CUITs guardados en entrada:", {
        facturador: entrada.facturador,
        factura_a_nombre_de: entrada.factura_a_nombre_de,
        tiene_factura: entrada.tiene_factura
      });

      // Actualizar comisión con la entrada relacionada
      await supabase
        .from("comisiones")
        .update({
          entrada_relacionada_id: entrada.id,
          updated_at: new Date().toISOString(),
        })
        .eq("id", comisionId);

      console.log("✅ Entrada de comisión generada:", entrada.id);
    } catch (error) {
      console.error("Error generando entrada de comisión:", error);
    }
  }

  // Función para eliminar entrada de comisión
  async function eliminarEntradaComision(entradaId) {
    try {
      // Obtener datos de la entrada antes de eliminar
      const { data: entradaEliminar } = await supabase
        .from("entradas")
        .select("id, monto, moneda, caja_destino_id")
        .eq("id", entradaId)
        .single();

      // Eliminar entrada
      await supabase
        .from("entradas")
        .update({
          activo: false,
          deleted_at: new Date().toISOString(),
          deleted_by: constructora?.usuario_id,
        })
        .eq("id", entradaId);

      // Cambiar estado de comisión a pendiente y limpiar referencia
      await supabase
        .from("comisiones")
        .update({
          estado: 'pendiente',
          fecha_cobro: null,
          entrada_relacionada_id: null,
          updated_at: new Date().toISOString(),
        })
        .eq("entrada_relacionada_id", entradaId);

      // 🔧 ACTUALIZAR BALANCE: Revertir el monto en la caja
      if (entradaEliminar) {
        await actualizarBalanceCajaComision(entradaEliminar, null);
      }

      console.log("✅ Entrada de comisión eliminada:", entradaId);
    } catch (error) {
      console.error("Error eliminando entrada de comisión:", error);
    }
  }

  // Función para restaurar entrada de comisión eliminada
  async function restaurarEntradaComision(comisionId, montoComision) {
    try {
      // Buscar entrada eliminada relacionada con esta comisión con datos completos
      const { data: entradaEliminada } = await supabase
        .from("entradas")
        .select("id, monto, moneda, caja_destino_id")
        .eq("comision_relacionada_id", comisionId)
        .eq("activo", false)
        .not("deleted_at", "is", null)
        .order("deleted_at", { ascending: false })
        .limit(1)
        .single();

      if (entradaEliminada) {
        // Restaurar entrada eliminada
        await supabase
          .from("entradas")
          .update({
            activo: true,
            deleted_at: null,
            deleted_by: null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", entradaEliminada.id);

        // Actualizar comisión con la entrada restaurada
        await supabase
          .from("comisiones")
          .update({
            entrada_relacionada_id: entradaEliminada.id,
            updated_at: new Date().toISOString(),
          })
          .eq("id", comisionId);

        // 🔧 ACTUALIZAR BALANCE: Restaurar el monto en la caja
        await actualizarBalanceCajaComision(null, entradaEliminada);

        console.log("✅ Entrada de comisión restaurada:", entradaEliminada.id);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error restaurando entrada de comisión:", error);
      return false;
    }
  }

  // Función para actualizar entrada relacionada cuando se modifica la comisión
  async function actualizarEntradaComision(comisionId, montoComision, monedaComision, tipoCambioComision) {
    try {
      // Buscar la entrada relacionada con datos completos
      const { data: entradaOriginal } = await supabase
        .from("entradas")
        .select("id, activo, monto, moneda, caja_destino_id")
        .eq("comision_relacionada_id", comisionId)
        .eq("activo", true)
        .single();

      if (entradaOriginal) {
        // Actualizar la entrada con los nuevos valores
        console.log("🔍 DEBUG sincronización comisión → entrada:", {
          montoComisionOriginal: montoComision,
          tipoMontoComision: typeof montoComision,
          monedaComision: monedaComision,
          tipoCambioComision: tipoCambioComision
        });
        
        await supabase
          .from("entradas")
          .update({
            monto: montoComision,
            moneda: monedaComision,
            tipo_cambio: tipoCambioComision,
            updated_at: new Date().toISOString(),
          })
          .eq("id", entradaOriginal.id);

        // 🔧 ACTUALIZAR BALANCE: Revertir monto original y aplicar nuevo monto
        const entradaNueva = {
          ...entradaOriginal,
          monto: montoComision,
          moneda: monedaComision
        };
        
        await actualizarBalanceCajaComision(entradaOriginal, entradaNueva);

        console.log("✅ Entrada de comisión actualizada:", entradaOriginal.id);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error actualizando entrada de comisión:", error);
      return false;
    }
  }

  // Función para detectar CUIT diferente al del proveedor
  const detectarCuitDiferente = (cuitIngresado, proveedorId, campo) => {
    if (!cuitIngresado || !proveedorId) return;

    const proveedor = todosLosProveedores.find(p => p.id === proveedorId);
    if (!proveedor || !proveedor.cuit) return;

    const cuitIngresadoLimpio = cuitIngresado.replace(/\D/g, "");
    const cuitProveedorLimpio = proveedor.cuit.replace(/\D/g, "");

    if (cuitIngresadoLimpio !== cuitProveedorLimpio) {
      setCuitDiferenteData({
        proveedor,
        cuitIngresado,
        cuitProveedor: proveedor.cuit,
        campo
      });
      setShowCuitDiferenteModal(true);
    }
  };

  const actualizarCuitProveedor = async () => {
    if (!cuitProveedor.trim() || !proveedorParaCuit) return;

    setGuardandoCuit(true);
    try {
      const { error } = await supabase
        .from("proveedores_servicios")
        .update({ cuit: cuitProveedor.trim() })
        .eq("id", proveedorParaCuit.id);

      if (error) {
        console.error("Error actualizando CUIT:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al actualizar CUIT: " + error.message
        );
      } else {
        showCustomAlert("success", "Éxito", "CUIT actualizado correctamente");

        // Actualizar la lista de proveedores
        const proveedoresActualizados = proveedores.map((p) =>
          p.id === proveedorParaCuit.id
            ? { ...p, cuit: cuitProveedor.trim() }
            : p
        );
        onProveedorCreado({ ...proveedorParaCuit, cuit: cuitProveedor.trim() });

        // Si esta salida tiene factura y el proveedor editado es el seleccionado,
        // autocompletar inmediatamente el CUIT del facturador
        if (
          formData.tiene_factura &&
          formData.proveedor_id === proveedorParaCuit.id &&
          !formData.facturador
        ) {
          setFormData((prev) => ({
            ...prev,
            facturador: formatearCUIT(cuitProveedor.trim()),
          }));
        }

        // Cerrar modal y limpiar
        setShowCuitModal(false);
        setProveedorParaCuit(null);
        setCuitProveedor("");
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
    setGuardandoCuit(false);
  };

  // Función para actualizar CUIT del proveedor desde modal de CUIT diferente
  const actualizarCuitProveedorDesdeModal = async () => {
    if (!cuitDiferenteData) return;

    setGuardandoCuit(true);
    try {
      const { error } = await supabase
        .from("proveedores_servicios")
        .update({ cuit: cuitDiferenteData.cuitIngresado })
        .eq("id", cuitDiferenteData.proveedor.id);

      if (error) {
        console.error("Error actualizando CUIT:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al actualizar CUIT: " + error.message
        );
      } else {
        showCustomAlert("success", "Éxito", "CUIT del proveedor actualizado correctamente");

        // Autocompletar CUIT del facturador si corresponde
        if (
          formData.tiene_factura &&
          formData.proveedor_id === cuitDiferenteData.proveedor.id &&
          !formData.facturador
        ) {
          setFormData((prev) => ({
            ...prev,
            facturador: formatearCUIT(cuitDiferenteData.cuitIngresado),
          }));
        }

        // Actualizar la lista de proveedores
        const proveedoresActualizados = todosLosProveedores.map((p) =>
          p.id === cuitDiferenteData.proveedor.id
            ? { ...p, cuit: cuitDiferenteData.cuitIngresado }
            : p
        );
        
        // Cerrar modal y limpiar
        setShowCuitDiferenteModal(false);
        setCuitDiferenteData(null);
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
    setGuardandoCuit(false);
  };
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 12000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
        animation: "fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: config.gradient,
            color: "white",
            padding: isMobile ? "16px" : "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <TrendingDown size={24} />
            <div>
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: isMobile ? "18px" : "20px",
                  fontWeight: "700",
                }}
              >
                {salida ? "Editar salida" : "Nueva salida"}
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                }}
              >
                {salida
                  ? "Modificar registro de salida"
                  : "Registrar un nuevo egreso"}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="button-hover"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "12px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "16px" : "20px",
            overflowY: "auto",
          }}
        >
        <div style={{ display: "grid", gap: isMobile ? "14px" : "16px" }}>
          {/* Proyecto */}
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Building2 size={12} />
              Proyecto *
            </label>
            <CustomDropdown
              value={formData.proyecto_id}
              onChange={(value) =>
                setFormData({ ...formData, proyecto_id: value })
              }
              options={proyectos.map((proyecto) => ({
                value: proyecto.id,
                label: `${proyecto.codigo} - ${proyecto.apellido_cliente}`,
              }))}
              placeholder="Selecciona un proyecto"
              disabled={!!permisosCliente?.proyectoId}
              icon={Building2}
            />
            {permisosCliente?.proyectoId && (
              <p
                style={{
                  fontSize: "10px",
                  color: "#0ea5e9",
                  margin: "4px 0 0 0",
                  fontStyle: "italic",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <MapPin size={8} />
                {permisosCliente.proyectoCodigo}
              </p>
            )}
          </div>

          {/* Proveedor */}
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Users size={12} />
              Proveedor *
            </label>
            <CustomDropdown
              value={formData.proveedor_id}
              onChange={(value) =>
                setFormData({ ...formData, proveedor_id: value })
              }
              options={todosLosProveedores
                .filter(
                  (p) => p.id === formData.proveedor_id || p.activo === true
                )
                .sort((a, b) => {
                  const aEsFrecuente = proveedoresFrecuentes.includes(a.id);
                  const bEsFrecuente = proveedoresFrecuentes.includes(b.id);
                  
                  // Primero los frecuentes, luego alfabético
                  if (aEsFrecuente && !bEsFrecuente) return -1;
                  if (!aEsFrecuente && bEsFrecuente) return 1;
                  
                  // Dentro de cada grupo, orden alfabético
                  return a.nombre.localeCompare(b.nombre);
                })
                .map((proveedor) => ({
                  value: proveedor.id,
                  label: proveedor.nombre,
                  isFrecuente: proveedoresFrecuentes.includes(proveedor.id)
                }))}
              placeholder="Selecciona proveedor"
              icon={Users}
              canCreate={true}
              onCreateNew={crearNuevoProveedor}
              createText="Crear proveedor"
            />
          </div>

          {/* Concepto */}
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Target size={12} />
              Concepto *
            </label>
            <CustomDropdown
              value={formData.concepto_id}
              onChange={(value) =>
                setFormData({ ...formData, concepto_id: value })
              }
              options={todasLosConceptos
                .filter(
                  (c) => c.id === formData.concepto_id || c.activo === true
                )
                .map((concepto) => ({
                  value: concepto.id,
                  label: concepto.nombre,
                }))}
              placeholder="Selecciona concepto"
              icon={Target}
              canCreate={true}
              onCreateNew={crearNuevoConcepto}
              createText="Crear concepto"
            />
          </div>

          {/* Etapa y Subetapa - Nueva sección */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "12px",
            }}
          >
            {/* Etapa */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Layers size={12} />
                Etapa
              </label>
              <CustomDropdown
                value={formData.etapa_id}
                onChange={(value) =>
                  setFormData({ ...formData, etapa_id: value })
                }
                options={etapas
                  .filter(
                    (e) => e.activo === true || e.id === formData.etapa_id
                  )
                  .map((etapa) => ({
                    value: etapa.id,
                    label: etapa.nombre,
                  }))}
                placeholder="Selecciona etapa"
                icon={Layers}
                canCreate={true}
                onCreateNew={crearNuevaEtapa}
                createText="Crear etapa"
              />
            </div>

            {/* Subetapa */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Package size={12} />
                Subetapa
              </label>
              <CustomDropdown
                value={formData.subetapa_id}
                onChange={(value) =>
                  setFormData({ ...formData, subetapa_id: value })
                }
                options={subetapasFiltradas
                  .filter(
                    (s) => s.activo === true || s.id === formData.subetapa_id
                  )
                  .map((subetapa) => {
                    const presupuestosInfo = verificarPresupuestoExiste(
                      formData.proyecto_id,
                      formData.concepto_id,
                      formData.etapa_id,
                      subetapa.id,
                      formData.proveedor_id
                    );

                    return {
                      value: subetapa.id,
                      label: subetapa.nombre,
                      tieneInicial: presupuestosInfo.tieneInicial,
                      tienePresupuesto: presupuestosInfo.tienePresupuesto,
                    };
                  })}
                placeholder="Selecciona subetapa"
                disabled={!formData.etapa_id}
                icon={Package}
                canCreate={formData.etapa_id}
                onCreateNew={crearNuevaSubetapa}
                createText="Crear subetapa"
              />
              {!formData.etapa_id && (
                <p
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    margin: "4px 0 0 0",
                    fontStyle: "italic",
                  }}
                >
                  Primero selecciona una etapa
                </p>
              )}
              {/* Indicador de presupuestos profesional */}
              {formData.proyecto_id &&
                formData.concepto_id &&
                formData.proveedor_id &&
                (() => {
                  const presupuestosInfo = verificarPresupuestoExiste(
                    formData.proyecto_id,
                    formData.concepto_id,
                    formData.etapa_id,
                    formData.subetapa_id,
                    formData.proveedor_id
                  );

                  if (
                    !presupuestosInfo.tieneInicial &&
                    !presupuestosInfo.tienePresupuesto
                  ) {
                    return null;
                  }

                  return (
                    <div
                      style={{
                        marginTop: "8px",
                        padding: "10px 12px",
                        background:
                          "linear-gradient(135deg, #f3e8ff 0%, #fdf2f8 100%)",
                        border: "1px solid #d8b4fe",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div style={{ display: "flex", gap: "6px" }}>
                        {presupuestosInfo.tieneInicial && (
                          <div
                            style={{
                              width: "24px",
                              height: "24px",
                              background:
                                "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                              borderRadius: "6px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Target size={12} color="white" />
                          </div>
                        )}
                        {presupuestosInfo.tienePresupuesto && (
                          <div
                            style={{
                              width: "24px",
                              height: "24px",
                              background:
                                "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
                              borderRadius: "6px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <ClipboardList size={12} color="white" />
                          </div>
                        )}
                      </div>
                      <p
                        style={{
                          fontSize: "11px",
                          color: "#6b21a8",
                          margin: 0,
                          fontWeight: "600",
                          lineHeight: "1.3",
                        }}
                      >
                        {presupuestosInfo.tieneInicial &&
                        presupuestosInfo.tienePresupuesto
                          ? "Vinculado a presupuesto inicial y presupuesto"
                          : presupuestosInfo.tieneInicial
                          ? "Vinculado a presupuesto inicial"
                          : "Vinculado a presupuesto"}
                      </p>
                    </div>
                  );
                })()}
            </div>
          </div>

          {/* Monto y Moneda */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "2fr 1fr" : "2fr 1fr",
              gap: "12px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <DollarSign size={12} />
                Monto *
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formatearMonto(formData.monto)}
                onChange={(e) => {
                  const valorLimpio = limpiarFormatoMonto(e.target.value);
                  setFormData({ ...formData, monto: valorLimpio });
                }}
                placeholder="0,00"
                style={{
                  width: "100%",
                  padding: isMobile ? "14px" : "12px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Moneda *
              </label>
              <CustomDropdown
                value={formData.moneda}
                onChange={(value) =>
                  setFormData({ ...formData, moneda: value })
                }
                options={[
                  { value: "ARS", label: "ARS" },
                  { value: "USD", label: "USD" },
                ]}
                placeholder="Moneda"
                icon={DollarSign}
              />
            </div>
          </div>

          {/* Equivalente */}
          {formData.monto && calcularEquivalente() && (
            <div
              style={{
                background: "#f0fdf4",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid #bbf7d0",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#059669",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                }}
              >
                <DollarSign size={12} />
                {calcularEquivalente()}
              </p>
            </div>
          )}

          {/* Fecha y Tipo de Cambio */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Calendar size={12} />
                Fecha *
              </label>
              <input
                type="date"
                value={formData.fecha}
                onChange={(e) =>
                  setFormData({ ...formData, fecha: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: isMobile ? "14px" : "12px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <DollarSign size={12} />
                T.C. *{" "}
                {loadingDolar && (
                  <Loader2 size={10} className="loading-pulse" />
                )}
              </label>
              <input
                type="number"
                inputMode="numeric"
                step="0.01"
                value={formData.tipo_cambio}
                onChange={(e) =>
                  setFormData({ ...formData, tipo_cambio: e.target.value })
                }
                placeholder="1.00"
                style={{
                  width: "100%",
                  padding: isMobile ? "14px" : "12px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "none";
                }}
              />
              {dolarOficial && (
                <p
                  style={{
                    fontSize: "10px",
                    color: "#059669",
                    margin: "4px 0 0 0",
                    fontWeight: "500",
                  }}
                >
                  <DollarSign
                    size={10}
                    style={{
                      verticalAlign: "text-bottom",
                      marginRight: "4px",
                    }}
                  />
                  Dólar oficial: ${dolarOficial.toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {/* Caja de Origen */}
          {formData.proyecto_id && (
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Wallet size={12} />
                Origen del pago *
                {saldoInsuficiente && formData.caja_origen_id && (
                  <AlertCircle size={12} color="#ef4444" />
                )}
              </label>
              <CustomDropdown
                value={formData.caja_origen_id || "cliente"}
                onChange={(value) => {
                  setFormData({
                    ...formData,
                    caja_origen_id: value === "cliente" ? "" : value,
                  });
                }}
                options={[
                  {
                    value: "cliente",
                    label: `Cliente: ${
                      proyectos.find((p) => p.id === formData.proyecto_id)
                        ?.apellido_cliente
                    }`,
                  },
                  ...cajasDisponibles.map((caja) => ({
                    value: caja.id,
                    label: `${generarNombreCaja(caja)} ($${parseFloat(
                      caja.balance_actual || 0
                    ).toLocaleString()})`,
                  })),
                ]}
                placeholder="Selecciona origen"
                icon={Wallet}
              />

              {!formData.caja_origen_id && (
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "6px",
                    padding: "8px",
                    marginTop: "6px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "10px",
                      color: "#059669",
                      margin: 0,
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <CheckCircle2 size={10} />
                    Cliente pagó directo. No afecta cajas.
                  </p>
                </div>
              )}

              {saldoInsuficiente && formData.caja_origen_id && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "6px",
                    padding: "8px",
                    marginTop: "6px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "10px",
                      color: "#dc2626",
                      margin: 0,
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <AlertCircle size={10} />
                    Saldo insuficiente en la caja seleccionada
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Método de Pago */}
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <CreditCard size={12} />
              Método de pago
            </label>
            <CustomDropdown
              value={formData.metodo_pago}
              onChange={(value) =>
                setFormData({ ...formData, metodo_pago: value })
              }
              options={[
                { value: "transferencia", label: "Transferencia" },
                { value: "efectivo", label: "Efectivo" },
                { value: "cheque", label: "Cheque" },
                { value: "deposito", label: "Depósito" },
                { value: "mercadopago", label: "MercadoPago" },
                { value: "debito", label: "Débito" },
                { value: "credito", label: "Crédito" },
                { value: "otro", label: "Otro" },
              ]}
              placeholder="Selecciona método"
              icon={CreditCard}
            />
          </div>

          {/* Descripción */}
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Descripción
            </label>
            <textarea
              value={formData.descripcion}
              onChange={(e) =>
                setFormData({ ...formData, descripcion: e.target.value })
              }
              placeholder="Descripción adicional..."
              rows={isMobile ? "2" : "2"}
              style={{
                width: "100%",
                padding: isMobile ? "12px" : "10px 12px",
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                fontSize: "16px",
                boxSizing: "border-box",
                outline: "none",
                resize: "vertical",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#f1f5f9";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Comprobante - Diseño compacto */}
          <div
            style={{
              background: "#f8fafc",
              padding: isMobile ? "12px" : "14px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
                marginBottom: formData.tiene_comprobante ? "10px" : "0",
              }}
            >
              <span
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <FileText size={12} />
                Comprobante
              </span>
              <button
                type="button"
                aria-pressed={Boolean(formData.tiene_comprobante)}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    tiene_comprobante: !prev.tiene_comprobante,
                    comprobante_url: !prev.tiene_comprobante ? prev.comprobante_url : "",
                  }))
                }
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 9999,
                  border: '1px solid ' + (formData.tiene_comprobante ? '#059669' : '#cbd5e1'),
                  background: formData.tiene_comprobante ? '#059669' : '#e2e8f0',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all .2s ease',
                  outline: 'none',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: formData.tiene_comprobante ? 22 : 2,
                    width: 18,
                    height: 18,
                    borderRadius: '9999px',
                    background: 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    transition: 'left .2s ease',
                  }}
                />
              </button>
            </div>

            {formData.tiene_comprobante && (
              <div
                style={{
                  border: "2px dashed #cbd5e1",
                  borderRadius: "8px",
                  padding: "12px",
                  textAlign: "center",
                  background: "white",
                }}
              >
                {formData.comprobante_url ? (
                  <div>
                    <CheckCircle2
                      size={24}
                      color="#059669"
                      style={{ marginBottom: "6px" }}
                    />
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#059669",
                        margin: "0 0 8px 0",
                        fontWeight: "600",
                      }}
                    >
                      Comprobante subido correctamente
                    </p>
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        onClick={() =>
                          window.open(formData.comprobante_url, "_blank")
                        }
                        style={{
                          background: "none",
                          border: "1px solid #059669",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          color: "#059669",
                          fontSize: "11px",
                          cursor: "pointer",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          touchAction: "manipulation",
                        }}
                      >
                        <Eye size={10} />
                        Ver
                      </button>
                      <button
                        onClick={() =>
                          setFormData({ ...formData, comprobante_url: "" })
                        }
                        style={{
                          background: "none",
                          border: "1px solid #ef4444",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          color: "#ef4444",
                          fontSize: "11px",
                          cursor: "pointer",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          touchAction: "manipulation",
                        }}
                      >
                        <Trash2 size={10} />
                        Quitar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>

                    {/* Inputs separados para cámara y galería */}
                    <input
                      type="file"
                      id="comprobante-camera-salida"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) =>
                        handleFileUpload(e.target.files[0], "comprobante")
                      }
                      style={{ display: "none" }}
                      disabled={subiendoComprobante}
                    />
                    <input
                      type="file"
                      id="comprobante-gallery-salida"
                      accept="image/*,application/pdf"
                      onChange={(e) =>
                        handleFileUpload(e.target.files[0], "comprobante")
                      }
                      style={{ display: "none" }}
                      disabled={subiendoComprobante}
                    />

                    {/* Opciones diferentes para móvil y desktop */}
                    {isMobile ? (
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          justifyContent: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <label
                          htmlFor="comprobante-camera-salida"
                          style={{
                            background: subiendoComprobante
                              ? "#94a3b8"
                              : "#059669",
                            color: "white",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: subiendoComprobante
                              ? "not-allowed"
                              : "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            flex: "1",
                            minWidth: "80px",
                            justifyContent: "center",
                            touchAction: "manipulation",
                          }}
                        >
                          <Camera size={12} />
                          Cámara
                        </label>
                        <label
                          htmlFor="comprobante-gallery-salida"
                          style={{
                            background: subiendoComprobante
                              ? "#94a3b8"
                              : "#3b82f6",
                            color: "white",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: subiendoComprobante
                              ? "not-allowed"
                              : "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            flex: "1",
                            minWidth: "80px",
                            justifyContent: "center",
                            touchAction: "manipulation",
                          }}
                        >
                          <Image size={12} />
                          Galería
                        </label>
                      </div>
                    ) : (
                      <label
                        htmlFor="comprobante-gallery-salida"
                        style={{
                          background: subiendoComprobante
                            ? "#94a3b8"
                            : "#059669",
                          color: "white",
                          padding: "8px 14px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: subiendoComprobante
                            ? "not-allowed"
                            : "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          touchAction: "manipulation",
                        }}
                      >
                        <Upload size={12} />
                        {subiendoComprobante
                          ? "Subiendo..."
                          : "Seleccionar comprobante"}
                      </label>
                    )}

                    {subiendoComprobante && (
                      <p
                        style={{
                          fontSize: "10px",
                          color: "#64748b",
                          margin: "8px 0 0 0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        <Loader2 size={10} className="loading-pulse" />
                        Subiendo comprobante...
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Factura - Diseño compacto */}
          <div
            style={{
              background: "#fefbf3",
              padding: isMobile ? "12px" : "14px",
              borderRadius: "12px",
              border: "1px solid #fbbf24",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
                marginBottom: formData.tiene_factura ? "10px" : "0",
              }}
            >
              <span
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <FileText size={12} />
                Factura
              </span>
              <button
                type="button"
                aria-pressed={Boolean(formData.tiene_factura)}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    tiene_factura: !prev.tiene_factura,
                    factura_url: !prev.tiene_factura ? prev.factura_url : "",
                    facturador: !prev.tiene_factura ? prev.facturador : "",
                    factura_a_nombre_de: !prev.tiene_factura ? prev.factura_a_nombre_de : "",
                    tipo_factura: !prev.tiene_factura ? (prev.tipo_factura || "A") : "A",
                  }))
                }
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 9999,
                  border: '1px solid ' + (formData.tiene_factura ? '#f59e0b' : '#cbd5e1'),
                  background: formData.tiene_factura ? '#f59e0b' : '#e2e8f0',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all .2s ease',
                  outline: 'none',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: formData.tiene_factura ? 22 : 2,
                    width: 18,
                    height: 18,
                    borderRadius: '9999px',
                    background: 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    transition: 'left .2s ease',
                  }}
                />
              </button>
            </div>

            {formData.tiene_factura && (
              <div style={{ display: "grid", gap: "10px" }}>
                {/* Datos de facturación compactos */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "6px",
                  }}
                >
                  <div>
                    <CustomDropdown
                      value={formData.tipo_factura}
                      onChange={(value) => setFormData({ ...formData, tipo_factura: value })}
                      options={[
                        { value: "A", label: "Factura A" },
                        { value: "B", label: "Factura B" },
                        { value: "C", label: "Factura C" }
                      ]}
                      placeholder="Selecciona tipo de factura"
                      icon={FileText}
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={formData.facturador}
                      onChange={(e) => {
                        const nuevoCuit = formatearCUIT(e.target.value);
                        setFormData({
                          ...formData,
                          facturador: nuevoCuit,
                        });
                      }}
                      onBlur={(e) => {
                        detectarCuitDiferente(e.target.value, formData.proveedor_id, 'facturador');
                      }}
                      placeholder="CUIT Facturador (XX-XXXXXXXX-X)"
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #f59e0b",
                        borderRadius: "6px",
                        fontSize: "16px",
                        boxSizing: "border-box",
                        background: "white",
                        outline: "none",
                        touchAction: "manipulation",
                      }}
                    />
                    {obtenerNombrePorCuit(formData.facturador) && (
                      <p
                        style={{
                          fontSize: "9px",
                          color: "#059669",
                          margin: "2px 0 0 0",
                          fontWeight: "500",
                        }}
                      >
                        {obtenerNombrePorCuit(formData.facturador)}
                      </p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      value={formData.factura_a_nombre_de}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          factura_a_nombre_de: formatearCUIT(e.target.value),
                        })
                      }
                      placeholder="CUIT Cliente (XX-XXXXXXXX-X)"
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #f59e0b",
                        borderRadius: "6px",
                        fontSize: "16px",
                        boxSizing: "border-box",
                        background: "white",
                        outline: "none",
                        touchAction: "manipulation",
                      }}
                    />
                    {obtenerNombrePorCuit(formData.factura_a_nombre_de) && (
                      <p
                        style={{
                          fontSize: "9px",
                          color: "#059669",
                          margin: "2px 0 0 0",
                          fontWeight: "500",
                        }}
                      >
                        {obtenerNombrePorCuit(formData.factura_a_nombre_de)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Upload de factura */}
                <div
                  style={{
                    border: "2px dashed #fbbf24",
                    borderRadius: "8px",
                    padding: "12px",
                    textAlign: "center",
                    background: "white",
                  }}
                >
                  {formData.factura_url ? (
                    <div>
                      <CheckCircle2
                        size={24}
                        color="#f59e0b"
                        style={{ marginBottom: "6px" }}
                      />
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#f59e0b",
                          margin: "0 0 8px 0",
                          fontWeight: "600",
                        }}
                      >
                        Factura subida correctamente
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Crear un enlace temporal para abrir en nueva pestaña sin recargar
                            const link = document.createElement('a');
                            link.href = formData.factura_url;
                            link.target = '_blank';
                            link.rel = 'noopener noreferrer';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          style={{
                            background: "none",
                            border: "1px solid #f59e0b",
                            borderRadius: "6px",
                            padding: "6px 10px",
                            color: "#f59e0b",
                            fontSize: "11px",
                            cursor: "pointer",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            touchAction: "manipulation",
                          }}
                        >
                          <Eye size={10} />
                          Ver
                        </button>
                        <button
                          onClick={() =>
                            setFormData({ ...formData, factura_url: "" })
                          }
                          style={{
                            background: "none",
                            border: "1px solid #ef4444",
                            borderRadius: "6px",
                            padding: "6px 10px",
                            color: "#ef4444",
                            fontSize: "11px",
                            cursor: "pointer",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            touchAction: "manipulation",
                          }}
                        >
                          <Trash2 size={10} />
                          Quitar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>

                      {/* Inputs separados para cámara y galería */}
                      <input
                        type="file"
                        id="factura-camera-salida"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) =>
                          handleFileUpload(e.target.files[0], "factura")
                        }
                        style={{ display: "none" }}
                        disabled={subiendoFactura}
                      />
                      <input
                        type="file"
                        id="factura-gallery-salida"
                        accept="image/*,application/pdf"
                        onChange={(e) =>
                          handleFileUpload(e.target.files[0], "factura")
                        }
                        style={{ display: "none" }}
                        disabled={subiendoFactura}
                      />

                      {/* Opciones diferentes para móvil y desktop */}
                      {isMobile ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            justifyContent: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <label
                            htmlFor="factura-camera-salida"
                            style={{
                              background: subiendoFactura
                                ? "#94a3b8"
                                : "#f59e0b",
                              color: "white",
                              padding: "8px 12px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: subiendoFactura
                                ? "not-allowed"
                                : "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              flex: "1",
                              minWidth: "80px",
                              justifyContent: "center",
                              touchAction: "manipulation",
                            }}
                          >
                            <Camera size={12} />
                            Cámara
                          </label>
                          <label
                            htmlFor="factura-gallery-salida"
                            style={{
                              background: subiendoFactura
                                ? "#94a3b8"
                                : "#3b82f6",
                              color: "white",
                              padding: "8px 12px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: subiendoFactura
                                ? "not-allowed"
                                : "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              flex: "1",
                              minWidth: "80px",
                              justifyContent: "center",
                              touchAction: "manipulation",
                            }}
                          >
                            <Image size={12} />
                            Galería
                          </label>
                        </div>
                      ) : (
                        <label
                          htmlFor="factura-gallery-salida"
                          style={{
                            background: subiendoFactura ? "#94a3b8" : "#f59e0b",
                            color: "white",
                            padding: "8px 14px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: subiendoFactura ? "not-allowed" : "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            touchAction: "manipulation",
                          }}
                        >
                          <Upload size={12} />
                          {subiendoFactura
                            ? "Subiendo..."
                            : "Seleccionar factura"}
                        </label>
                      )}

                      {subiendoFactura && (
                        <p
                          style={{
                            fontSize: "10px",
                            color: "#64748b",
                            margin: "8px 0 0 0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                          }}
                        >
                          <Loader2 size={10} className="loading-pulse" />
                          Subiendo factura...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

          {/* Comisiones - Solo visible para constructores */}
          {!permisosCliente && (
            <div
              style={{
                background: "#f0f9ff",
                padding: isMobile ? "12px" : "14px",
                borderRadius: "12px",
                border: "1px solid #0ea5e9",
                marginTop: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: formData.tiene_comision ? "10px" : "0",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <DollarSign size={12} />
                  Comisión del proveedor
                </span>
                <button
                  type="button"
                  aria-pressed={Boolean(formData.tiene_comision)}
                  onClick={async () => {
                    const nuevaComision = !formData.tiene_comision;
                    
                    // Si se está activando la comisión, obtener el TC actual de venta
                    if (nuevaComision) {
                      try {
                        const res = await fetch("https://dolarapi.com/v1/dolares/oficial");
                        const data = await res.json();
                        const venta = data?.venta;
                        
                        if (venta) {
                          setDolarOficialCompra(venta);
                          setFormData((prev) => ({
                            ...prev,
                            tiene_comision: nuevaComision,
                            monto_comision: "",
                            tipo_cambio_comision: venta.toString(),
                          }));
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            tiene_comision: nuevaComision,
                            monto_comision: "",
                          }));
                        }
                      } catch (e) {
                        console.error('Error obteniendo TC venta para comisión:', e);
                        setFormData((prev) => ({
                          ...prev,
                          tiene_comision: nuevaComision,
                          monto_comision: "",
                        }));
                      }
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        tiene_comision: nuevaComision,
                        monto_comision: "",
                      }));
                    }
                  }}
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 9999,
                    border: '1px solid ' + (formData.tiene_comision ? '#22c55e' : '#cbd5e1'),
                    background: formData.tiene_comision ? '#22c55e' : '#e2e8f0',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all .2s ease',
                    outline: 'none',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: formData.tiene_comision ? 22 : 2,
                      width: 18,
                      height: 18,
                      borderRadius: '9999px',
                      background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'left .2s ease',
                    }}
                  />
                </button>
              </div>

              {formData.tiene_comision && (
                <div style={{ display: "grid", gap: "12px" }}>
                  {/* Moneda y TC de la comisión */}
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) minmax(0,1fr)", gap: "12px", alignItems: "start" }}>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ display: "block", marginBottom: 4, fontSize: 11, fontWeight: 500, color: "#64748b" }}>Moneda de la comisión</label>
                      <CustomDropdown
                        value={formData.moneda_comision || formData.moneda}
                        onChange={async (value) => {
                          const monedaComision = value;
                          setFormData((prev) => ({ ...prev, moneda_comision: monedaComision }));
                          
                          if (monedaComision === 'USD' && !formData.tipo_cambio_comision) {
                            try {
                              const res = await fetch("https://dolarapi.com/v1/dolares/oficial");
                              const data = await res.json();
                              const venta = data?.venta;
                              
                              if (venta) {
                                setDolarOficialCompra(venta);
                                setFormData((prev) => ({ ...prev, tipo_cambio_comision: venta.toString() }));
                              }
                            } catch (e) {
                              console.error('Error obteniendo TC venta para comisión:', e);
                            }
                          }
                          if (monedaComision === 'ARS') {
                            setFormData((prev) => ({ ...prev, tipo_cambio_comision: '1' }));
                          }
                        }}
                        options={[{ value: 'ARS', label: 'ARS' }, { value: 'USD', label: 'USD' }]}
                        placeholder="Moneda comisión"
                        icon={DollarSign}
                      />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ display: "block", marginBottom: 4, fontSize: 11, fontWeight: 500, color: "#64748b" }}>Tipo de cambio comisión</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={
                          formData.tipo_cambio_comision
                            || (dolarOficialCompra ? dolarOficialCompra.toString() : '')
                        }
                        onChange={(e) => setFormData((prev) => ({ ...prev, tipo_cambio_comision: e.target.value }))}
                        onFocus={async () => {
                          try {
                            // Obtener siempre el valor actual de venta para comisiones
                            const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
                            const data = await response.json();
                            const venta = data?.venta;
                            
                            if (venta) {
                              setDolarOficialCompra(venta);
                            }
                            
                            if ((formData.moneda_comision || formData.moneda) === 'USD' && venta) {
                              setFormData((prev) => ({ ...prev, tipo_cambio_comision: venta.toString() }));
                            }
                          } catch (err) {
                            console.error('Error precargando TC oficial venta para comisión', err);
                          }
                        }}
                        placeholder={dolarOficialCompra ? dolarOficialCompra.toString() : 'TC oficial (venta)'}
                        style={{ width: "100%", maxWidth: "100%", padding: 10, border: "1px solid #0ea5e9", borderRadius: 6, background: "white", minWidth: 0, boxSizing: "border-box" }}
                      />
                    </div>
                  </div>
                  {/* Retención en origen */}
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#64748b",
                        }}
                      >
                        Retener comisión al proveedor
                      </span>
                      <button
                        type="button"
                        aria-pressed={Boolean(formData.comision_retencion_en_origen)}
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            comision_retencion_en_origen: !prev.comision_retencion_en_origen,
                          }))
                        }
                        style={{
                          width: 44,
                          height: 24,
                          borderRadius: 9999,
                          border: '1px solid ' + (formData.comision_retencion_en_origen ? '#22c55e' : '#cbd5e1'),
                          background: formData.comision_retencion_en_origen ? '#22c55e' : '#e2e8f0',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'all .2s ease',
                          outline: 'none',
                        }}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            top: 2,
                            left: formData.comision_retencion_en_origen ? 22 : 2,
                            width: 18,
                            height: 18,
                            borderRadius: '9999px',
                            background: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            transition: 'left .2s ease',
                          }}
                        />
                      </button>
                    </div>
                    <div style={{ minHeight: "18px" }}>
                      {(formData.monto && formData.monto_comision) ? (
                        <p style={{ fontSize: "12px", color: "#0ea5e9", margin: 0 }}>
                          Neto proveedor: {(() => {
                            try {
                              const monto = parseFloat(formData.monto || 0);
                              const comi = parseFloat((formData.monto_comision || "0").toString().replace(/\./g, "")) || 0;
                              const neto = (formData.comision_retencion_en_origen ? (monto - comi) : monto);
                              return neto.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                            } catch { return "0,00"; }
                          })()}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  {/* Monto de la comisión */}
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "4px",
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#64748b",
                      }}
                    >
                      Monto de la comisión
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formData.monto_comision}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d.]/g, "");
                        // Formatear con puntos cada 3 dígitos
                        const numericValue = value.replace(/\./g, "");
                        const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                        setFormData({
                          ...formData,
                          monto_comision: formattedValue,
                        });
                      }}
                      onBlur={(e) => {
                        // Asegurar que siempre sea string
                        const value = e.target.value || "";
                        if (typeof value !== 'string') {
                          setFormData({
                            ...formData,
                            monto_comision: "",
                          });
                        }
                      }}
                      placeholder="0.00"
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #0ea5e9",
                        borderRadius: "6px",
                        fontSize: "16px",
                        boxSizing: "border-box",
                        background: "white",
                        outline: "none",
                        touchAction: "manipulation",
                      }}
                    />
                    {formData.monto_comision && formData.monto && (
                      <p
                        style={{
                          fontSize: "10px",
                          color: "#0ea5e9",
                          margin: "4px 0 0 0",
                          fontWeight: "500",
                        }}
                      >
                        {(() => {
                          try {
                            const montoComisionStr = typeof formData.monto_comision === 'string'
                              ? formData.monto_comision.replace(/\./g, "")
                              : formData.monto_comision?.toString()?.replace(/\./g, "") || "0";
                            const montoSalidaStr = formData.monto?.toString() || "0";

                            const montoComision = parseFloat(montoComisionStr) || 0;
                            const montoSalida = parseFloat(montoSalidaStr) || 0;

                            const tcSalida = parseFloat(formData.tipo_cambio || "1") || 1;
                            const tcComision = parseFloat(formData.tipo_cambio_comision || (dolarOficialCompra || 1)) || 1;

                            const monedaComision = formData.moneda_comision || formData.moneda;

                            const salidaARS = formData.moneda === 'USD' ? montoSalida * tcSalida : montoSalida;
                            const comisionARS = monedaComision === 'USD' ? montoComision * tcComision : montoComision;

                            const porcentaje = salidaARS > 0 ? (comisionARS / salidaARS) * 100 : 0;
                            return isNaN(porcentaje) ? "0.0" : porcentaje.toFixed(1);
                          } catch (error) {
                            return "0.0";
                          }
                        })()}% del monto de la salida
                      </p>
                    )}
                  </div>

                  {/* Estado de la comisión */}
                  <div>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#64748b",
                        marginBottom: "4px",
                      }}
                    >
                      Estado de la comisión
                      <span
                        style={{
                          fontSize: "10px",
                          color: formData.comision_cobrada ? "#059669" : "#f59e0b",
                          fontWeight: "600",
                        }}
                      >
                        {formData.comision_cobrada ? "Cobrada" : "Pendiente"}
                      </span>
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            comision_cobrada: false,
                          })
                        }
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          border: "1px solid #f59e0b",
                          borderRadius: "6px",
                          background: formData.comision_cobrada ? "white" : "#fef3c7",
                          color: "#f59e0b",
                          fontSize: "11px",
                          fontWeight: "600",
                          cursor: "pointer",
                        }}
                      >
                        Pendiente
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            comision_cobrada: true,
                          })
                        }
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          border: "1px solid #059669",
                          borderRadius: "6px",
                          background: formData.comision_cobrada ? "#d1fae5" : "white",
                          color: "#059669",
                          fontSize: "11px",
                          fontWeight: "600",
                          cursor: "pointer",
                        }}
                      >
                        Cobrada
                      </button>
                    </div>
                  </div>

                  {/* Documentación de la comisión */}
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#64748b",
                      }}
                    >
                      Documentación de la comisión
                    </label>
                    
                    {/* Comprobante de comisión */}
                    <div
                      style={{
                        background: "#fefbf3",
                        padding: "10px",
                        borderRadius: "8px",
                        border: "1px solid #fbbf24",
                        marginBottom: "8px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          fontSize: "11px",
                          fontWeight: "500",
                          color: "#374151",
                          cursor: "pointer",
                          marginBottom: "8px",
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <FileText size={10} />
                          Comprobante de comisión
                        </span>
                        <input
                          type="checkbox"
                          checked={formData.tiene_comprobante_comision}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              tiene_comprobante_comision: e.target.checked,
                            })
                          }
                          style={{
                            width: "12px",
                            height: "12px",
                            accentColor: "#f59e0b",
                          }}
                        />
                      </label>

                      {formData.tiene_comprobante_comision && (
                        <div
                          style={{
                            border: "2px dashed #fbbf24",
                            borderRadius: "8px",
                            padding: "8px",
                            textAlign: "center",
                            background: "white",
                          }}
                        >
                          {formData.comprobante_comision_url ? (
                            <div>
                              <CheckCircle2 size={16} color="#f59e0b" style={{ marginBottom: "4px" }} />
                              <p style={{ fontSize: "10px", color: "#f59e0b", margin: "0 0 6px 0", fontWeight: "600" }}>
                                Comprobante subido
                              </p>
                              <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                                <button
                                  onClick={() => window.open(formData.comprobante_comision_url, "_blank")}
                                  style={{
                                    background: "none",
                                    border: "1px solid #f59e0b",
                                    borderRadius: "4px",
                                    padding: "4px 6px",
                                    color: "#f59e0b",
                                    fontSize: "9px",
                                    cursor: "pointer",
                                    fontWeight: "500",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "2px",
                                  }}
                                >
                                  <Eye size={8} />
                                  Ver
                                </button>
                                <button
                                  onClick={() => setFormData({ ...formData, comprobante_comision_url: "" })}
                                  style={{
                                    background: "none",
                                    border: "1px solid #ef4444",
                                    borderRadius: "4px",
                                    padding: "4px 6px",
                                    color: "#ef4444",
                                    fontSize: "9px",
                                    cursor: "pointer",
                                    fontWeight: "500",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "2px",
                                  }}
                                >
                                  <Trash2 size={8} />
                                  Quitar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <FileText size={16} color="#64748b" style={{ marginBottom: "4px" }} />
                              <input
                                type="file"
                                id="comprobante-comision-camera"
                                accept="image/*"
                                capture="environment"
                                onChange={(e) => handleFileUpload(e.target.files[0], "comprobante_comision")}
                                style={{ display: "none" }}
                              />
                              <input
                                type="file"
                                id="comprobante-comision-gallery"
                                accept="image/*,application/pdf"
                                onChange={(e) => handleFileUpload(e.target.files[0], "comprobante_comision")}
                                style={{ display: "none" }}
                              />
                              <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                                <label
                                  htmlFor="comprobante-comision-camera"
                                  style={{
                                    background: "#f59e0b",
                                    color: "white",
                                    padding: "4px 6px",
                                    borderRadius: "4px",
                                    fontSize: "9px",
                                    fontWeight: "600",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "2px",
                                  }}
                                >
                                  <Camera size={8} />
                                  Cámara
                                </label>
                                <label
                                  htmlFor="comprobante-comision-gallery"
                                  style={{
                                    background: "#3b82f6",
                                    color: "white",
                                    padding: "4px 6px",
                                    borderRadius: "4px",
                                    fontSize: "9px",
                                    fontWeight: "600",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "2px",
                                  }}
                                >
                                  <Image size={8} />
                                  Galería
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Factura de comisión */}
                    <div
                      style={{
                        background: "#fefbf3",
                        padding: "10px",
                        borderRadius: "8px",
                        border: "1px solid #fbbf24",
                        marginBottom: "8px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          fontSize: "11px",
                          fontWeight: "500",
                          color: "#374151",
                          cursor: "pointer",
                          marginBottom: "8px",
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <FileText size={10} />
                          Factura de comisión
                        </span>
                        <input
                          type="checkbox"
                          checked={formData.tiene_factura_comision}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData({
                              ...formData,
                              tiene_factura_comision: checked,
                            });
                            
                            // Auto-completar CUITs cuando se marca factura
                            if (checked) {
                              const proveedorSeleccionado = todosLosProveedores.find(
                                (p) => p.id === formData.proveedor_id
                              );
                              const proyectoSeleccionado = proyectos.find(
                                (p) => p.id === formData.proyecto_id
                              );
                              
                              if (proveedorSeleccionado && !formData.facturador_comision) {
                                setFormData(prev => ({
                                  ...prev,
                                  facturador_comision: formatearCUIT(proveedorSeleccionado.cuit || ""),
                                }));
                              }
                              
                                      // Auto-completar CUIT de la constructora para comisión
        if (!formData.factura_a_nombre_de_comision) {
          const proyectoSeleccionado = proyectos.find(p => p.id === formData.proyecto_id);
          if (proyectoSeleccionado && proyectoSeleccionado.cuit_cliente) {
            setFormData(prev => ({
              ...prev,
              factura_a_nombre_de_comision: proyectoSeleccionado.cuit_cliente,
            }));
          }
        }
                            }
                          }}
                          style={{
                            width: "12px",
                            height: "12px",
                            accentColor: "#f59e0b",
                          }}
                        />
                      </label>

                      {formData.tiene_factura_comision && (
                        <div style={{ display: "grid", gap: "8px" }}>
                          {/* Campos de facturación */}
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                            <div>
                              <label style={{ fontSize: "10px", color: "#f59e0b", fontWeight: "600", display: "block", marginBottom: "4px" }}>
                                CUIT Facturador: *
                              </label>
                              <input
                                type="text"
                                value={formData.facturador_comision}
                                onChange={(e) => {
                                  const nuevoCuit = formatearCUIT(e.target.value);
                                  setFormData({
                                    ...formData,
                                    facturador_comision: nuevoCuit,
                                  });
                                }}
                                onBlur={(e) => {
                                  detectarCuitDiferente(e.target.value, formData.proveedor_id, 'facturador_comision');
                                }}
                                placeholder="CUIT Facturador (XX-XXXXXXXX-X)"
                                required
                                style={{
                                  width: "100%",
                                  padding: "8px",
                                  border: formData.facturador_comision ? "1px solid #f59e0b" : "1px solid #ef4444",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  boxSizing: "border-box",
                                  background: "white",
                                  outline: "none",
                                }}
                              />
                              {obtenerNombrePorCuit(formData.facturador_comision) && (
                                <p
                                  style={{
                                    fontSize: "9px",
                                    color: "#059669",
                                    margin: "2px 0 0 0",
                                    fontWeight: "500",
                                  }}
                                >
                                  {obtenerNombrePorCuit(formData.facturador_comision)}
                                </p>
                              )}
                              {!formData.facturador_comision && (
                                <p style={{ fontSize: "9px", color: "#ef4444", margin: "2px 0 0 0" }}>
                                  CUIT obligatorio para factura
                                </p>
                              )}
                            </div>

                            <div>
                              <label style={{ fontSize: "10px", color: "#f59e0b", fontWeight: "600", display: "block", marginBottom: "4px" }}>
                                Facturado a nombre de: *
                              </label>
                              <input
                                type="text"
                                value={formData.factura_a_nombre_de_comision}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    factura_a_nombre_de_comision: formatearCUIT(e.target.value),
                                  })
                                }
                                placeholder="CUIT (XX-XXXXXXXX-X)"
                                required
                                style={{
                                  width: "100%",
                                  padding: "8px",
                                  border: formData.factura_a_nombre_de_comision ? "1px solid #f59e0b" : "1px solid #ef4444",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  boxSizing: "border-box",
                                  background: "white",
                                  outline: "none",
                                }}
                              />
                              {obtenerNombrePorCuit(formData.factura_a_nombre_de_comision) && (
                                <p
                                  style={{
                                    fontSize: "9px",
                                    color: "#059669",
                                    margin: "2px 0 0 0",
                                    fontWeight: "500",
                                  }}
                                >
                                  {obtenerNombrePorCuit(formData.factura_a_nombre_de_comision)}
                                </p>
                              )}
                              {!formData.factura_a_nombre_de_comision && (
                                <p style={{ fontSize: "9px", color: "#ef4444", margin: "2px 0 0 0" }}>
                                  CUIT obligatorio para factura
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Subida de factura */}
                          <div
                            style={{
                              border: "2px dashed #fbbf24",
                              borderRadius: "8px",
                              padding: "8px",
                              textAlign: "center",
                              background: "white",
                            }}
                          >
                            {formData.factura_comision_url ? (
                              <div>
                                <CheckCircle2 size={16} color="#f59e0b" style={{ marginBottom: "4px" }} />
                                <p style={{ fontSize: "10px", color: "#f59e0b", margin: "0 0 6px 0", fontWeight: "600" }}>
                                  Factura subida
                                </p>
                                <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                                  <button
                                    onClick={() => window.open(formData.factura_comision_url, "_blank")}
                                    style={{
                                      background: "none",
                                      border: "1px solid #f59e0b",
                                      borderRadius: "4px",
                                      padding: "4px 6px",
                                      color: "#f59e0b",
                                      fontSize: "9px",
                                      cursor: "pointer",
                                      fontWeight: "500",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "2px",
                                    }}
                                  >
                                    <Eye size={8} />
                                    Ver
                                  </button>
                                  <button
                                    onClick={() => setFormData({ ...formData, factura_comision_url: "" })}
                                    style={{
                                      background: "none",
                                      border: "1px solid #ef4444",
                                      borderRadius: "4px",
                                      padding: "4px 6px",
                                      color: "#ef4444",
                                      fontSize: "9px",
                                      cursor: "pointer",
                                      fontWeight: "500",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "2px",
                                    }}
                                  >
                                    <Trash2 size={8} />
                                    Quitar
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <FileText size={16} color="#64748b" style={{ marginBottom: "4px" }} />
                                <input
                                  type="file"
                                  id="factura-comision-camera"
                                  accept="image/*"
                                  capture="environment"
                                  onChange={(e) => handleFileUpload(e.target.files[0], "factura_comision")}
                                  style={{ display: "none" }}
                                />
                                <input
                                  type="file"
                                  id="factura-comision-gallery"
                                  accept="image/*,application/pdf"
                                  onChange={(e) => handleFileUpload(e.target.files[0], "factura_comision")}
                                  style={{ display: "none" }}
                                />
                                <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                                  <label
                                    htmlFor="factura-comision-camera"
                                    style={{
                                      background: "#f59e0b",
                                      color: "white",
                                      padding: "4px 6px",
                                      borderRadius: "4px",
                                      fontSize: "9px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "2px",
                                    }}
                                  >
                                    <Camera size={8} />
                                    Cámara
                                  </label>
                                  <label
                                    htmlFor="factura-comision-gallery"
                                    style={{
                                      background: "#3b82f6",
                                      color: "white",
                                      padding: "4px 6px",
                                      borderRadius: "4px",
                                      fontSize: "9px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "2px",
                                    }}
                                  >
                                    <Image size={8} />
                                    Galería
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Información */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px",
                      background: "#e0f2fe",
                      borderRadius: "6px",
                      border: "1px solid #b3e5fc",
                    }}
                  >
                    <Info size={12} color="#0ea5e9" />
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#0c4a6e",
                        fontWeight: "500",
                      }}
                    >
                      Al marcar como "cobrada", se generará automáticamente una entrada a la caja de empresa
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Toggle de Adicional */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px",
              background: "white",
              border: "1px solid #f1f5f9",
              borderRadius: "12px",
              marginTop: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
                marginBottom: "0",
                width: "100%",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Package size={12} />
                Es gasto adicional
              </span>
              <button
                type="button"
                aria-pressed={Boolean(formData.es_adicional)}
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    es_adicional: !prev.es_adicional,
                  }));
                }}
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 9999,
                  border: '1px solid ' + (formData.es_adicional ? '#22c55e' : '#cbd5e1'),
                  background: formData.es_adicional ? '#22c55e' : '#e2e8f0',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all .2s ease',
                  outline: 'none',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: formData.es_adicional ? 22 : 2,
                    width: 18,
                    height: 18,
                    borderRadius: '9999px',
                    background: 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    transition: 'left .2s ease',
                  }}
                />
              </button>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div
          style={{
            background: "#f8fafc",
            padding: isMobile ? "20px" : "25px",
            borderTop: "2px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <button
            onClick={onClose}
            className="button-hover"
            style={{
              padding: isMobile ? "12px 20px" : "14px 24px",
              border: "2px solid #f1f5f9",
              borderRadius: "12px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            Cancelar
          </button>

          <button
            onClick={handleSave}
            disabled={guardando || subiendoComprobante || subiendoFactura}
            className="button-hover"
            style={{
              padding: isMobile ? "12px 24px" : "14px 28px",
              border: "none",
              borderRadius: "12px",
              background:
                guardando || subiendoComprobante || subiendoFactura
                  ? "#94a3b8"
                  : config.gradient,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor:
                guardando || subiendoComprobante || subiendoFactura
                  ? "not-allowed"
                  : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            {guardando || subiendoComprobante || subiendoFactura ? (
              <>
                <Loader2 size={16} className="loading-pulse" />
                Guardando salida...
              </>
            ) : (
              <>
                <Save size={16} />
                Guardar salida
              </>
            )}
          </button>
        </div>
      </div>

      {/* Modal para nuevo proveedor */}
      {showNewProveedorModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1001,
            animation: "fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              padding: "32px",
              width: isMobile ? "90%" : "500px",
              maxWidth: "90vw",
              animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
            }}
          >
            <h3
              style={{
                margin: "0 0 24px 0",
                fontSize: "20px",
                fontWeight: "700",
                color: "#1e293b",
              }}
            >
              Agregar nuevo proveedor
            </h3>

            <div style={{ display: "grid", gap: "20px" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Nombre del proveedor *
                </label>
                <input
                  type="text"
                  placeholder="Ej: Juan Pérez, Empresa ABC..."
                  value={nuevoProveedor.nombre}
                  onChange={(e) =>
                    setNuevoProveedor({
                      ...nuevoProveedor,
                      nombre: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                  onKeyPress={(e) =>
                    e.key === "Enter" && agregarNuevoProveedor()
                  }
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3b82f6";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  CUIT (opcional)
                </label>
                <input
                  type="text"
                  placeholder="Ej: 20-12345678-9"
                  value={nuevoProveedor.cuit}
                  onChange={(e) =>
                    setNuevoProveedor({
                      ...nuevoProveedor,
                      cuit: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                  onKeyPress={(e) =>
                    e.key === "Enter" && agregarNuevoProveedor()
                  }
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3b82f6";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                onClick={() => {
                  setShowNewProveedorModal(false);
                  setNuevoProveedor({
                    nombre: "",
                    cuit: "",
                  });
                }}
                className="button-hover"
                style={{
                  padding: "12px 20px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  background: "white",
                  color: "#64748b",
                  fontSize: "14px",
                  cursor: "pointer",
                  fontWeight: "600",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={agregarNuevoProveedor}
                disabled={guardandoProveedor}
                className="button-hover"
                style={{
                  padding: "12px 20px",
                  border: "none",
                  borderRadius: "12px",
                  background: guardandoProveedor
                    ? "#94a3b8"
                    : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  color: "white",
                  fontSize: "14px",
                  cursor: guardandoProveedor ? "not-allowed" : "pointer",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: "600",
                  touchAction: "manipulation",
                }}
              >
                {guardandoProveedor ? (
                  <>
                    <Loader2 size={16} className="loading-pulse" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Guardar proveedor
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para nueva subetapa */}
      {showNewSubetapaModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1001,
            animation: "fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              padding: "32px",
              width: isMobile ? "90%" : "500px",
              maxWidth: "90vw",
              animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
            }}
          >
            <h3
              style={{
                margin: "0 0 24px 0",
                fontSize: "20px",
                fontWeight: "700",
                color: "#1e293b",
              }}
            >
              Agregar nueva subetapa
            </h3>

            <div style={{ display: "grid", gap: "20px" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Etapa *
                </label>
                <CustomDropdown
                  value={nuevaSubetapa.etapa_id}
                  onChange={(value) =>
                    setNuevaSubetapa({
                      ...nuevaSubetapa,
                      etapa_id: value,
                    })
                  }
                  options={etapas.map((etapa) => ({
                    value: etapa.id,
                    label: etapa.nombre,
                  }))}
                  placeholder="Selecciona etapa"
                  icon={Layers}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Nombre de la subetapa *
                </label>
                <input
                  type="text"
                  placeholder="Ej: Excavación, Hormigonado..."
                  value={nuevaSubetapa.nombre}
                  onChange={(e) =>
                    setNuevaSubetapa({
                      ...nuevaSubetapa,
                      nombre: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                  onKeyPress={(e) =>
                    e.key === "Enter" && agregarNuevaSubetapa()
                  }
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3b82f6";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                onClick={() => {
                  setShowNewSubetapaModal(false);
                  setNuevaSubetapa({
                    nombre: "",
                    etapa_id: "",
                  });
                }}
                className="button-hover"
                style={{
                  padding: "12px 20px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  background: "white",
                  color: "#64748b",
                  fontSize: "14px",
                  cursor: "pointer",
                  fontWeight: "600",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={agregarNuevaSubetapa}
                disabled={guardandoSubetapa}
                className="button-hover"
                style={{
                  padding: "12px 20px",
                  border: "none",
                  borderRadius: "12px",
                  background: guardandoSubetapa
                    ? "#94a3b8"
                    : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  color: "white",
                  fontSize: "14px",
                  cursor: guardandoSubetapa ? "not-allowed" : "pointer",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: "600",
                  touchAction: "manipulation",
                }}
              >
                {guardandoSubetapa ? (
                  <>
                    <Loader2 size={16} className="loading-pulse" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Guardar subetapa
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal para agregar CUIT al proveedor */}
      {showCuitModal && proveedorParaCuit && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 15000,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              width: "100%",
              maxWidth: isMobile ? "90%" : "400px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              border: "2px solid #3b82f6",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "#eff6ff",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                }}
              >
                <Users size={24} color="#3b82f6" />
              </div>
              <h3
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                }}
              >
                Agregar CUIT
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#64748b",
                  lineHeight: "1.4",
                }}
              >
                Proveedor: <strong>{proveedorParaCuit.nombre}</strong>
              </p>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                CUIT (opcional)
              </label>
              <input
                type="text"
                placeholder="Ej: 20-12345678-9"
                value={cuitProveedor}
                onChange={(e) =>
                  setCuitProveedor(formatearCUIT(e.target.value))
                }
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onKeyPress={(e) =>
                  e.key === "Enter" &&
                  cuitProveedor.trim() &&
                  actualizarCuitProveedor()
                }
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              <button
                onClick={() => {
                  setShowCuitModal(false);
                  setProveedorParaCuit(null);
                  setCuitProveedor("");
                }}
                style={{
                  flex: 1,
                  padding: "12px 20px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  background: "white",
                  color: "#64748b",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  touchAction: "manipulation",
                }}
              >
                Omitir
              </button>
              <button
                onClick={actualizarCuitProveedor}
                disabled={guardandoCuit}
                style={{
                  flex: 1,
                  padding: "12px 20px",
                  border: "none",
                  borderRadius: "8px",
                  background: guardandoCuit ? "#94a3b8" : "#3b82f6",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: guardandoCuit ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  touchAction: "manipulation",
                }}
              >
                {guardandoCuit ? (
                  <>
                    <Loader2 size={16} className="loading-pulse" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Guardar CUIT
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para CUIT diferente */}
      {showCuitDiferenteModal && cuitDiferenteData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 15000,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              width: "100%",
              maxWidth: isMobile ? "90%" : "500px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              border: "2px solid #f59e0b",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "#fef3c7",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                }}
              >
                <AlertCircle size={24} color="#f59e0b" />
              </div>
              <h3
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                }}
              >
                CUIT diferente detectado
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#64748b",
                  lineHeight: "1.4",
                }}
              >
                El CUIT ingresado es diferente al registrado para <strong>{cuitDiferenteData.proveedor.nombre}</strong>
              </p>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <div style={{ display: "grid", gap: "12px" }}>
                <div style={{ 
                  background: "#fef3c7", 
                  padding: "12px", 
                  borderRadius: "8px",
                  border: "1px solid #f59e0b"
                }}>
                  <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#92400e", fontWeight: "600" }}>
                    CUIT registrado:
                  </p>
                  <p style={{ margin: 0, fontSize: "14px", color: "#92400e" }}>
                    {cuitDiferenteData.cuitProveedor || "No registrado"}
                  </p>
                </div>
                <div style={{ 
                  background: "#eff6ff", 
                  padding: "12px", 
                  borderRadius: "8px",
                  border: "1px solid #3b82f6"
                }}>
                  <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#1e40af", fontWeight: "600" }}>
                    CUIT ingresado:
                  </p>
                  <p style={{ margin: 0, fontSize: "14px", color: "#1e40af" }}>
                    {cuitDiferenteData.cuitIngresado}
                  </p>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              <button
                onClick={() => {
                  setShowCuitDiferenteModal(false);
                  setCuitDiferenteData(null);
                }}
                style={{
                  flex: 1,
                  padding: "12px 20px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  background: "white",
                  color: "#64748b",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  touchAction: "manipulation",
                }}
              >
                Usar solo para esta salida
              </button>
              <button
                onClick={actualizarCuitProveedorDesdeModal}
                disabled={guardandoCuit}
                style={{
                  flex: 1,
                  padding: "12px 20px",
                  border: "none",
                  borderRadius: "8px",
                  background: guardandoCuit ? "#94a3b8" : "#f59e0b",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: guardandoCuit ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  touchAction: "manipulation",
                }}
              >
                {guardandoCuit ? (
                  <>
                    <Loader2 size={16} className="loading-pulse" />
                    Actualizando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Actualizar proveedor
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Salidas;